/**
 * Variant C shell — the root container the App mounts when the
 * ``?ui=variant-c`` flag is on.
 *
 * Owns:
 *   - The run-state reducer (idle | mid-run | approval | post-run).
 *   - The LoomFresh selection captured at Start Draft (so the top
 *     bar can show it through the run lifecycle).
 *   - The active doc-type config (drives the initial section list).
 *   - The SSE subscription via useVariantCRun.
 *
 * Does NOT own:
 *   - Library/workspace fetching (LoomFresh handles its own picker).
 *   - Per-section status logic (useVariantCRun + sseAdapter).
 *   - Visual presentation (TopBarC, LoomFresh, LoomSpine).
 *
 * Composition:
 *
 *   idle     → <TopBarC/> + <LoomFresh/>
 *   mid-run  → <TopBarC heartbeat/> + <LoomSpine/>
 *   approval → <TopBarC heartbeat/> + <LoomSpine/> + (popout in P5)
 *   post-run → <TopBarC/> + <LoomSpine/> + (review surface in P7)
 */

import { makeStyles } from "@fluentui/react-components";
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  type DocTypeConfig,
  type SectionIssue,
  adhocDraftSection,
  cancelAdhocDraft,
  blobToBase64,
  draftNowRun,
  deleteDocument,
  downloadBlob,
  fetchDocumentDownload,
  replaceDocument,
  fetchRunDocument,
  fetchSpine,
  getDocTypeConfig,
  getRunAuditLibrary,
  type RunAuditLibrary,
  type RunIssueSection,
  getRunIssues,
  getSectionIssues,
  listActiveRuns,
  listRuns,
  listUserActiveRuns,
  listUserRuns,
  getRunSnapshot,
  getSectionState,
  getSessionToken,
  createLibrary,
  type ArtefactStageEvent,
  type CheckoutRun,
  getDocTypes,
  getDoctypeAbout,
  getExpectedSources,
  getDocuments,
  getDocumentPayment,
  getWorkspaceBilling,
  postPromo,
  logout,
  markSectionInserted,
  runDocumentUrl,
  type StartedPaidDraft,
  streamTableArtefact,
  type TableArtefactPreview,
  uploadContent,
  uploadFile,
  upsertDocumentSession,
  type UploadBatch,
  reprocessDocument,
} from "../api/client";
import { setFigureRunScope } from "../services/formatter";
import { buildOrder, billableRoots, isRefusal, type Order } from "./payments/order";
import { type DocTypePriceEntry, docPriceOf, isSalesAssisted } from "./payments/pricing";
import { coverageOf } from "./payments/coverage";
import { onPaywallRequest } from "./payments/paywall";
import { useCheckout } from "./payments/useCheckout";
import OrderDialog from "./payments/OrderDialog";
import CheckoutDialog from "./payments/CheckoutDialog";
import BillingDialog from "./payments/BillingDialog";
import ChangePasswordDialog from "./components/ChangePasswordDialog";
import UnlockAsthraDialog from "./components/UnlockAsthraDialog";
import SalesAssistedDialog from "./components/SalesAssistedDialog";
import { fetchImageDataUri } from "../api/imageSearchApi";
import { REVIEW_AGENT_ENABLED } from "../config/reviewAgent";
import { isDevBuild } from "../config/buildEnv";
import { useFlag } from "../context/FeatureFlagsContext";
import { useUserInputBridge } from "../hooks/useUserInputBridge";
import { type EmptyDocWatcherHandle, startEmptyDocWatcher } from "../services/emptyDocDetector";
import {
  beginLiveDocLoad,
  deferUntilLiveDocSettled,
  endLiveDocLoad,
  isLiveDocLoading,
} from "../services/liveDocLoadState";
import {
  clearSaveNotice,
  flushPendingAutoSaves,
  initSyncEngine,
  markSectionChanged,
  pauseSync,
  resumeSync,
} from "../services/syncEngine";
import { checkDocHasContent, subscribeSelectionChange, subscribeSelectionSettled } from "../services/wordProbe";
import { createParagraphSectionCache, selectionParagraphId } from "../services/cursorSectionCache";
import { coalesceInFlight, parkWhileWordBusy, queueWordRead } from "../services/wordReadQueue";
import { isWordWriteQueueBusy, whenWordWriteQueueIdle } from "../services/wordWriteQueue";
import { getLogger } from "../observability";
import {
  clearActiveRunId,
  clearDocument,
  clearWordHydrationMarker,
  detectCursorSectionNumberWithFallback,
  getActiveRunId,
  getDraftPointer,
  hydrateLiveDocFromBase64,
  insertInlineImageAtSelection,
  insertSectionContent,
  isImageSelected,
  isSyncReady,
  isValidLibraryId,
  isWordDesktop,
  loadDocSelectionProps,
  markSyncReady,
  navigateToSection,
  readSectionSubtree,
  rememberSkeletonTitles,
  replaceSelectedImage,
  rotateDocumentId,
  saveDocSelectionProps,
  saveDocumentId,
  saveWordInsertionMode,
  selectMarkerParagraph,
  setActiveRunId,
  setDocHasTitlePage,
  setDraftPointer,
  countEndNoteFieldsIn,
  type TitlePageStyle,
  updateTitlePageFields,
} from "../services/wordWriter";
import { endNoteOverwriteMessage } from "../services/endnoteGuard";
import type { Library, MentionAttachment, ProgressEvent, Workspace } from "../types";
import type { RunStatus } from "../types/index";
import { useChatHistoryView } from "./chatHistoryWiring";
import AsthraBubbleSurface from "./components/AsthraBubbleSurface";
// CommandPalette retired 2026-04-23 (Spec 29 palette-merge). The
// slash menu is now the sole command surface.
import CommandRail, { type CommandRailHandle } from "./components/CommandRail";
import type { ContextSlot } from "./components/ContextStrip";
import DigestReturnPill from "./components/DigestReturnPill";
import DoctypePicker, { lookupDoctypeChoice, regionTag } from "./components/DoctypePicker";
import { isPrivateTemplateCode, privateTemplateName } from "./doc-catalog";
import AdhocProposalMessage from "./components/AdhocProposalMessage";
import EditProposalMessage from "./components/EditProposalMessage";
import { HomeScene, type RunLoadContext } from "./components/HomeScene";
import { ImageSearchPane } from "./components/ImageSearchPane";
import Inspector from "./components/Inspector";
import KillRunConfirmation from "./components/KillRunConfirmation";
import { makeRedraftHandler, openSourceImpact, SourceImpactHost } from "./source-impact";
// LoomFresh stays imported for the LoomFreshSelection type (still
// the shape handleStartDraft consumes). The component itself is no
// longer rendered after Spec 29 Phase A — the conversational flow
// replaced its takeover.
import type { LoomFreshSelection, SectionItem } from "./components/LoomFresh";
import LoomPlanReview, { type PlanData } from "./components/LoomPlanReview";
import LoomSelection, { type DraftedSectionMeta, type LoomNaming, type LoomSelectionCommit } from "./components/LoomSelection";
import CustomDraftPicker from "./components/CustomDraftPicker";
import LoomSpine from "./components/LoomSpine";
import RunCapsule from "./components/RunCapsule";
import ImportExistingDocModal from "./components/ImportExistingDocModal";
import { HomePromptDialog, type HomePromptRequest } from "./components/HomePromptDialog";
import { setupDocumentInfo } from "./documentIdentity";
import { useDocumentRecord } from "./hooks/useDocumentRecord";
import { useFolders } from "./hooks/useFiling";
import DuplicateUploadModal from "./components/DuplicateUploadModal";
import { type SourceReplacedState, SourceReplacedNotice } from "./components/SourceReplacedNotice";
import NewProjectConfirmModal from "./components/NewProjectConfirmModal";
import RegenOne, { type RegenConfirmation, type RegenTarget } from "./components/RegenOne";
import SaveAndStartFreshModal from "./components/SaveAndStartFreshModal";
import RegenControlBar, { RegenNotice } from "./components/RegenControlBar";
import ResolveControlBar from "./components/ResolveControlBar";
import { useProposalReviewFlow } from "./hooks/useProposalReviewFlow";
import FixWindowHost from "./review/FixWindowHost";
import { ReviewPopup } from "./review/ReviewPopup";
import { sanitizeDraftForWord } from "./services/draftSanitizer";
import { type FixProgress, fixStatusFor } from "./review/fixProtocol";
import { SaveProgressCard } from "./components/SaveProgressCard";
import { WordBusyStrip, useSlowWordWrite } from "./components/WordBusyStrip";
import { LoadProgressCard } from "./components/LoadProgressCard";
import {
  buildSaveSteps,
  buildLoadSteps,
  buildCustomImportSteps,
  CUSTOM_IMPORT_STEP_DEFS,
  LOAD_STEP_DEFS,
} from "./components/HomeLoadProgress";
import SpineControlBar from "./components/SpineControlBar";
import SubmissionIssuesTab, { type ResolveSelectionTarget } from "./components/SubmissionIssuesTab";
import { toFixIssue } from "./components/inspector/IssueRow";
import TabBar, { type TabId } from "./components/TabBar";
import TimelineView, { buildIdleGreeting, IdleGreeting } from "./components/TimelineView";
import { TocPopup } from "./components/TocPopup";
import TopBarC from "./components/TopBarC";
import UserInputInlineRow from "./components/UserInputInlineRow";
import { WordOpsPanel } from "./components/WordOpsPanel";
import WorkspacePicker from "./components/WorkspacePicker";
import LibraryStep from "./components/LibraryStep";
import { type DoctypeChoice, buildCustomSkipSelection, buildLoomFreshSelection, pickerFolderChoice, suggestedDraftName } from "./draftFlow";
import { entriesNewestFirst } from "./eventLog";
import { decideGreetingEmission } from "./greetingPolicy";
import { useAllowedDocCodes } from "./hooks/useAllowedDocCodes";
import { useAppConfig } from "./hooks/useAppConfig";
import { parseWriteApprovalPayload } from "../components/WriteApprovalView";
import { describeToolCall, getToolDisplay } from "../config/toolDisplayNames";
import { ActionGroup, StepRow } from "./components/ActionGroup";
import MessageBubble from "./components/MessageBubble";
import WriteApprovalMessage from "./components/WriteApprovalMessage";
import ChangeSetApprovalMessage, { parseChangeSetPayload } from "./components/ChangeSetApprovalMessage";
import { type ChatDispatchTarget, buildStreamingStatus, useChatBridge } from "./hooks/useChatBridge";
import { useChatSystem } from "./hooks/useChatSystem";
import { useDocumentContext } from "./hooks/useDocumentContext";
import { useDraftFlow } from "./hooks/useDraftFlow";
import { type AdhocProposal, useAdhocProposalFlow } from "./hooks/useAdhocProposalFlow";
import { useHighlightEditFlow } from "./hooks/useHighlightEditFlow";
import { useRegenFlow } from "./hooks/useRegenFlow";
import { useSelectionChip } from "./hooks/useSelectionChip";
import {
  type AgentActivityLine,
  type AgentActivityMap,
  applyActivityEvent,
  clearAllActivity,
  hasAnyActivity,
  nextRunActivity,
} from "./services/agentActivityFeed";
import { RESOLVE_WHILE_DRAFTING_MESSAGE, type ResolveSummary, useResolveIssuesFlow } from "./hooks/useResolveIssuesFlow";
import { useRunControl } from "./hooks/useRunControl";
import { useRunVerdictWatcher } from "./hooks/useRunVerdictWatcher";
import { useSelectionCitations } from "./hooks/useSelectionCitations";
import { useSessionBootstrap } from "./hooks/useSessionBootstrap";
import { describeSaveNotice, useSaveNotice, useSyncStatus } from "./hooks/useSyncStatus";
import { type SpineSection, computeLockedModuleIds, useVariantCRun } from "./hooks/useVariantCRun";
import { filterSectionsToRunScope } from "./utils/runScope";
import { type RunStep, capsuleStateFor, runStepStatusText } from "./runCapsuleModel";
import {
  getAuditLibraryBuild,
  isAuditActiveForRun,
  noteAuditSkippedForLibrary,
  startAudit,
  subscribeAuditActivity,
  subscribeAuditLibraryBuild,
  subscribeAuditStarts,
} from "./reviewStore";
import {
  consumeReleasedSectionInsert,
  dropHeldSectionInsert,
  holdSectionInsert,
  releaseAllHeldSectionInserts,
  releaseHeldSectionInsert,
} from "./services/heldSectionInserts";
import { useWordEdits } from "./hooks/useWordEdits";
import { useWordWriteQueue } from "./hooks/useWordWriteQueue";
import { useWorkspaceLibrary } from "./hooks/useWorkspaceLibrary";
import { useWriterPresence } from "./hooks/useWriterPresence";
import { intentLabel } from "./intentLabels";
// Spec 34 — chat-first Library Hub (replaces the legacy modal).
import { Button, type ContextChip, FooterBar, Notice } from "../ds/components";
import { FirstRun } from "../components/LoginForm";
import { type HubMode, LibraryHubSlideOver } from "./library-hub/components/LibraryHubSlideOver";
import ArtefactBuilderPanel from "./library-hub/artefact/ArtefactBuilderPanel";
import ArtefactPreviewModal from "./library-hub/artefact/ArtefactPreviewModal";
import type { HubListTabs } from "./library-hub/components/HubList";
import { failedItems, HubFailedTab, HubUploadsTab } from "./library-hub/components/HubUploadStatus";
import { type UploadQueueDeps, useLibraryUploadQueue } from "./library-hub/hooks/useLibraryUploadQueue";
import { inventoryDtoToView } from "./library-hub/inventoryAdapter";
import { sameName } from "./library-hub/dedupeUploads";
import { CopyFromSource } from "./components/CopyFromSource";
import { totalPageCount } from "./library-hub/pageCount";
import { mergeUploadsAndInventory } from "./library-hub/mergeUploadsAndInventory";
import { resolveProgressLabel } from "./library-hub/resolveProgressLabel";
import type { DocumentRow } from "./library-hub/types";
import { liveUpload } from "./library-hub/uploadStatus";
import { deriveDocTypeOptions, isTargetDoctype } from "./libraryDoctypeVocabulary";
import { type ChatMessage, RUN_STARTED_TEXT } from "./messageProjection";
// DisclosureBanner retired from always-visible chrome (Spec 29) —
// copy relocates into the agent's greeting message (Phase 3).
// Import kept commented out in case it's needed temporarily during
// the migration; remove in Phase 4 when the greeting carries the
// regulatory copy.
import { formatSectionLabel, setUnnumberedSections } from "./sectionLabel";
import type { SetupCommand } from "./setupCardModel";
import { type GateMeta, type SelectionNode, type WriteStrategy, buildGateMeta, buildSelectionTree } from "./selection";
import { leafIdentityPatch, leafOptions, scopedCatalog } from "./leafDocuments";
import { shouldLiveProject } from "./liveProjection";
import {
  CHAT_PAGE_SIZE,
  fetchDocumentChat,
  fetchRunChat,
  flushAllPendingSaves,
  getCachedDocId,
  rememberRunRecoveryHint,
  scheduleSave,
  setCachedDocId,
} from "./services/documentSessionService";
import { performForeignDocRotation } from "./services/foreignDocRotation";
import { getLibraryDoctype, setLibraryDoctype } from "./services/libraryDoctypeStore";
import { loadCustomRunIntoLiveDoc } from "./services/loadCustomRunIntoLiveDoc";
import { loadRunIntoLiveDoc } from "./services/loadRunIntoLiveDoc";
import {
  LIVE_DOC_LOADED_TTL_MS,
  type LiveDocLoadMap,
  clearLiveDocLoad,
  expireLiveDocLoaded,
  outcomeFromSectionsWritten,
  setLiveDocFailed,
  setLiveDocLoaded,
  setLiveDocLoading,
} from "./services/liveDocLoadTracker";
import { applyModuleReinsert, queueModuleReinsert } from "./services/reinsertModuleIntoLiveDoc";
import { openExternal } from "./services/openExternal";
import { buildResolveInstruction } from "./services/resolveInstruction";
import { sourceScopeNote } from "./services/sourceScopeNote";
import { hasTemplateCover, writeTitlePage } from "./services/templateCover";
import {
  RUN_FAILED_AFTER_DRAFTING_ERROR,
  deriveRunSummaryFromSpine,
  mapCustomSpineDTO,
  mapSpineDTO,
  reconcileSpineWithRunStatus,
  restoresToIdle,
  restoresToPostRun,
  submissionSectionTotal,
  withReadyTimes,
} from "./services/runHydration";
import { shouldShowPostRunBanner } from "./postRunBanner";
import { buildRetrievalSubline } from "./sseAdapter";
import type { RetrievalProgress, RunIdentity } from "./sseAdapter";
import {
  type TimelineFilter,
  type VariantCState,
  type ViewMode,
  entryMatchesFilter,
  initialState,
  isActiveRun,
  variantCReducer,
} from "./state";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "./tokens";
import { deriveQualitySignals, translateVerdictToApproval } from "./verdictTranslator";

/**
 * Auto-load a single-section redraft into the live Word document without a
 * confirmation step — for BOTH redraft entry points (the Inspector "Regenerate
 * this section" flow and the chat ``/redraft`` command). Kill-switch: set
 * ``VITE_REDRAFT_AUTO_LOAD=0`` (or "false") to fall back to the prior behavior
 * (Inspector regen requires a manual Load; chat redraft shows an Approve card).
 * Default ON.
 */
const REDRAFT_AUTO_LOAD =
  import.meta.env.VITE_REDRAFT_AUTO_LOAD !== "0" && import.meta.env.VITE_REDRAFT_AUTO_LOAD !== "false";

/**
 * Poll Word until a freshly-inserted docx has finished rendering AND the
 * heading structure has stopped changing.  Called after
 * hydrateLiveDocFromBase64 / loadRunIntoLiveDoc to ensure sync doesn't resume —
 * and, via the liveDocLoadState guard, that background re-projection doesn't
 * fire — while the body is still empty or mid-insert.
 *
 * A bare "paragraph count > 1" check returns as soon as the FIRST content
 * appears, but the native-numbering / built-in-style rebuild (hydrate
 * STEP3/3.5) keeps rewriting the leading headings for a beat after that. During
 * that window a section-boundary scan is degraded, which is exactly when a
 * racing delete-then-reinsert blanks a large region. So we also require the
 * count of heading-styled paragraphs to be non-zero and STABLE across two
 * consecutive polls before treating the doc as settled.
 *
 * Resolves once settled, or after `timeoutMs` (then resumes regardless so the
 * engine is never stuck paused).
 */
async function _waitForDocumentLoaded(timeoutMs = 15_000): Promise<void> {
  const POLL_MS = 500;
  const deadline = Date.now() + timeoutMs;
  let pollCount = 0;
  let prevHeadingCount = -1;

  while (Date.now() < deadline) {
    pollCount++;
    try {
      const { paraCount, headingCount } = await Word.run(async (ctx) => {
        const paras = ctx.document.body.paragraphs;
        paras.load("items/styleBuiltIn");
        await ctx.sync();
        const headings = paras.items.filter((p) => {
          const style = (p.styleBuiltIn as string) || "";
          return style.includes("Heading") || style.includes("heading");
        }).length;
        return { paraCount: paras.items.length, headingCount: headings };
      });
      console.log(
        `[load-draft] _waitForDocumentLoaded poll#${pollCount} paragraphCount=${paraCount} headingCount=${headingCount} prevHeadingCount=${prevHeadingCount}`,
      );
      // Settled = content present AND at least one recognized heading whose count
      // held steady since the previous poll (numbering/styles done re-attaching).
      if (paraCount > 1 && headingCount > 0 && headingCount === prevHeadingCount) {
        console.log(
          `[load-draft] _waitForDocumentLoaded satisfied at poll#${pollCount} paragraphCount=${paraCount} headingCount=${headingCount}`,
        );
        return;
      }
      prevHeadingCount = headingCount;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.log(`[load-draft] _waitForDocumentLoaded poll#${pollCount} threw — Word busy — error="${message}"`);
    }
    await new Promise<void>((r) => setTimeout(r, POLL_MS));
  }
  console.log(`[load-draft] _waitForDocumentLoaded TIMED OUT after poll#${pollCount} — resuming sync anyway`);
}

/** Sentinel commandId for the persisted idle greeting (Phase 9.5
 *  follow-up). Lets the one-shot greeting share the ``agent_note``
 *  EventKind without inventing a new one (avoids 4 closed-enum
 *  guard updates), while still being detectable for the reload
 *  no-emit check. The double-underscore signals "internal — not a
 *  registry command id". */
const GREETING_SENTINEL_ID = "__greeting__";

/** Stable empty activity-lines array (module-level so the Inspector prop keeps
 *  a constant identity across renders when a section has no live feed). */
const EMPTY_ACTIVITY_LINES: readonly AgentActivityLine[] = [];

// The spine's box: the Inspector docks over it (position: absolute; inset: 0),
// so the spine stays mounted underneath and keeps its scroll position.
const SPINE_DOCK_STYLE: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  position: "relative",
  display: "flex",
  flexDirection: "column",
};

/** Upper bound on the click-time cursor-section read behind the section
 *  chips; past it the chip reports "no section" instead of hanging. */
const CURSOR_SECTION_READ_TIMEOUT_MS = 10_000;

/** Paragraph-walk cap for the scope chip's background cursor-section probe
 *  (20 paragraphs per sync, so at most 5 syncs). Bookmarked docs resolve via
 *  the light probe first; past this the chip shows "document". */
const CURSOR_CHIP_MAX_WALK = 100;

/** Paragraph-walk cap for the click-time read behind the section chips. Looser
 *  than the scope chip's, but bounded (15 syncs): the uncapped walk (up to 500
 *  syncs) kept running in queueWordRead long after the caller's timeout gave up
 *  on it, and every passive read — the scope chip's included — waited behind it. */
const CURSOR_SECTION_READ_MAX_WALK = 300;

/**
 * Doctype codes the picker is narrowed to when ``show.all-doctypes`` is
 * off. Codes are DoctypePicker catalog codes (doc-catalog.ts), not
 * backend doc_type values — "CSR-Abbr" is a separate code from "CSR" and
 * would have to be listed explicitly to appear.
 *
 * Module-level so the array identity is stable across renders; an inline
 * literal would re-run the picker's filtering memos on every pass.
 */
const RESTRICTED_DOC_CODES: readonly string[] = ["CSR", "CTD M2"];

const useStyles = makeStyles({
  root: {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    backgroundColor: surfaceColors.paper,
    fontFamily: fonts.body,
    color: inkColors.ink,
    position: "relative",
  },
  startingOverlay: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "24px",
    textAlign: "center",
  },
  startingMark: {
    fontFamily: fonts.display,
    fontSize: "16px",
    fontWeight: 700,
    color: brandColors.primary,
  },
  statusChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "999px",
    border: `1px solid color-mix(in srgb, ${brandColors.primary} 20%, transparent)`,
    background: `color-mix(in srgb, ${brandColors.primary} 7%, transparent)`,
    color: brandColors.primary,
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
  },
  startingHint: {
    fontSize: "12px",
    color: inkColors.ink3,
  },
  errorBanner: {
    margin: "12px",
    padding: "10px",
    background: "var(--danger-soft)",
    border: "1px solid color-mix(in srgb, var(--danger) 35%, transparent)",
    borderRadius: "3px",
    fontSize: "12px",
    color: "var(--danger)",
  },
  resetButton: {
    marginTop: "8px",
    fontSize: "11px",
    fontFamily: fonts.body,
    padding: "6px 12px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "3px",
    background: "var(--surface-raised)",
    color: inkColors.ink2,
    cursor: "pointer",
  },
});

// ---------------------------------------------------------------------------
// Helpers (pure functions extracted to utils/spineSnapshot.ts)
// ---------------------------------------------------------------------------
import {
  buildPersistedSpineSnapshotSection,
  deriveDocCaption,
  isEmptyFilter,
  normalisePersistedSpineSnapshot,
  normalisePersistedTimelineFilter,
  restorePersistedSpineSnapshotSection,
  shortHash,
  spineSectionsFromConfig,
  spineSectionsFromOutline,
  synthesizeProjectName,
  toPersistedSpineSnapshot,
} from "./utils/spineSnapshot";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface VariantCShellProps {
  /** Logged-in user's identifier (typically email) for display in
   *  TopBarC. Null/empty hides the label. Sourced from App.tsx's
   *  session state — auth SSOT lives in App, not duplicated here. */
  userIdentifier?: string | null;
}

/** No server record of uploads in this build (UI only). */
const NO_UPLOAD_BATCHES: readonly UploadBatch[] = [];

export const VariantCShell: React.FC<VariantCShellProps> = ({ userIdentifier }) => {
  const styles = useStyles();
  const [state, dispatch] = useReducer(variantCReducer, initialState);
  // Monotonic counter passed to the Drafts panel so it refreshes the moment a
  // run completes or a section finishes, instead of waiting for its 15s poll.
  const [draftsRefreshNonce, setDraftsRefreshNonce] = useState(0);
  const draftedProgressRef = useRef(-1);
  // Document session — fetched once on mount after Office.js resolves
  // the AsthrDocumentId. Gates the workspace fetch so the session's
  // workspace preference is available before pickInitialWorkspace runs.
  const [activeSelection, setActiveSelection] = useState<LoomFreshSelection | null>(null);
  // The WHOLE doctype config, as fetched. Only the leaf picker (which needs
  // every leaf) and the writers of the config read it — everything else reads
  // the leaf-scoped `docConfig` below.
  const [fullDocConfig, setDocConfig] = useState<DocTypeConfig | null>(null);
  // Which eCTD leaf this document IS. Seeded from the persisted session on
  // bootstrap and set when the writer picks one. `null` for a doctype filed as
  // ONE document and for every document created before the leaf split — both
  // must show the whole doctype, which is what `scopedCatalog` does with null.
  const [leafDocumentId, setLeafDocumentId] = useState<string | null>(null);
  // The config the whole shell sees: narrowed to the one eCTD leaf this
  // document IS. Scoping it HERE, once, is what keeps the picker, the spine,
  // the progress total, the TOC and every Word rebuild on the same section
  // set — scoping only the picker left the spine and the Load drawing the
  // whole CTD module around a single leaf. Narrowing the CONFIG (rather than
  // filtering rows downstream) keeps every count, gate packet and commit on
  // one consistent set, and it is the renderer's own rule, so the outline and
  // the produced .docx cannot disagree. Returns `fullDocConfig` BY REFERENCE
  // when it does not apply (no leaf id, no leaves, or a stale id), so memos
  // stay stable and a stale id widens back rather than emptying the outline.
  const docConfig = useMemo<DocTypeConfig | null>(() => {
    // A private workspace template's unnumbered headings render title-only:
    // the spine gutter hides their internal number, and every live
    // re-projection resolves them by title as well. Registered here, before
    // the spine renders from this config, so the first paint is already right.
    const templateNumbering = fullDocConfig?.heading_numbering === "template";
    setUnnumberedSections(
      templateNumbering ? (fullDocConfig.unnumbered_sections ?? []) : [],
      templateNumbering ? (fullDocConfig.skeleton ?? []).map(([num, title]) => [String(num), title] as const) : [],
    );
    return scopedCatalog(fullDocConfig, leafDocumentId) as DocTypeConfig | null;
  }, [fullDocConfig, leafDocumentId]);
  // The cursor lookup maps an unnumbered template heading ("Approvals") to its
  // section by skeleton title. Load and the scaffold register the skeleton; a
  // pane reload on an already-drafted document does neither, so register it
  // here too — and forget chip answers worked out without it.
  // Same for whether the doc type has a title page: the walk calls text above
  // the first heading the title page when the AsthraTpEnd bookmark is missing.
  const docHasTitlePage = (docConfig?.title_page as { enabled?: boolean } | null | undefined)?.enabled !== false;
  useEffect(() => {
    setDocHasTitlePage(!!docConfig && docHasTitlePage);
    if (docConfig?.skeleton?.length) rememberSkeletonTitles(docConfig.skeleton);
    cursorChipCacheRef.current.clear();
  }, [docConfig, docHasTitlePage]);
  // Diagnostic — fires when docConfig transitions from non-null → null,
  // which is the exact state that disables every Drafts panel Load
  // button. The stack trace pinpoints the offending caller (rotate-
  // project, reset, section cancel, or some unexpected path). Gated on
  // the prior-value ref so the initial null mount does not log.
  const docConfigPrevRef = useRef<DocTypeConfig | null>(null);
  useEffect(() => {
    if (docConfigPrevRef.current !== null && fullDocConfig === null) {
      console.info("[VariantCShell] docConfig cleared", new Error("docConfig=null stack").stack);
    }
    docConfigPrevRef.current = fullDocConfig;
  }, [fullDocConfig]);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  // Persistent, dismissable warning raised when a Stop request could not be
  // confirmed against the backend (network/5xx after retries) — the run may
  // still be drafting server-side. Rendered at the shell root (below) so it
  // survives the post-run / idle transition that a stop triggers; the idle-only
  // ``startError`` banner would not.
  const [stopWarning, setStopWarning] = useState<string | null>(null);
  // Guards against concurrent or rapid-repeated "Load drafted document" calls.
  const loadDraftInFlightRef = useRef(false);
  // Second Load click after the EndNote warning replaces the document anyway.
  const endNoteLoadAckRef = useRef(false);
  const [loadDraftInFlight, setLoadDraftInFlight] = useState(false);
  // Live progress for the floating LoadProgressCard on the spine "Load doc"
  // button: which checkpoint is in flight (0 prepare → 1 download → 2 rebuild →
  // 3 restore → 4 spine; LOAD_STEP_DEFS.length = all done). ``-1`` = idle.
  const [loadDocStepIndex, setLoadDocStepIndex] = useState(-1);
  const [loadDocError, setLoadDocError] = useState<string | null>(null);
  const [loadDocDone, setLoadDocDone] = useState(false);
  // Live progress for the floating "Build on this document" (custom-import)
  // card: 0 outline → 1 create project → 2 build spine → 3 load sections;
  // CUSTOM_IMPORT_STEP_DEFS.length = all done. ``-1`` = idle (card hidden).
  const [customImportStep, setCustomImportStep] = useState(-1);
  const [customImportError, setCustomImportError] = useState<string | null>(null);
  const [customImportDone, setCustomImportDone] = useState(false);
  // Submission mode of the currently loaded run ("custom" for build-on-existing
  // -document, else "standard"/null). Drives the progress denominator: a custom
  // run counts against its own heading total, not the doctype template's.
  const [runSubmissionMode, setRunSubmissionMode] = useState<string | null>(null);
  // Custom submission "Draft more sections" — the template-tree LoomSelection
  // can't render CUSTOM-N headings, so custom runs use the dedicated
  // CustomDraftPicker (instruction-bearing sections only). True while it's open.
  const [customDraftPickerActive, setCustomDraftPickerActive] = useState(false);
  // Live token counts accumulated from SSE token_usage events during an
  // active run. Reset whenever the runId changes (new run or idle reset).
  // Passed to TabBar → TokenUsageDisplay to show real-time accumulation
  // rather than polling the quota API.
  const [liveTokens, setLiveTokens] = useState<{
    input: number;
    output: number;
    thinking: number;
  } | null>(null);
  // Inspector state — which section the writer drilled into. Cleared
  // on reset and on inspector close. Lives in the shell (not the
  // reducer) because it's UI navigation, not run lifecycle.
  const [focusedSectionId, setFocusedSectionId] = useState<string | null>(null);
  // Monotonic counter bumped each time the writer clicks a spine ``N⚖``
  // regulator badge — handed to <Inspector> so it opens the Review tab even
  // when the section is already focused (where a section-change reset wouldn't
  // fire). See LoomSpine.onOpenReview / Inspector.openReviewSignal.
  const [reviewOpenSignal, setReviewOpenSignal] = useState(0);
  // Monotonic counter bumped when a highlighted-span edit is approved. Handed
  // to <Inspector> → <DataGapsTabPanel> so the open-items list (missing-info +
  // regulator flags) re-fetches against the freshly-reconciled section without
  // the writer clicking Refresh. The server reconciles the ledger inline in
  // section_state.transition, so by the time the approve write-back resolves
  // the ledger is already current — a re-read is all that's needed.
  const [issuesRefreshSignal, setIssuesRefreshSignal] = useState(0);
  // Bumped when the resolve review queue may have changed — a resolve pass
  // produced a proposal (``resolve_proposal_ready`` / ``resolve_section_proposed``)
  // or the pass finished — so the "Proposals to review" band re-fetches its
  // pending list without the writer pressing Refresh.
  const [proposalsRefreshSignal, setProposalsRefreshSignal] = useState(0);
  // Bumped to open the review window (Issues tab's review queue, bigger).
  const [reviewWindowSignal, setReviewWindowSignal] = useState(0);
  // Bumped when a section's Action History may have changed (a lifecycle write,
  // a regen, a resolve section finishing, a run ending) so the inspector's
  // Action History tab re-fetches without the writer switching sections.
  const [actionsRefreshSignal, setActionsRefreshSignal] = useState(0);
  // Open-issue tally for the Issues tab badge. The shell fetches it proactively
  // (so the badge draws the writer toward the tab before they open it) and
  // SubmissionIssuesTab reports live updates back via onOpenCountChange after
  // in-tab dismiss/reopen — keeping the badge correct without a refetch. Null
  // hides the chip (no run, still loading, or fetch failed).
  const [issuesOpenCount, setIssuesOpenCount] = useState<number | null>(null);
  // Per-module OPEN regulatory-issue counts from the section_issues ledger — the
  // authoritative live source the spine's ⚖ badge must reflect. Bridged from the
  // same getRunIssues fetch that drives the tab badge (below); null until the
  // first fetch (spine then falls back to the SSE/DB regulatorFlagCount). Fixes
  // "spine doesn't show section N's open regulatory issues": the audit writes
  // kind="regulatory" rows the ISSUES tab reads, but the spine never read them.
  const [regulatoryOpenByModule, setRegulatoryOpenByModule] = useState<Record<string, number> | null>(null);
  // Bulk-resolve wizard: non-null holds the selected targets and opens the
  // takeover; null = closed. Populated from the Issues tab's "Resolve selected".
  const [resolveWizardTargets, setResolveWizardTargets] = useState<ResolveSelectionTarget[] | null>(null);
  // The issues a started fix pass was given: the Fix window shows their status.
  const [fixPassTargets, setFixPassTargets] = useState<ResolveSelectionTarget[] | null>(null);
  // Cursor-derived section number for the chat rail's scope chip. Sourced by
  // a debounced + run-gated subscriber (below) so the chip reads §X.X.X while
  // the writer browses the doc post-draft, and resets to null whenever a new
  // run starts. ``handleSendChat`` still resolves the cursor section on
  // demand for the dispatch target — this state is only for the rail chip.
  const [cursorSectionNumber, setCursorSectionNumber] = useState<string | null>(null);
  // Commands button now opens the slash menu instead.
  // Library pick (Phase 7b) lifted from LoomFresh so the lifted
  // approval modal + the palette command + LoomFresh's picker all
  // read from one source of truth.
  const [selectedLibraryId, setSelectedLibraryId] = useState<string>("");
  // The active run's dynamic audit library (precedent-aware audit), fetched for
  // the Library Hub's Source↔Audit toggle. Null until fetched / when no run.
  const [hubAuditLibrary, setHubAuditLibrary] = useState<RunAuditLibrary | null>(null);
  // Live doctype pick (Phase 7.2 loose-coupled state). Hoisted here
  // alongside selectedLibraryId so the inventory-refresh effect below
  // can read it; the original declaration site (~810) was below the
  // refresh callback and produced a TDZ error when referenced.
  const [selectedDocCode, setSelectedDocCode] = useState<string | null>(null);
  const selectedDocCodeRef = useRef(selectedDocCode);
  selectedDocCodeRef.current = selectedDocCode;
  // Where the submission is filed — picked with the doctype (DoctypePicker's
  // region step) and sent as ``region`` on the run start. Required to draft.
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  // "How it works" from the account menu re-shows the first-run intro.
  const [introOpen, setIntroOpen] = useState(false);
  // New document step 3 (sources) — the library pick inside the /draft
  // orchestration. The Library Hub stays the place to manage libraries.
  const [libraryStepOpen, setLibraryStepOpen] = useState(false);
  const selectedRegionRef = useRef(selectedRegion);
  selectedRegionRef.current = selectedRegion;
  // A draft launch that arrived without a region (e.g. a saved doctype from
  // before regions existed): held here while the picker asks for the region
  // only, then relaunched with it.
  const regionRelaunchRef = useRef<LoomFreshSelection | null>(null);
  const [regionStepFor, setRegionStepFor] = useState<DoctypeChoice | null>(null);
  // A NEW build-on-your-document run must be named: a launch without a name
  // is held here while the name prompt is open, then relaunched with it.
  // (Template drafts are named on the section picker instead.)
  const [namePromptFor, setNamePromptFor] = useState<LoomFreshSelection | null>(null);
  // The name screen also files the draft in a folder, so naming and filing are
  // one step. Folders load only while it is open, for the run's workspace.
  const namePromptWorkspaceIds = useMemo(
    () => (namePromptFor?.workspaceId ? [namePromptFor.workspaceId] : []),
    [namePromptFor?.workspaceId],
  );
  const { folders: namePromptFolders, create: createNamePromptFolder } = useFolders(namePromptWorkspaceIds);
  const namePromptRequest = useMemo<HomePromptRequest | null>(
    () =>
      namePromptFor
        ? {
            kind: "name",
            title: "Name this draft",
            eyebrow: [namePromptFor.docName, namePromptFor.region].filter(Boolean).join(" · ") || undefined,
            label: `Give this ${namePromptFor.docName || "draft"} a name you'll recognise on Home.`,
            placeholder: "e.g. SNG-208 Phase 1 study report",
            hint: "You can rename it later.",
            confirm: "Continue",
            folders: namePromptFor.workspaceId
              ? {
                  options: [
                    { value: "", label: "Unfiled", detail: "Not in a folder" },
                    ...(namePromptFolders ?? []).map((f) => ({ value: f.id, label: f.label })),
                  ],
                  initial: namePromptFor.folderId ?? "",
                }
              : undefined,
          }
        : null,
    [namePromptFor, namePromptFolders],
  );
  // Phase 6.2 — DoctypePicker (Fluent Dialog modal, the canonical
  // doctype-selection surface) open-state.
  const [doctypePickerOpen, setDoctypePickerOpen] = useState<boolean>(false);
  const [tocOpen, setTocOpen] = useState<boolean>(false);
  // Section the writer is currently hovering in the TOC popup. Feeds the
  // rail's scope chip (via commandRailInspectorSectionNumber below) so the
  // chip previews the hovered section ahead of a click; cleared whenever
  // the popup closes so the chip falls back to the cursor section.
  const [tocHoveredSectionNumber, setTocHoveredSectionNumber] = useState<string | null>(null);
  const [showKillConfirmation, setShowKillConfirmation] = useState<boolean>(false);
  // "Draft now" sent: the in-flight sections are finishing from what they
  // gathered. Clears once a section lands (the run then carries on as normal).
  const [draftNowPending, setDraftNowPending] = useState<boolean>(false);
  const [killConfirming, setKillConfirming] = useState<boolean>(false);
  const [imageSelected, setImageSelected] = useState<boolean>(false);
  // Mirror the writer's active doctype selection into libraryDoctypeStore
  // for the active library. (Source priority: selectedDocCode > activeSelection.docCode)
  useEffect(() => {
    const docCode = selectedDocCode ?? activeSelection?.docCode;
    if (!selectedLibraryId || !docCode) return;
    if (!isTargetDoctype(docCode)) return;
    if (getLibraryDoctype(selectedLibraryId) === docCode) return;
    setLibraryDoctype(selectedLibraryId, docCode);
  }, [selectedLibraryId, selectedDocCode, activeSelection?.docCode]);

  // Opens the Library Hub for the currently-selected library.
  // Logout handler (palette command). Best-effort: the existing
  // api/client.logout() POSTs to the backend and clears session
  // state. After it resolves, force a full reload — simplest correct
  // way to return the user to the login screen regardless of which
  // Variant C surface they triggered logout from. Declared here
  // (above paletteCommands which captures it via useMemo) so JS
  // hoisting doesn't trip.
  const handleLogout = useCallback(async () => {
    // A live run keeps drafting through logout — it runs on the server.
    // Re-login restores it: SSE re-attaches and the Word rehydrate is
    // chained on the single write queue (useRunControl), so it never
    // contends with section writes. Pausing here cancelled in-flight
    // drafts (run 6ac0eee8, 3.2.P.3.5).
    try {
      await logout();
      // No provider-side sign-out to undo: the Microsoft/Google flows run in a
      // short-lived Office dialog whose MSAL/GIS cache is discarded when the
      // dialog closes, and each sign-in re-prompts with the account picker
      // (prompt=select_account). Clearing our own session (above) is the act.
    } catch (err) {
      console.warn("[VariantCShell] logout request failed (continuing):", err);
    } finally {
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    }
  }, []);
  // Coalesced: selection-change events call this continuously; while one
  // lookup is in flight, further calls share it instead of queueing more.
  // Without this the lookups piled up in queueWordRead whenever one outlasted
  // the trigger interval on a long document, and every read behind them (chip
  // clicks, the empty-doc check) waited longer the longer the session ran
  // (observed 0.7 s → >10 s).
  // Paragraph → section number for the scope chip (cursorSectionCache).
  const cursorChipCacheRef = useRef(createParagraphSectionCache<string | null>());
  // A refresh skipped because Word was busy is replayed once Word is idle:
  // the selection event that asked for it does not repeat, so without the
  // replay the chip kept its pre-write value (often "document") until the
  // writer moved the cursor — e.g. after a chat edit was applied in place.
  const refreshCursorSectionNumberRef = useRef<(() => Promise<void>) | null>(null);
  const cursorRefreshParkedRef = useRef(false);
  const parkCursorRefreshUntilIdle = () => {
    if (cursorRefreshParkedRef.current) return;
    cursorRefreshParkedRef.current = true;
    const replay = () => {
      cursorRefreshParkedRef.current = false;
      void refreshCursorSectionNumberRef.current?.();
    };
    if (isLiveDocLoading()) deferUntilLiveDocSettled("cursorChipRefresh", replay);
    else whenWordWriteQueueIdle(replay);
  };
  // Bumped per refresh: an answer that arrives after its refresh timed out is
  // applied only if no newer refresh has started since.
  const cursorRefreshGenRef = useRef(0);
  // Bounded so one Word read that never returns can't pin coalesceInFlight to a
  // dead promise — every later selection change shared it and the chip stayed
  // on "document" for the rest of the session. Logs one line per refresh
  // (cursor_chip.refresh) saying where it ended.
  const makeCursorSectionRefresh = (explicit: boolean) =>
    coalesceInFlight(async () => {
      const gen = ++cursorRefreshGenRef.current;
      const startedAt = Date.now();
      const run = runCursorSectionRefresh(explicit, (num) => {
        if (gen === cursorRefreshGenRef.current) setCursorSectionNumber(num);
      }).catch((err) => `error:${err instanceof Error ? err.message : String(err)}`);
      let timer: number | undefined;
      const outcome = await Promise.race([
        run,
        new Promise<string>((resolve) => {
          timer = window.setTimeout(() => resolve("timeout"), CURSOR_SECTION_READ_TIMEOUT_MS);
        }),
      ]);
      window.clearTimeout(timer);
      getLogger("addin.cursorChip").info("cursor_chip.refresh", { explicit, outcome, ms: Date.now() - startedAt });
    });
  const runCursorSectionRefresh = async (explicit: boolean, apply: (num: string | null) => void): Promise<string> => {
    const runState = runStateRef.current;
    if (isActiveRun(runState)) {
      return `skip:active-run(${runState})`;
    }
    // A Load or queued write owns Word right now. On Word desktop a probe
    // queued behind it only lengthens the stall and then fires stale —
    // skip now and replay once Word is idle again.
    if (isLiveDocLoading() || isWordWriteQueueBusy()) {
      parkCursorRefreshUntilIdle();
      return isLiveDocLoading() ? "parked:loading" : "parked:write-busy";
    }
    // Light-probe miss/throw handling (bookmark-less docs, hosts without
    // Range.getBookmarks) lives in the shared helper so this chip and the
    // citations popover resolve sections identically. Reads stay serialized
    // through queueWordRead so they can't race the empty-doc probes.
    // A document without section bookmarks (the light probe misses) gets the
    // heading walk on every move to a new paragraph — never vetoed: a miss is
    // about where the cursor was (front matter above the first numbered
    // heading), so vetoing the document after one miss left the chip on
    // "document" wherever the cursor went next. The walk fetches paragraphs in
    // batches (a few syncs), and the paragraph cache below stops repeats.
    // Same paragraph as last time: the answer has not changed, and the probe
    // below reads Word from the document start to the caret (see
    // cursorSectionCache). One cheap read of the caret's paragraph id instead.
    const paraId = await queueWordRead("cursorParagraphId", selectionParagraphId).catch(() => null);
    if (!explicit) {
      const cached = cursorChipCacheRef.current.get(paraId);
      if (cached.hit) {
        apply(cached.value);
        return `cache:${cached.value ?? "none"}`;
      }
    }
    let walked = false;
    const num = await detectCursorSectionNumberWithFallback(queueWordRead, {
      maxWalk: CURSOR_CHIP_MAX_WALK,
      onLightMiss: () => {
        walked = true;
        return true;
      },
    });
    cursorChipCacheRef.current.set(paraId, num);
    apply(num);
    const via = walked ? "walk" : "bookmark";
    return `${via}:${num ?? "none"} para=${paraId ?? "none"}`;
  };
  const refreshCursorSectionNumberExplicit = useMemo(() => makeCursorSectionRefresh(true), []);
  const refreshCursorSectionNumber = useMemo(() => makeCursorSectionRefresh(false), []);
  refreshCursorSectionNumberRef.current = refreshCursorSectionNumber;

  const handleScopeChipClick = useCallback(() => {
    // Best-effort refresh before opening TOC so the left chip label and
    // navigator highlight track the writer's latest cursor location.
    void refreshCursorSectionNumberExplicit();
    setTocOpen(true);
  }, [refreshCursorSectionNumberExplicit]);

  // The TOC popup can close without a hover ever ending on a row (Escape,
  // backdrop click, or navigating away) — reset the hover preview whenever
  // it closes so a stale hovered section can't linger on the scope chip.
  useEffect(() => {
    if (!tocOpen) {
      setTocHoveredSectionNumber(null);
    }
  }, [tocOpen]);
  // Backend runtime config — sandbox flag + which doc-types have
  // fixtures. Source of truth for the TopBar mode pill + LoomFresh's
  // doc availability gate.
  const {
    appConfig,
    modeToggling,
    modeToggleError,
    planModeToggling,
    planModeToggleError,
    refreshAppConfig,
    handleModeToggle,
    handlePlanModeToggle,
    dismissModeToggleError,
    dismissPlanModeToggleError,
  } = useAppConfig();

  // ----- Word-write side-effect path (SSE → Word document) -------------
  // ``leafDocumentId`` is threaded in so the scaffold builds ONLY this eCTD
  // leaf's headings — without it a CMC/CTD leaf document would get the whole
  // 65-section doctype tree. Single-document doctypes pass null and are
  // unaffected (scopedCatalog returns the config by reference).
  const {
    handleProgressEvent,
    scaffoldedRef,
    insertionModeRef,
    liveProjectionRef,
    agentLastWriteAtRef,
    enqueueWordWrite,
  } = useWordWriteQueue(fullDocConfig, leafDocumentId);

  // -- Intent-routed chat: live drafting activity in the chat thread ----
  // Set when a chat message triggered a regen / ad-hoc draft; updated
  // from raw run SSE below; rendered through TimelineView's existing
  // streamingStatus / streamingDraft transient bubble. Ref mirror so
  // the SSE callback reads current state without re-subscribing.
  interface ChatRunActivityState {
    moduleId: string;
    kind: "regen" | "adhoc";
    instruction: string;
    phase: "starting" | "retrieving" | "drafting";
    draftSoFar: string;
    startedAt: number;
    // Live retrieval subline (which items are resolving, resolved/total,
    // cascade level) — the SAME string the spine shows on its drafting row,
    // built from the shared ``buildRetrievalSubline`` helper. Lets the chat
    // regen row stream retrieval detail instead of a coarse phase word.
    detail?: string;
    retrieval?: RetrievalProgress | null;
  }
  const [chatRunActivity, setChatRunActivity] = useState<ChatRunActivityState | null>(null);
  const chatRunActivityRef = useRef<ChatRunActivityState | null>(null);
  const updateChatRunActivity = useCallback((next: ChatRunActivityState | null) => {
    chatRunActivityRef.current = next;
    setChatRunActivity(next);
  }, []);

  // ``adhoc_draft_ready`` capture — the offer function lives on a ref
  // because the proposal flow hook is instantiated further down (it
  // needs resolveSectionLabel, which closes over ``sections``), while
  // the SSE wrapper below is declared before either exists. Same
  // pattern as chatRunActivityRef.
  const adhocProposalOfferRef = useRef<
    ((event: { module_id?: string; run_id?: string; section_response?: unknown }) => void) | null
  >(null);
  // Filled from the resolve flow (declared far below) so the progress handler —
  // declared above it — can advance the resolve phase machine on each event.
  const resolveFlowOnEventRef = useRef<((event: ProgressEvent) => void) | null>(null);
  // Same pattern for the single-section regen's pause/stop control bar.
  const regenFlowOnEventRef = useRef<((event: ProgressEvent) => void) | null>(null);
  // Forward ref to ``startSyncEngineForCurrentRun`` (declared further down, so
  // it cannot be a dependency of the progress-event callback above it without
  // a TDZ error at render). Same pattern as ``resolveFlowOnEventRef``.
  const startSyncEngineRef = useRef<(() => void) | null>(null);

  // Post-approve auto-save hook — filled by an effect next to
  // handleSave (declared far below); called by the proposal decision
  // handlers when a chat-driven edit is accepted.
  const autoSaveRef = useRef<(() => Promise<void>) | null>(null);
  // Section-scoped auto-save (by module id) — set alongside autoSaveRef; called
  // by the proposal decision handlers so an accepted edit persists just its
  // own section instead of the whole document.
  const autoSaveModuleRef = useRef<((moduleId: string | null) => Promise<void>) | null>(null);

  // Terminal draft-launch hook — filled by an effect next to launchDraft
  // (declared below, after startCustomFlow). The doctype/library orchestration
  // callbacks (tryAdvanceDraft, handleDoctypePick, advanceDraftWithLibrary) are
  // declared above launchDraft, so they call it through this ref to avoid a
  // temporal-dead-zone reference in their dependency arrays.
  const launchDraftRef = useRef<((selection: LoomFreshSelection) => void) | null>(null);

  // Per-section "loading into Word…" tracker shown on the spine row after a
  // redraft, cleared only on a CONFIRMED write (sectionsWritten > 0) and flipped
  // to a failed state when nothing lands. Shell-owned + passed to LoomSpine as a
  // prop (NOT on SpineSection) so it survives the SSE section_state_changed
  // subline-wipe and spine rebuilds. Keyed by module_id — the same id the regen
  // SSE path uses for its spine updates.
  const [liveDocLoadBySection, setLiveDocLoadBySection] = useState<LiveDocLoadMap>(() => new Map());

  // Expire the transient "loaded into Word" ticks. One timer for the whole map
  // (not one per section): it is armed only while a ``loaded`` entry exists,
  // fires at the earliest expiry, and prunes. ``expireLiveDocLoaded`` returns
  // the SAME map reference when nothing aged out, so setState bails and this
  // effect cannot re-arm itself into a loop. ``loading`` / ``failed`` entries
  // are never time-expired — their own write resolves them.
  useEffect(() => {
    let earliest = Number.POSITIVE_INFINITY;
    for (const entry of liveDocLoadBySection.values()) {
      if (entry.state === "loaded") earliest = Math.min(earliest, entry.startedAt);
    }
    if (!Number.isFinite(earliest)) return;
    const dueIn = Math.max(0, earliest + LIVE_DOC_LOADED_TTL_MS - Date.now());
    const t = setTimeout(() => {
      setLiveDocLoadBySection((m) => expireLiveDocLoaded(m, Date.now()));
    }, dueIn);
    return () => clearTimeout(t);
  }, [liveDocLoadBySection]);

  // Per-section LIVE agent activity feed (what tool Asthra is calling right now),
  // shell-owned like liveDocLoadBySection so it survives the section_state_changed
  // subline wipe + spine rebuilds. Fed by every AsthraAgent/tool event now
  // forwarded on the run stream (drafting, regen AND bulk resolve).
  const [agentActivityBySection, setAgentActivityBySection] = useState<AgentActivityMap>(() => new Map());
  const [latestRunActivity, setLatestRunActivity] = useState<AgentActivityLine | null>(null);

  // Best-effort re-projection of one module's section(s) from the server SSOT
  // into the live Word doc, serialised through the single Word-write queue so it
  // can't interleave with a Load / audit re-projection, and deferred while a
  // load is still settling (same guard reviewStore.reprojectSection uses — a
  // delete-then-reinsert during the settle window can blank a region). Drives the
  // spine tracker: ``loading`` up front (persists across the settle defer), then
  // ``clear`` on a confirmed write or ``failed`` when nothing lands. One bounded
  // retry covers a doc that was still settling on the first attempt. A silent
  // no-op (sectionsWritten 0) means no matching heading — the doc likely needs a
  // full Load, which the failed subline tells the writer.
  //
  // Resolves once this request has settled (written, no-op or failed) so a
  // caller holding a section in "running" — reviewStore releasing a section
  // held for its audit — can wait for the markers to actually be in the doc.
  // A request superseded while deferred (same section re-requested during a
  // Load) never resolves; such callers race it with a timeout.
  const reprojectSectionIntoLiveDoc = useCallback(
    (runId: string | null, moduleId: string, trigger: "drafting" | "redraft" = "redraft"): Promise<void> => {
      if (!runId) return Promise.resolve();
      let settle: () => void = () => {};
      const settled = new Promise<void>((resolve) => {
        settle = resolve;
      });
      setLiveDocLoadBySection((m) => setLiveDocLoading(m, moduleId, Date.now()));
      // Re-check at run time, not just at request time: a Load can start while
      // this job waits in the queue, and writing into a body that is being
      // replaced is how section text ended up above the title page.
      const deferKey = `${trigger}:${runId}:${moduleId}`;
      // The section fetch + fragment render run outside the Word write queue;
      // only the Office.js work holds it.
      const attempt = () =>
        queueModuleReinsert(runId, moduleId, {}, enqueueWordWrite, (plan) =>
          isLiveDocLoading() ? Promise.resolve(null) : applyModuleReinsert(plan),
        );
      const run = async () => {
        try {
          const first = await attempt();
          if (first === null) {
            deferUntilLiveDocSettled(deferKey, () => void run());
            return;
          }
          let { sectionsWritten, sectionsAttempted } = first;
          if (outcomeFromSectionsWritten(sectionsWritten, sectionsAttempted) === "failed") {
            // The doc may still be settling (styles / native numbering
            // re-attaching) so the heading scan missed. Give it one retry.
            // A "noop" is NOT retried — the module had nothing to write, so a
            // second identical pass would find nothing to write either.
            await new Promise((resolve) => setTimeout(resolve, 700));
            const retry = await attempt();
            if (retry === null) {
              deferUntilLiveDocSettled(deferKey, () => void run());
              return;
            }
            ({ sectionsWritten, sectionsAttempted } = retry);
          }
          const outcome = outcomeFromSectionsWritten(sectionsWritten, sectionsAttempted);
          if (outcome === "noop") {
            // Nothing to project (every declared section empty, or all prose
            // routed to children this module does not own). Clear the
            // optimistic "loading" tick rather than leaving the row spinning
            // forever, and do NOT show a failure — nothing went wrong.
            setLiveDocLoadBySection((m) => clearLiveDocLoad(m, moduleId));
          } else if (outcome === "success") {
            // Transient "loaded into Word" tick on the spine row; expires on
            // the spine's own timer (see expireLiveDocLoaded on the tick).
            setLiveDocLoadBySection((m) => setLiveDocLoaded(m, moduleId, Date.now()));
            // Batch-SSOT: tell the server this section is now in the live doc
            // so its lifecycle advances to ``inserted``. Drafting only — a
            // redraft/audit re-projection is re-rendering a section whose
            // lifecycle the server already owns. Side-effect; must not break
            // the write path, and the server self-walks drafted -> inserted.
            if (trigger === "drafting") {
              void markSectionInserted(runId, moduleId).catch(() => {
                /* non-fatal */
              });
            }
          } else {
            console.warn(
              `[VariantCShell] redraft re-projection wrote 0 sections for ${moduleId} — heading not found in the live doc (needs a full Load?)`,
            );
            setLiveDocLoadBySection((m) => setLiveDocFailed(m, moduleId, Date.now()));
          }
        } catch (err) {
          console.warn("[VariantCShell] redraft live re-projection failed (non-fatal):", err);
          setLiveDocLoadBySection((m) => setLiveDocFailed(m, moduleId, Date.now()));
        }
        settle();
      };
      if (isLiveDocLoading()) {
        deferUntilLiveDocSettled(deferKey, () => void run());
      } else {
        void run();
      }
      return settled;
    },
    [enqueueWordWrite],
  );

  // Wraps handleProgressEvent to also accumulate token_usage fields from
  // each SSE event into ``liveTokens``. Passed to useVariantCRun in place
  // of the raw handler so the TabBar can show real-time token counts.
  const handleProgressEventWithTokens = useCallback(
    (event: Parameters<typeof handleProgressEvent>[0]) => {
      handleProgressEvent(event);
      // Live per-section agent activity feed (tool calls, retrieval steps,
      // rounds…) for the spine. Same event stream for drafting, regen and
      // bulk resolve, so all three now show what the agent is doing live.
      setAgentActivityBySection((m) => applyActivityEvent(m, event));
      // Newest line across the run for the Spine's live-activity strip; kept
      // after the per-section feed clears so the last step stays visible.
      setLatestRunActivity((p) => nextRunActivity(p, event));
      // Advance the resolve pass phase machine (progress / paused / resumed).
      resolveFlowOnEventRef.current?.(event);
      // Settle the regen control bar (running → paused / done).
      regenFlowOnEventRef.current?.(event);
      // A completed run (including a bulk resolve-issues run) or a completed
      // single-section regenerate may have changed the issue ledger — refresh
      // the Issues tab list + open-count badge. ``regen_complete`` is emitted
      // separately from ``pipeline_complete`` (it's deliberately non-terminal so
      // the SSE stream stays open) and the regen reseed now resolves markers the
      // redraft removed and opens new ones, so it must trigger the same refresh.
      if (
        event.event_type === "pipeline_complete" ||
        event.event_type === "regen_complete" ||
        event.event_type === "resolve_finished"
      ) {
        setIssuesRefreshSignal((n) => n + 1);
      }
      // A finished regulatory audit added/cleared regulator flags on a section —
      // refresh the run-wide Issues tab so they surface without a manual Save.
      if (event.event_type === "audit_complete") {
        setIssuesRefreshSignal((n) => n + 1);
      }
      // The automatic missing-info fill finished a section: its proposals are
      // in — refresh the review queue, the Issues tab (per-flag outcomes) and
      // Action History.
      if (event.event_type === "gap_fill_complete" && event.module_id) {
        setProposalsRefreshSignal((n) => n + 1);
        setIssuesRefreshSignal((n) => n + 1);
        setActionsRefreshSignal((n) => n + 1);
      }
      // A resolve pass finished a section (or produced a proposal): the review
      // queue changed. Refresh the "Proposals to review" band + Action History —
      // NOTHING is written into Word here. The change lands in the document only
      // when the writer Approves an individual proposal (see useProposalReviewFlow).
      if (
        (event.event_type === "resolve_section_proposed" ||
          event.event_type === "resolve_proposal_ready" ||
          event.event_type === "resolve_proposal_superseded") &&
        event.module_id
      ) {
        setProposalsRefreshSignal((n) => n + 1);
        setActionsRefreshSignal((n) => n + 1);
      }
      if (event.event_type === "pipeline_complete" || event.event_type === "resolve_finished") {
        setProposalsRefreshSignal((n) => n + 1);
      }
      // Live projection hands the document back at completion. "Load doc" is
      // otherwise the ONLY place that re-enables sync (markSyncReady +
      // setDraftPointer("live") + start the engine), which was fine when the
      // writer HAD to press it to see any content at all. With the draft
      // already in the doc a writer may never press it, and their edits would
      // silently never autosave. Ownership only — the body is never touched.
      if (event.event_type === "pipeline_complete" && liveProjectionRef.current) {
        const completedRunId = runIdRef.current;
        if (completedRunId) {
          void (async () => {
            try {
              await markSyncReady(completedRunId);
              await setDraftPointer("live");
              startSyncEngineRef.current?.();
            } catch (err) {
              console.warn("[VariantCShell] live-projection sync handback failed (non-fatal):", err);
            }
          })();
        }
      }
      // Action History for this section may have changed — re-fetch it if open.
      if (
        event.event_type === "section_state_changed" ||
        event.event_type === "regen_complete" ||
        event.event_type === "pipeline_complete" ||
        event.event_type === "resolve_finished"
      ) {
        setActionsRefreshSignal((n) => n + 1);
      }
      // Live section projection during DRAFTING: a section just finished, so
      // load it into the live Word doc. Deliberately re-read from the server
      // SSOT (``getSectionContent``) rather than written from this event's
      // payload — the Specification gate's ``section_ready`` carries NO
      // ``sections[]`` (only a draft_preview), so a payload write silently
      // skipped every spec section. Routing through the same re-projection the
      // redraft path uses also buys the settle deferral, the bounded retry and
      // the spine's per-row "loaded into Word" tick.
      //
      // ``awaiting_audit``: the server is auditing this section right now. Hold
      // the insert until the audit settles and put the section in ONCE, flags
      // included — inserting the bare draft here meant the audit rewrote the
      // whole section a minute later (the "added and re-added" bug). Released
      // only by ``section_final`` below (``regen_complete`` for a regen), sent
      // after the audited draft is saved — also for an audit skipped because
      // the audit library isn't built yet (the draft then goes in unaudited and
      // the Issues tab says so). reviewStore's ``audit_complete`` waits on this
      // insert instead of writing the section itself.
      if (liveProjectionRef.current && event.event_type === "section_ready" && event.module_id) {
        const readyRunId = runIdRef.current;
        const readyModuleId = event.module_id;
        const awaitingAudit =
          (event.section_response as { awaiting_audit?: unknown } | undefined)?.awaiting_audit === true;
        if (awaitingAudit && readyRunId) {
          holdSectionInsert(readyRunId, readyModuleId, () =>
            reprojectSectionIntoLiveDoc(readyRunId, readyModuleId, "drafting"),
          );
        } else {
          // Clear a stale "released" mark from an earlier pass so a later
          // regen_complete doesn't mistake this write for an audit release.
          if (readyRunId) consumeReleasedSectionInsert(readyRunId, readyModuleId);
          void reprojectSectionIntoLiveDoc(readyRunId, readyModuleId, "drafting");
        }
      }
      if (event.event_type === "section_final" && event.module_id && runIdRef.current) {
        const outcome = (event.section_response as { audit_outcome?: unknown } | undefined)?.audit_outcome;
        if (outcome === "skipped_library_not_ready") {
          noteAuditSkippedForLibrary(runIdRef.current, event.module_id);
        }
        void releaseHeldSectionInsert(runIdRef.current, event.module_id);
      }
      // A run-level terminal must never strand a held section out of the doc
      // (an audit outliving the run, a stop/pause mid-audit).
      if (
        runIdRef.current &&
        (event.event_type === "pipeline_complete" ||
          event.event_type === "cancelled" ||
          event.event_type === "error" ||
          event.event_type === "paused")
      ) {
        releaseAllHeldSectionInserts(runIdRef.current);
      }
      // Inspector "Regenerate this section" (Path A) auto-load: the regen
      // committed the section's SSOT server-side but never touched the open
      // document. Re-project just this module from the server into the live doc
      // so the redraft appears without a manual Load.
      // ``regen_complete`` carries ``module_id = regen_target``.
      // A STOPPED regen never touched the document and the server has put the
      // previous draft back in the SSOT — re-projecting would only re-render it.
      //
      // A regen's ``section_ready`` is held for its audit too (the server sends
      // no ``section_final`` for regen — this event is emitted after the audit
      // join, so after the audited draft is saved). Release it here; when a
      // release already put the section in the doc, a second write would be
      // the same visible re-add.
      if (event.event_type === "regen_complete" && event.module_id && runIdRef.current) {
        const regenRunId = runIdRef.current;
        const regenModuleId = event.module_id;
        const cancelled = (event.section_response as { outcome?: unknown } | undefined)?.outcome === "cancelled";
        if (cancelled) {
          // Nothing to write — the server restored the previous draft.
          dropHeldSectionInsert(regenRunId, regenModuleId);
          consumeReleasedSectionInsert(regenRunId, regenModuleId);
        } else if (releaseHeldSectionInsert(regenRunId, regenModuleId)) {
          consumeReleasedSectionInsert(regenRunId, regenModuleId);
        } else if (consumeReleasedSectionInsert(regenRunId, regenModuleId)) {
          // Already inserted by the audit release — nothing more to write.
        } else if (REDRAFT_AUTO_LOAD) {
          void reprojectSectionIntoLiveDoc(regenRunId, regenModuleId);
        }
      }
      // Ad-hoc draft proposal ready → offer it to the Approve/Reject
      // flow (transient card in the thread; the durable EventLog entry
      // lands at decision time — see useAdhocProposalFlow).
      if (event.event_type === "adhoc_draft_ready") {
        adhocProposalOfferRef.current?.(event);
      }
      // Feed the chat thread's live drafting bubble while a
      // chat-initiated regen / ad-hoc draft is running on this module.
      const activity = chatRunActivityRef.current;
      if (activity && event.module_id === activity.moduleId) {
        switch (event.event_type) {
          case "text_chunk":
            // Chunks are incremental (~50 chars) — accumulate.
            updateChatRunActivity({
              ...activity,
              phase: "drafting",
              draftSoFar: activity.draftSoFar + (event.message || ""),
            });
            break;
          case "retrieval_plan":
          case "retrieval_plan_retry":
          case "retrieval_progress":
          case "retrieval_item_added":
          case "retrieval_item_resolved":
          case "retrieval_item_advance":
          case "checklist_item":
          case "checklist_drafting":
          case "draft_starting": {
            // Capture the live retrieval subline (same string the spine
            // shows) so the chat regen row streams which items are being
            // fetched and how many are resolved/written-off — not just a
            // coarse "gathering data…". ``draft_starting`` marks the
            // hand-off to writing.
            const r = buildRetrievalSubline(
              event.event_type,
              event.section_response as Record<string, unknown> | undefined,
            );
            updateChatRunActivity({
              ...activity,
              phase:
                event.event_type === "draft_starting" || event.event_type === "checklist_drafting"
                  ? "drafting"
                  : "retrieving",
              detail: r?.subline ?? activity.detail,
              retrieval: r ? r.retrieval : activity.retrieval,
            });
            break;
          }
          case "phase_start":
          case "phase_done":
          case "check_start":
          case "check_done":
            // The section's first-draft / checks phases and each check: the
            // server's narration is the bubble's detail line.
            if (event.message) updateChatRunActivity({ ...activity, phase: "drafting", detail: event.message });
            break;
          case "section_ready":
          case "adhoc_draft_ready":
          case "regen_complete":
          case "adhoc_draft_complete":
          case "resolve_section_proposed":
          case "resolve_section_failed":
          case "error":
          case "cancelled":
            // Terminal for the activity — the durable EventLog entry
            // (draft / adhoc_draft projection) replaces the bubble.
            updateChatRunActivity(null);
            break;
          default:
            break;
        }
      }
      if (event.token_usage) {
        const usage = event.token_usage as Record<string, number>;
        const input =
          (usage.input_tokens ?? 0) + (usage.cache_creation_input_tokens ?? 0) + (usage.cache_read_input_tokens ?? 0);
        const output = usage.output_tokens ?? 0;
        const thinking = usage.thinking_tokens ?? 0;
        if (input || output || thinking) {
          setLiveTokens((prev) => ({
            input: (prev?.input ?? 0) + input,
            output: (prev?.output ?? 0) + output,
            thinking: (prev?.thinking ?? 0) + thinking,
          }));
        }
      }
    },
    [handleProgressEvent, updateChatRunActivity, reprojectSectionIntoLiveDoc, liveProjectionRef],
  );

  // Reset accumulated live-token counts whenever the runId rotates
  // (RUN_STARTED with a new id) or clears (RESET_TO_IDLE → null).
  // biome-ignore lint/correctness/useExhaustiveDependencies: state.runId is an intentional trigger dep; setLiveTokens is a stable setter
  useEffect(() => {
    setLiveTokens(null);
    setAgentActivityBySection((m) => clearAllActivity(m));
    setLatestRunActivity(null);
  }, [state.runId]);

  // ``[IMAGE DATA: <caption>]`` may name a picture the writer inserted and
  // saved into this run's document — let the formatter resolve those first.
  useEffect(() => {
    setFigureRunScope(state.runId ?? null, getSessionToken);
  }, [state.runId]);

  // Wall-clock timestamp the current run started — drives the run
  // card's elapsed timer. Reset on
  // RESET_TO_IDLE; set on RUN_STARTED dispatch path.
  const [runStartedAt, setRunStartedAt] = useState<number | null>(null);
  // Percent complete while run-restore is repainting sections into Word.
  // Null hides the inline indicator next to the stop control.
  const [restoreHydrationProgressPct, setRestoreHydrationProgressPct] = useState<number | null>(null);

  // Plan-review state (owned here so useSessionBootstrap can seed planId/planData from hydrate).
  const [planData, setPlanData] = useState<PlanData | null>(null);
  const [planId, setPlanId] = useState<string | null>(null);

  // Surfaced when the empty-doc detector observes a "had content →
  // empty" transition and there's a project in flight to abandon.
  // Drives the NewProjectConfirmModal render.
  const [newProjectConfirmOpen, setNewProjectConfirmOpen] = useState(false);
  // Surfaced when the writer starts a brand-new draft while the current document
  // already holds a (non-actively-drafting) run. Holds the pending selection so
  // the confirm handler can save the current doc, reset to idle, then launch the
  // new submission's section picker. Drives the SaveAndStartFreshModal render.
  const [freshDraftPending, setFreshDraftPending] = useState<LoomFreshSelection | null>(null);
  const [freshDraftSaving, setFreshDraftSaving] = useState(false);
  // Surfaced (feature-flagged) on login when a non-empty document is opened
  // with no associated run — offers the "build on this document" flow. One-shot
  // per session; the ref latches so a re-render can't re-open it after dismiss.
  const [importExistingDocOpen, setImportExistingDocOpen] = useState(false);
  const importDetectionDoneRef = useRef(false);
  // A content-bearing document opened by a first-time writer (the "Try on
  // Word" starter document). Treated like an empty document: the Home scene
  // instead of the build-on prompt, and the first draft replaces the text
  // without the save-and-clear dialog. Cleared once a draft starts.
  const [starterDoc, setStarterDoc] = useState(false);
  const starterDocRef = useRef(false);
  // Latched by the custom-import detector when the OPEN document has content but
  // no run this user can access (a foreign AsthrDocumentId → another user's runs).
  // Consumed by the commit handlers (rotateForeignDocIfNeeded) to rotate the
  // doc_id before startRun so the backend tenancy guard
  // (server/routes/pipeline.py:1847) sees a brand-new document.
  const foreignDocDetectedRef = useRef(false);
  const emptyDocWatcherRef = useRef<EmptyDocWatcherHandle | null>(null);
  // Ref to the CommandRail so clicking ``/command`` links in chat
  // bubbles can programmatically prefill and focus the input.
  const commandRailRef = useRef<CommandRailHandle>(null);
  // Stable callback — safe to declare here because it only closes
  // over the ref (which has a stable identity throughout the
  // component lifetime).
  const handleReply = useCallback((message: ChatMessage) => {
    commandRailRef.current?.setReplyTarget(message);
  }, []);

  // The LoomSelection commit — captured so actuallyStartRun can pass
  // module_ids + the shell can thread the writer's strategy preview
  // choice forward. Null before the first commit of this run, or
  // after RESET_TO_IDLE clears run-scoped state.
  const [sectionCommit, setSectionCommit] = useState<LoomSelectionCommit | null>(null);

  // Refs for beforeunload handlers — readable without stale closures.
  const runIdRef = React.useRef<string | null>(null);
  const runStateRef = React.useRef(state.state);
  const [runSubscriptionNonce, setRunSubscriptionNonce] = useState(0);
  const [runSubscriptionStartMode, setRunSubscriptionStartMode] = useState<"history" | "latest">("history");
  // Bumped by useDraftFlow on every drafting start so useVariantCRun paints
  // the "Preparing run" card even for continuations (``latest`` mode).
  const [runBootSeedNonce, setRunBootSeedNonce] = useState(0);
  // True when the restored run's status (from the REST /runs/{id} fetch
  // in useRunControl) is terminal. ``useVariantCRun`` reads this to skip
  // its SSE subscribe effect — Redis stream is evicted ~15 min after
  // completion and a subscribe attempt 404s.
  const [terminalRunStatus, setTerminalRunStatus] = useState(false);
  // True while useRunControl is projecting the backend snapshot into
  // reducer + spine + Word state. useVariantCRun uses this as an SSE gate
  // so restore cannot race with live stream updates.
  const [restoreInFlight, setRestoreInFlight] = useState(false);

  // ----- Document session bootstrap + beforeunload flush -----------------
  const {
    documentSessionId,
    setDocumentSessionId,
    sessionLoading,
    sessionWorkspaceRef,
    restoredIdentity,
    bootstrapRunId,
    bootstrapDismissedRunId,
    setPersistedSpineSnapshot,
    persistedChatMessages,
    setPersistedChatMessages,
    persistedChatHasMore,
    setPersistedChatHasMore,
    restoredConversationId,
    setRestoredConversationId,
    restoredInsertionMode,
    knownHadContent,
    setKnownHadContent,
    restoredInventorySnapshot,
    restoredInventoryDoctype,
  } = useSessionBootstrap({
    setSelectedLibraryId,
    setSelectedDocCode,
    setActiveSelection,
    setDocConfig,
    setLeafDocumentId,
    setSectionCommit,
    setPlanId,
    setPlanData,
    setFocusedSectionId,
    dispatch,
  });

  useEffect(() => {
    if (sessionLoading) return;
    const mode = restoredInsertionMode ?? "batch";
    insertionModeRef.current = mode;
    void saveWordInsertionMode(mode);
    const docId = getCachedDocId();
    if (!docId) return;
    scheduleSave(docId, { insertion_mode: mode });
  }, [sessionLoading, restoredInsertionMode, insertionModeRef]);

  // ----- Paragraph sync engine -------------------------------------------
  // The real-time block-level sync engine is GATED on the document having been
  // marked "ready" (the pipeline draft was loaded into Word for this run — see
  // AsthrSyncReady / markSyncReady). Before the first load the body holds only
  // placeholder text ("Asthra is drafting…") that must NEVER be pushed to
  // DocService over the real server-side draft. We start it imperatively (via a
  // ref) so the load handler can kick it off the instant the draft lands — while
  // still paused — preserving the seed→pause→resume-rebaseline ordering.
  const syncCleanupRef = useRef<null | (() => void)>(null);
  const syncStartedRunRef = useRef<string | null>(null);
  // Bumped when a Load marks the document sync-ready, so the gate effect below
  // re-checks even when the loaded run is the one already in state.
  const [syncGateNonce, setSyncGateNonce] = useState(0);

  const startSyncEngineForCurrentRun = useCallback(() => {
    const runId = state.runId ?? null;
    if (!runId) return;
    if (syncStartedRunRef.current === runId && syncCleanupRef.current) return; // already running
    syncCleanupRef.current?.();
    // The watcher only knows the cursor's dotted section number; hand it the
    // live spine so a scoped auto-save can fetch ONE module by id instead of
    // the whole document. Read via sectionsRef so this callback isn't rebuilt
    // on every spine update.
    syncCleanupRef.current = initSyncEngine(
      () => state.runId ?? null,
      (sectionNumber) => sectionsRef.current.find((s) => s.number === sectionNumber)?.id ?? null,
    );
    syncStartedRunRef.current = runId;
    console.log(`[sync-gate] sync engine started for runId=${runId}`);
  }, [state.runId]);

  const stopSyncEngine = useCallback(() => {
    if (syncCleanupRef.current) {
      syncCleanupRef.current();
      syncCleanupRef.current = null;
      console.log("[sync-gate] sync engine stopped");
    }
    syncStartedRunRef.current = null;
  }, []);

  // On mount / runId change, restore sync only if the document was already marked
  // ready for this run (e.g. reopening an already-loaded draft). A fresh run whose
  // draft hasn't been loaded yet stays gated off until the load handler starts it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: syncGateNonce is an explicit re-check trigger
  useEffect(() => {
    if (sessionLoading) return;
    const runId = state.runId ?? null;
    if (!runId) {
      stopSyncEngine();
      return;
    }
    let cancelled = false;
    void Promise.all([isSyncReady(runId), getDraftPointer()]).then(([ready, pointer]) => {
      if (cancelled) return;
      // draft_pointer === "server" means a draft / "Draft more sections" run is
      // in progress and owns the document server-side. Keep sync off even across
      // a task-pane reload mid-run — until handleLoadDraftedDocument flips the
      // pointer back to "live" — so the add-in never pushes the Word body over
      // the server's concurrent writes (the DLL-scramble root cause).
      if (ready && pointer !== "server") startSyncEngineForCurrentRun();
      else stopSyncEngine();
    });
    return () => {
      cancelled = true;
    };
  }, [sessionLoading, state.runId, startSyncEngineForCurrentRun, stopSyncEngine, syncGateNonce]);
  startSyncEngineRef.current = startSyncEngineForCurrentRun;

  // Stop the engine on unmount.
  useEffect(() => () => stopSyncEngine(), [stopSyncEngine]);

  // ----- Persist UI state across reload ----------------------------------
  // Spine + chat persistence: wire each piece of writer-visible UI
  // state to ``scheduleSave`` so a refresh / reopen rehydrates it from
  // MongoDB. Each effect is gated on ``sessionLoading === false`` so
  // the bootstrap's restored values do not immediately echo back to
  // the backend (which would be a no-op write but still wasteful and
  // would also overwrite a concurrent same-doc edit from another tab
  // before that tab's hydrate had a chance to merge).

  // Persist the timeline filter on every change after bootstrap.
  useEffect(() => {
    if (sessionLoading) return;
    const docId = getCachedDocId();
    if (!docId) return;
    scheduleSave(docId, {
      timeline_filter: state.timelineFilter as unknown as Record<string, unknown>,
    });
  }, [sessionLoading, state.timelineFilter]);

  // Persist the focused section ID on every change after bootstrap.
  // ``null`` is a meaningful clear (writer dismissed the inspector) so
  // we send it explicitly — the patch builder translates ``null`` into
  // a backend ``clear_fields`` directive.
  useEffect(() => {
    if (sessionLoading) return;
    const docId = getCachedDocId();
    if (!docId) return;
    scheduleSave(docId, { focused_section_id: focusedSectionId });
  }, [sessionLoading, focusedSectionId]);

  // ----- Spec 23 W1 + W2: workspace / library state --------------------
  const {
    workspaces,
    selectedWorkspaceId,
    libraries,
    workspaceFetchError,
    libraryInventory,
    libraryInventoryLoading,
    libraryResolveEvent,
    refreshLibraries,
    refreshLibraryInventory,
    classifyLibraryInventory,
    invalidateResolveGuard,
    removeDocumentFromInventory,
    handleSelectedWorkspaceIdChange,
    createAndSelectWorkspace,
  } = useWorkspaceLibrary({
    sessionLoading,
    sessionWorkspaceRef,
    selectedLibraryId,
    setSelectedLibraryId,
    selectedDocCode,
    activeDocCode: activeSelection?.docCode,
    runId: state.runId,
    dispatch,
    userIdentifier,
    restoredInventorySnapshot,
    restoredInventoryDoctype,
  });

  // Library id → name, so a Home/Drafts card whose run carries no
  // ``library_name`` still labels its library (never the project name).
  const libraryNameById = useMemo(() => {
    const byId: Record<string, string> = {};
    for (const lib of libraries) if (lib.name) byId[lib.id] = lib.name;
    return byId;
  }, [libraries]);

  // -- Spine ⇄ Timeline view switch (Spec 17 P4) -------------------------
  // Regen flow — handled by useRegenFlow below (after resolveSectionLabel).

  // -- Spine ⇄ Timeline view switch (Spec 17 P4) -------------------------
  // Single dispatch; reducer is idempotent (same-mode → same object,
  // React skips re-render). See SpineTimelineToggle for the UX.
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    dispatch({ type: "VIEW_MODE_SET", mode });
    scheduleSave(getCachedDocId(), { view_mode: mode });
  }, []);

  // Timeline reads the log newest-first, narrowed by the active
  // filter. Memoised on (log, filter) — the reducer hands fresh refs
  // on either change, so reference-equality tracks semantic change.
  // entryMatchesFilter returns true for every entry when the filter
  // is empty (its "all dims null" semantics), so this single
  // expression covers both filtered and unfiltered cases.
  const timelineEntries = useMemo(
    () => entriesNewestFirst(state.eventLog).filter((e) => entryMatchesFilter(e, state.timelineFilter)),
    [state.eventLog, state.timelineFilter],
  );

  // Variant D Phase 3 — per-document chat history. Seeded from the
  // backend hydrate payload for this Word document and incrementally
  // synced back to the backend as EventLog entries arrive.
  // Phase: run-scoped chat — passing ``state.runId`` makes the view
  // filter to "messages for this run + session-level history". When the
  // run changes (Drafts panel load, RUN_RESTORED, fresh RUN_STARTED),
  // the hook re-seeds from ``persistedChatMessages`` which the shell
  // refetches via ``setPersistedChatMessages`` in ``handleRunLoaded``.
  // Pagination: the seed is the newest CHAT_PAGE_SIZE messages;
  // ``loadOlder`` prepends earlier pages as the timeline scrolls up.
  const {
    messages: chatHistoryMessages,
    hasMoreOlder: chatHasMoreOlder,
    isLoadingOlder: chatIsLoadingOlder,
    loadOlder: chatLoadOlder,
  } = useChatHistoryView(
    documentSessionId,
    state.eventLog.entries,
    persistedChatMessages,
    state.runId ?? null,
    persistedChatHasMore,
  );

  // Spec 23 P7 — run-level counter aggregate drives the event
  // legend strip above the spine. Memoised on the counters map ref
  // Whether any filter dimension is currently active. Drives the
  // empty-state hints (shows "M of N events" vs "N events").
  // (Previously drove SpineTimelineToggle's context strip, retired
  // in Spec 29 Phase 1 when the toggle was replaced by the TabBar.)
  const filterIsActive = useMemo(() => !isEmptyFilter(state.timelineFilter), [state.timelineFilter]);

  // -- Initial section list for the run ------------------------------------
  const initialSections = useMemo<SpineSection[]>(() => {
    if (!docConfig) return [];
    return spineSectionsFromConfig(docConfig);
  }, [docConfig]);

  // -- SSE wiring ----------------------------------------------------------
  // Live audit-library build line (audit_library_* SSE → reviewStore), shown
  // under the spine's "Preparing run" card: the build starts in parallel with
  // drafting and otherwise only surfaces on the Issues tab.
  const auditLibraryBuildPhase = React.useSyncExternalStore(subscribeAuditLibraryBuild, () =>
    state.runId ? getAuditLibraryBuild(state.runId) : null,
  );
  const auditLibraryInitNote = auditLibraryBuildPhase?.message
    ? `Regulatory review library: ${auditLibraryBuildPhase.message}`
    : null;

  // A manual section audit ("Run review") can run on an idle/completed run,
  // which otherwise holds no live SSE stream. Track whether any audit is in
  // flight for this run so useVariantCRun keeps a stream open for it — that is
  // how the audit's live step/counter narration reaches the panel.
  const reviewAuditActive = React.useSyncExternalStore(subscribeAuditActivity, () =>
    state.runId ? isAuditActiveForRun(state.runId) : false,
  );
  // The automatic post-draft audit runs only once the audit library is ready,
  // and a per-run catch-up sweep audits the sections that finished drafting while
  // it was still building — those audits routinely fire AFTER the run is
  // terminal. Keep the audit-only SSE stream open while the library is building
  // (and briefly after it becomes ready) so their live events reach this client;
  // otherwise the loaded doc + spine would only update on a manual reload. Once
  // the sweep's first audit_started arrives, reviewStore's own audit-active
  // signal keeps the stream open, so this only has to bridge the gap.
  const [auditLibPending, setAuditLibPending] = useState(false);
  useEffect(() => {
    const runId = state.runId ?? null;
    if (!runId) {
      setAuditLibPending(false);
      return undefined;
    }
    let cancelled = false;
    let poll: ReturnType<typeof setInterval> | null = null;
    let graceTimer: ReturnType<typeof setTimeout> | null = null;
    const stopPoll = () => {
      if (poll) {
        clearInterval(poll);
        poll = null;
      }
    };
    const tick = async () => {
      let status: string | undefined;
      try {
        const lib = await getRunAuditLibrary(runId);
        status = lib?.audit_library?.status;
      } catch {
        status = undefined;
      }
      if (cancelled) return;
      // ``preparing`` covers the brief pre-ref window at the very start of a
      // custom submission's temp-source bootstrap, before the builder stamps
      // ``acquiring`` — so the audit SSE stream opens in time to carry the
      // temp-library build events to reviewStore's build-phase store.
      const building = status === "acquiring" || status === "processing" || status === "preparing";
      if (building) {
        setAuditLibPending(true);
        return;
      }
      // Settled (ready / empty / failed / none) or no library — stop polling. If
      // it just became ready, hold a grace window so the catch-up sweep's first
      // audit_started can attach before the stream closes.
      stopPoll();
      if (status === "ready") {
        setAuditLibPending(true);
        graceTimer = setTimeout(() => {
          if (!cancelled) setAuditLibPending(false);
        }, 90_000);
      } else {
        setAuditLibPending(false);
      }
    };
    void tick();
    poll = setInterval(() => void tick(), 8000);
    return () => {
      cancelled = true;
      stopPoll();
      if (graceTimer) clearTimeout(graceTimer);
    };
  }, [state.runId]);
  const auditActive = reviewAuditActive || auditLibPending;
  // -- Word-write side effects -------------------------------------------
  // Fired before the spine's own translation for every SSE event the
  // run emits. Two responsibilities:
  //   1. gate_start (first one only) — scaffold the Word doc with the
  //      doc-type's heading skeleton so the writer sees structure
  //      immediately, before any draft text arrives. Section mode only;
  //      batch mode lets the backend assemble the final docx.
  //   2. section_ready — write that section's draft_content into the
  const {
    sections,
    activeSectionId,
    hydrateSections,
    identity,
    adoptIdentity,
    streamError,
    initStatus,
    setSectionStatus,
    setSectionInstruction,
    seedStreamCursor,
    clearSectionActivity,
  } = useVariantCRun({
    runId: state.runId,
    initialSections,
    dispatch,
    onProgressEvent: handleProgressEventWithTokens,
    subscriptionNonce: runSubscriptionNonce,
    subscriptionStartMode: runSubscriptionStartMode,
    bootSeedNonce: runBootSeedNonce,
    runState: state.state,
    terminalRunStatus,
    auditActive,
    restoreInFlight,
    initialIdentity: restoredIdentity,
  });

  // Nudge the Drafts panel to refresh the instant a run completes or a section
  // finishes drafting, so a fresh draft shows without waiting for the poll.
  useEffect(() => {
    const draftedNow = sections.filter((s) => s.status === "drafted").length;
    const completed = state.state === "post-run";
    if (draftedNow !== draftedProgressRef.current || completed) {
      draftedProgressRef.current = draftedNow;
      setDraftsRefreshNonce((n) => n + 1);
    }
    // The run_bundles row the panel reads is written around pipeline_complete,
    // so a completed run may not be indexed yet on this first bump — a second
    // bump ~1.5s later beats that write race (the 15s poll is the backstop).
    if (completed) {
      const t = setTimeout(() => setDraftsRefreshNonce((n) => n + 1), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [state.state, sections]);

  // -- Payment / checkout (native Dodo checkout for custom workspaces) ----
  // Drafting is free unless the active workspace is billable. The demo
  // workspace is exempt server-side, so its sample libraries never see a price;
  // a custom workspace is billable, so its custom libraries do. Prices are taken
  // from /doc-types (live doc_pricing.json); the charge is computed server-side.
  // The new-document checkout, once the gate has diverted a start into it. Both
  // orders are computed up front — the writer chooses per-section or the whole
  // document in the dialog — and the run is built at pay time from that choice.
  interface PendingCheckout {
    readonly sectionOrder: Order | null;
    readonly wholeOrder: Order | null;
    readonly docId: string;
    readonly docType: string;
    readonly selection: LoomFreshSelection;
    readonly moduleIds: string[] | null;
    readonly workspaceId: string;
    /** Billable roots the selection covers, for a section-scoped promo grant. */
    readonly selectedRoots: string[];
    /** The run being continued (unlocking sections mid-document), or null for a fresh run. */
    readonly continueRunId: string | null;
    /** The writer's picker comments, replayed onto whichever run the payment starts. */
    readonly runInstructions: string | null;
  }
  const [pendingCheckout, setPendingCheckout] = useState<PendingCheckout | null>(null);
  // The doctype a writer picked that is priced server-side but not on the
  // self-serve allowlist, so there is no checkout to open for it. Non-null holds
  // `SalesAssistedDialog` up; see the sales-assisted branch in `paymentGate`.
  const [salesAssisted, setSalesAssisted] = useState<string | null>(null);
  // The custom "build on your own document" paywall. Run-less: on payment
  // either the stashed import selection is dispatched (a fresh custom import),
  // or the refused action is re-run (an audit or "Draft more" on a custom run
  // that is not yet unlocked — see `payments/paywall.ts`).
  const [customUnlock, setCustomUnlock] = useState<{
    readonly selection: LoomFreshSelection | null;
    readonly retry?: () => void;
    readonly docId: string;
    readonly docType: string;
  } | null>(null);
  const pendingCustomRef = useRef<{ selection: LoomFreshSelection | null; retry?: () => void } | null>(null);
  const startCustomFlowRef = useRef<((selection: LoomFreshSelection) => void) | null>(null);
  const finalizePaidRunStartRef = useRef<
    ((s: StartedPaidDraft, moduleIds?: readonly string[] | null) => Promise<void>) | null
  >(null);
  // Section scope of the run the open checkout will start (null = whole document).
  const paidRunScopeRef = useRef<readonly string[] | null>(null);
  // ``actuallyStartRun`` is declared below (by ``useDraftFlow``), so the payment
  // callbacks reach it through this ref to avoid a use-before-declaration.
  const actuallyStartRunRef = useRef<
    | ((
        selection: LoomFreshSelection,
        planId: string | null,
        moduleIds: string[] | null,
        continueRunId: string | null,
        opts?: { skipPaymentGate?: boolean; runInstructions?: string | null },
      ) => Promise<void>)
    | null
  >(null);
  const [billingOpen, setBillingOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  // Whether the active workspace is the demo (billing-exempt) one. Custom
  // libraries may only be created in a custom workspace, so a confirmed exempt
  // workspace blocks library creation and points the writer at making one.
  // Only ``exempt_workspace`` blocks — any uncertainty allows, so a lookup blip
  // never traps the writer.
  const [activeWorkspaceExempt, setActiveWorkspaceExempt] = useState(false);

  const checkout = useCheckout({
    onStarted: (started) => {
      setPendingCheckout(null);
      void finalizePaidRunStartRef.current?.(started, paidRunScopeRef.current);
    },
    onUnlocked: () => {
      // The flat custom unlock cleared. Proceed with the stashed custom build,
      // which the standard custom-submission path dispatches — or re-run the
      // refused audit / "Draft more".
      const pending = pendingCustomRef.current;
      pendingCustomRef.current = null;
      setCustomUnlock(null);
      if (pending?.selection) startCustomFlowRef.current?.(pending.selection);
      else pending?.retry?.();
    },
  });

  // A refused audit / "Draft more" on a custom document that is not unlocked
  // asks for the flat unlock here (see `payments/paywall.ts`). Per-section
  // refusals are left to the caller's message: a drafted document's sections
  // are bought from the section picker, before they are drafted.
  useEffect(
    () =>
      onPaywallRequest((request) => {
        if (request.kind !== "custom") return false;
        const docId = getCachedDocId();
        const docType =
          request.docType || lookupDoctypeChoice(selectedDocCodeRef.current)?.backendDocType || null;
        if (!docId || !docType) return false;
        setCustomUnlock({ selection: null, retry: request.retry, docId, docType });
        return true;
      }),
    [],
  );

  const paymentGate = useCallback(
    async ({
      selection,
      moduleIds,
      continueRunId,
      runInstructions,
      sectionIds,
    }: {
      selection: LoomFreshSelection;
      moduleIds: string[] | null;
      continueRunId: string | null;
      runInstructions: string | null;
      sectionIds?: readonly string[];
    }): Promise<boolean> => {
      // Anything without a config/workspace is never gated. A continuation IS
      // gated now — unlocking a section is paying for it — but it is offered
      // only the per-section order, never the whole-document upsell.
      const config = docConfig;
      const workspaceId = selection.workspaceId;
      if (!config || !workspaceId) return true;
      // Private workspace templates are not sold per document; the server's
      // gate has no product for them either.
      if (isPrivateTemplateCode(selection.backendDocType)) return true;

      // Demo / exempt workspace → free. Fails open, so a lookup blip never
      // fronts a paywall on a free run.
      const billing = await getWorkspaceBilling(workspaceId);
      if (!billing.billing_required) return true;

      // Price STRICTLY from the live doc_pricing.json (/doc-types), not the card.
      // The whole entry is kept, not just the price: `self_serve` is the only
      // thing that tells "free" from "sales-assisted" below.
      let entry: DocTypePriceEntry | undefined;
      try {
        entry = (await getDocTypes()).find((e) => e.code === selection.backendDocType);
      } catch {
        return true; // catalogue unreachable → don't block a draft
      }
      const price = docPriceOf(entry);

      const docId = getCachedDocId();
      if (!docId) return true;

      // What this document is already paid up for, so covered roots drop out of
      // the cart and a fully covered document drafts for free. Fails open.
      //
      // Asked before the sales-assisted check below, deliberately: a document
      // somebody has already been invoiced for — or granted a promo on — must
      // draft, whatever its doctype is sold like.
      let coverage: ReturnType<typeof coverageOf>;
      try {
        coverage = coverageOf(await getDocumentPayment(docId));
      } catch {
        coverage = { whole: false, roots: new Set<string>() };
      }
      if (coverage.whole) return true;

      // **Sales-assisted is not free**, and this is where that used to go
      // wrong. `doc_pricing.json` lists two doctypes; the server's rate card
      // prices twenty-odd and the gate asks *that* one, so a CER in a billable
      // workspace is refused 402. Its price here is `null`, and reading a
      // missing price as "free" drafted straight into that refusal with no
      // checkout ever offered. There is no product to sell, so say so.
      if (isSalesAssisted(entry)) {
        setSalesAssisted(selection.backendDocType);
        return false;
      }

      if (price === undefined) return true; // genuinely not on sale → free

      // The caller's ids win: on a picker commit `sectionCommit` is set in the
      // same tick and this closure still holds the previous one, which left
      // `selected` empty and offered only the whole document.
      const selected = sectionIds ?? sectionCommit?.resolvedSectionIds ?? [];
      const covered = coverage.roots;

      // The per-section order, with already-paid roots removed.
      let sectionOrder: Order | null = null;
      if (selected.length > 0) {
        const result = buildOrder({
          config,
          docId,
          docType: selection.backendDocType,
          price,
          selected,
          whole: false,
          covered,
        });
        if (!isRefusal(result)) sectionOrder = result;
        else if (result.kind === "all-covered") return true; // selection already paid
        // "nothing-selected" / "sold-whole" → fall through to the whole-doc offer
      }

      // The whole-document order, offered as the "pay for everything" choice —
      // but only on a fresh run. A continuation adds sections to an existing run,
      // so buying the whole document is not one of its moves.
      const wholeResult = continueRunId
        ? null
        : buildOrder({
            config,
            docId,
            docType: selection.backendDocType,
            price,
            selected,
            whole: true,
            covered,
          });
      const wholeOrder = wholeResult && !isRefusal(wholeResult) ? wholeResult : null;

      if (sectionOrder === null && wholeOrder === null) return true;

      setPendingCheckout({
        sectionOrder,
        wholeOrder,
        docId,
        docType: selection.backendDocType,
        selection,
        moduleIds,
        workspaceId,
        selectedRoots: [...billableRoots(config, selected, covered)],
        continueRunId: continueRunId ?? null,
        runInstructions,
      });
      return false; // diverted into checkout
    },
    [docConfig, sectionCommit],
  );

  // Build the run for a chosen order and open its checkout. Whole-document scope
  // drafts every module (``module_ids: null``); a section order drafts exactly
  // what was picked.
  const payForOrder = useCallback(
    (order: Order) => {
      const pc = pendingCheckout;
      if (!pc) return;
      const run: CheckoutRun = {
        project_name: synthesizeProjectName(pc.selection),
        name: pc.selection.name?.trim() || undefined,
        library_ids: [pc.selection.libraryId],
        library_names: { [pc.selection.libraryId]: pc.selection.libraryName },
        module_ids: order.scope === "document" ? null : (pc.moduleIds ?? null),
        library_inventories: libraryInventory ? { [pc.selection.libraryId]: libraryInventory } : undefined,
        insertion_mode_override: "batch",
        workspace_id: pc.workspaceId,
        continue_run_id: pc.continueRunId ?? undefined,
        run_instructions: pc.runInstructions ?? undefined,
        region: pc.selection.region || undefined,
      };
      paidRunScopeRef.current = run.module_ids ?? null;
      void checkout.begin([...order.items], run);
    },
    [pendingCheckout, libraryInventory, checkout],
  );

  // Apply a promo to the new-document order. A valid code grants coverage
  // server-side, so re-driving the start passes the gate for free.
  const applyOrderPromo = useCallback(
    async (code: string, scope: "section" | "document"): Promise<{ ok: boolean; message?: string }> => {
      const pc = pendingCheckout;
      if (!pc) return { ok: false, message: "Nothing to unlock." };
      const result = await postPromo(
        scope === "document"
          ? { code, doc_id: pc.docId, doc_type: pc.docType, scope: "document" }
          : {
              code,
              doc_id: pc.docId,
              doc_type: pc.docType,
              scope: "section",
              sections: pc.selectedRoots,
            },
      );
      if (!result.valid) return { ok: false, message: "That code is not valid." };
      setPendingCheckout(null);
      const moduleIds = scope === "document" ? null : pc.moduleIds;
      void actuallyStartRunRef.current?.(pc.selection, null, moduleIds, pc.continueRunId, {
        runInstructions: pc.runInstructions,
      });
      return { ok: true };
    },
    [pendingCheckout],
  );

  // -- Draft flow (handleStartDraft → plan → run start) -------------------
  // Saves Word before a "Draft more" redraft of drafted sections (useDraftFlow).
  const saveBeforeRedraft = useCallback(async () => {
    await autoSaveRef.current?.();
  }, []);
  const {
    planLoading,
    planError,
    draftingMore,
    lockedModuleIdsForNextPlan,
    sectionPickerActive,
    pendingDraftStart,
    setPendingDraftStart,
    planApiHealthy,
    handleFetchSections,
    handleStartDraft,
    handlePlanApprove,
    handlePlanSkip,
    handlePlanModify,
    handleSectionCommit,
    handleSectionCancel,
    handleDraftMoreSections,
    resetDraftFlowState,
    handleGenerateSection,
    actuallyStartRun,
    finalizePaidRunStart,
    startCustomSubmission,
  } = useDraftFlow({
    runId: state.runId,
    activeSelection,
    setActiveSelection,
    docConfig,
    setDocConfig,
    sectionCommit,
    setSectionCommit,
    planId,
    setPlanId,
    planData,
    setPlanData,
    setStarting,
    setStartError,
    setRunSubscriptionStartMode,
    seedStreamCursor,
    setTerminalRunStatus,
    setRunSubscriptionNonce,
    setRunBootSeedNonce,
    setRunStartedAt,
    dispatch,
    scaffoldedRef,
    insertionModeRef,
    liveProjectionRef,
    libraryInventory,
    sections,
    submissionMode: runSubmissionMode,
    paymentGate,
    saveBeforeRedraft,
  });

  // The section picker names a new document and files it ("Save this document
  // as" above Start), so it needs the run workspace's folders while it is open.
  const pickerFolderWorkspaceIds = useMemo(
    () => (sectionPickerActive && activeSelection?.workspaceId ? [activeSelection.workspaceId] : []),
    [sectionPickerActive, activeSelection?.workspaceId],
  );
  const { folders: pickerFolders, create: createPickerFolder } = useFolders(pickerFolderWorkspaceIds);
  const pickerNaming = useMemo<LoomNaming | undefined>(() => {
    if (!activeSelection) return undefined;
    const folderLabel = activeSelection.folderId
      ? pickerFolders?.find((f) => f.id === activeSelection.folderId)?.label
      : undefined;
    if (draftingMore) return { mode: "existing", name: activeSelection.name, folderLabel };
    return {
      mode: "new",
      suggestedName: activeSelection.name?.trim() || suggestedDraftName(activeSelection),
      // The folder row appears once the workspace's folders load — even when
      // the writer has none yet, so "+ New folder…" can file the first one.
      // None without a workspace: a folder belongs to one.
      folders:
        pickerFolders && activeSelection.workspaceId
          ? {
              options: [
                { value: "", label: "Unfiled" },
                ...pickerFolders.map((f) => ({ value: f.id, label: f.label })),
              ],
              initial: activeSelection.folderId ?? "",
            }
          : undefined,
    };
  }, [activeSelection, draftingMore, pickerFolders]);

  // The chat's setup card shows the document's name and folder. New
  // documents get them in "Save as" (``activeSelection``); a reopened one, or
  // one renamed / refiled on Home or Drafts, from the server's run record,
  // refetched when a run starts or loads and when the writer switches views.
  const documentRecord = useDocumentRecord(documentSessionId || null, `${state.runId ?? ""}|${state.viewMode}`);
  const setupFolderId = documentRecord?.folderId ?? activeSelection?.folderId ?? null;
  const setupFolderWorkspace = activeSelection?.workspaceId ?? documentRecord?.workspaceId ?? null;
  const setupFolderWorkspaceIds = useMemo(
    () => (setupFolderId && setupFolderWorkspace ? [setupFolderWorkspace] : []),
    [setupFolderId, setupFolderWorkspace],
  );
  const { folders: setupFolders } = useFolders(setupFolderWorkspaceIds);
  const setupDocument = useMemo(
    () => setupDocumentInfo(activeSelection, documentRecord, setupFolders ?? pickerFolders),
    [activeSelection, documentRecord, setupFolders, pickerFolders],
  );
  // Resolve the picker's folder (creating a new one first) before the commit
  // reaches the draft flow, which files the run into ``folderId``.
  const handlePickerCommit = useCallback(
    async (commit: LoomSelectionCommit) => {
      if (commit.name && commit.folder) {
        let folderId: string | null;
        if ("newLabel" in commit.folder) {
          try {
            folderId = (await createPickerFolder(commit.folder.newLabel)).id;
          } catch (err) {
            setStartError(`Couldn't create the folder: ${err instanceof Error ? err.message : String(err)}`);
            return;
          }
        } else {
          folderId = commit.folder.value || null;
        }
        pendingFolderIdRef.current = folderId;
        await handleSectionCommit({ ...commit, folderId });
        return;
      }
      await handleSectionCommit(commit);
    },
    [createPickerFolder, handleSectionCommit],
  );

  // Bridge the checkout's onStarted (defined above useDraftFlow) to the paid
  // run finaliser (returned by it) without a declaration cycle.
  useEffect(() => {
    finalizePaidRunStartRef.current = finalizePaidRunStart;
    actuallyStartRunRef.current = actuallyStartRun;
  });

  // Impacted-sections panel: redraft through the same continue-sections path.
  const sourceImpactRedraft = useMemo(
    () => makeRedraftHandler(actuallyStartRun, activeSelection, state.runId),
    [actuallyStartRun, activeSelection, state.runId],
  );

  // Resolve whether the active workspace is the demo (exempt) one, for the
  // library-create guard. Blocks only on a confirmed ``exempt_workspace``.
  useEffect(() => {
    if (!selectedWorkspaceId) {
      setActiveWorkspaceExempt(false);
      return;
    }
    let live = true;
    void getWorkspaceBilling(selectedWorkspaceId)
      .then((b) => {
        if (live) setActiveWorkspaceExempt(b.billing_reason === "exempt_workspace");
      })
      .catch(() => {
        if (live) setActiveWorkspaceExempt(false);
      });
    return () => {
      live = false;
    };
  }, [selectedWorkspaceId]);

  // Keep refs in sync on every render so beforeunload / pagehide
  // handlers can read the current run state without stale closures.
  // Intentionally has no dependency array — runs after every render.
  useEffect(() => {
    runIdRef.current = state.runId;
    runStateRef.current = state.state;
  });

  // Save spine snapshot only when the count of "completed" sections changes
  // (drafted | needs_approval | flagged), not on every subline / animation
  // tick. Reduces from ~160 serialise+network calls per 40-module run down
  // to ~40 (one per module that transitions out of queued/drafting).
  const completedSectionCount = sections.reduce(
    (n, s) => n + (s.status === "drafted" || s.status === "flagged" || s.status === "needs_approval" ? 1 : 0),
    0,
  );
  // activeSelection guards entry but does not gate the trigger — picking
  // a new library/doctype must not re-persist the same snapshot.
  const activeSelectionRef = useRef(activeSelection);
  useEffect(() => {
    activeSelectionRef.current = activeSelection;
  });
  // sections / activeSelection intentionally omitted — sections is read for
  // data but completedSectionCount is the intended trigger, and activeSelection
  // gates entry only (read via ref so its churn doesn't re-fire).
  // biome-ignore lint/correctness/useExhaustiveDependencies: completedSectionCount is the intended trigger
  useEffect(() => {
    const docId = getCachedDocId();
    if (!docId || !activeSelectionRef.current || sections.length === 0) return;
    scheduleSave(docId, {
      spine_snapshot: toPersistedSpineSnapshot(sections),
    });
  }, [completedSectionCount]);

  // Persist the full selection tuple unconditionally any time
  // ``activeSelection`` lands. Coupling persistence to the state mutation
  // (rather than to specific gestures like ``handleStartDraft``) means
  // every code path that sets a selection — picker commit, programmatic
  // restore, library-only intermediate pick — gets the full record on
  // disk. This was the root cause of doc-mopwpigy-mcf8j5's null fields:
  // the writer's path through the picker hit ``advanceDraftWithLibrary``
  // (partial save) without ever reaching ``handleStartDraft``, and no
  // effect was watching the state itself. (Bug A — 2026-05-07)
  useEffect(() => {
    const docId = getCachedDocId();
    if (!docId || !activeSelection) return;
    scheduleSave(docId, {
      workspace_id: activeSelection.workspaceId ?? undefined,
      library_id: activeSelection.libraryId,
      library_name: activeSelection.libraryName,
      doc_code: activeSelection.docCode,
      doc_name: activeSelection.docName,
      doc_framework: activeSelection.docFramework,
      backend_doc_type: activeSelection.backendDocType,
    });
    // Mirror to Word custom properties as a secondary source of truth.
    // On reload the bootstrap reads these back if the MongoDB session
    // is missing or was written without library/doctype fields.
    saveDocSelectionProps({
      libraryId: activeSelection.libraryId,
      libraryName: activeSelection.libraryName,
      docCode: activeSelection.docCode,
      docName: activeSelection.docName,
      backendDocType: activeSelection.backendDocType,
      workspaceId: activeSelection.workspaceId,
    });
  }, [activeSelection]);

  // Empty-doc detector. Mounts as soon as the doc id is resolved —
  // we DON'T gate on ``activeSelection`` because a corrupted session
  // (selection fields all null but spine/plan/run still present, the
  // exact Frankenstein state Bug A produced) would otherwise never
  // get the prompt. The "is there really a project here" check lives
  // in ``initialKnownToHaveContent`` instead, sourced from the
  // hydrate payload.
  //
  // Re-mounts when ``knownHadContent`` flips from false → true
  // (bootstrap completes after the doc id arrives). The dependency
  // array deliberately includes it so the watcher reads the
  // up-to-date initial state.
  useEffect(() => {
    if (!documentSessionId) return;
    const handle = startEmptyDocWatcher({
      // An empty body isn't always a "cleared document". If this doc has a
      // run still streaming server-side (e.g. the writer logged out/in, or
      // reopened the file before it hydrated), the destructive rotate-project
      // prompt would orphan that run. Check the live feed first: when a live
      // run targets this doc, stay silent — HomeScene renders (empty
      // classification) and floats its "Reconnect" card instead. The heartbeat
      // check inside /runs/active means a dead/terminal run won't suppress the
      // genuine "start fresh?" prompt.
      onEmpty: () => {
        const docId = getCachedDocId();
        if (!docId) {
          setNewProjectConfirmOpen(true);
          return;
        }
        void listActiveRuns({ docId, limit: 1 })
          .then((resp) => {
            if ((resp.runs ?? []).length > 0) {
              console.info("[VariantCShell] empty doc has a live run — offering reconnect, not rotate", {
                docId,
                runId: resp.runs[0]?.run_id,
              });
              return;
            }
            setNewProjectConfirmOpen(true);
          })
          .catch((err: unknown) => {
            // Feed unreachable — fall back to the existing prompt rather than
            // silently swallowing a genuine clear.
            console.warn("[VariantCShell] live-run check failed; showing rotate prompt:", err);
            setNewProjectConfirmOpen(true);
          });
      },
      initialKnownToHaveContent: knownHadContent,
    });
    emptyDocWatcherRef.current = handle;
    // Trigger-driven check — no ambient subscription. Fire once on
    // setup; ``state.state``-transition effect below also calls check()
    // on run-completion and reset. Routed through queueWordRead so
    // it can't race a selection-change probe that's still draining.
    void queueWordRead("emptyDocCheck:init", () => handle.check());
    return () => {
      handle.stop();
      if (emptyDocWatcherRef.current === handle) {
        emptyDocWatcherRef.current = null;
      }
    };
  }, [documentSessionId, knownHadContent]);

  // Run the empty-doc check on state transitions (esp. mid-run →
  // post-run / idle, which is when the writer is most likely to clear
  // the doc and start over). Not on flips between active states: the
  // run owns the doc then, and selection settles still check it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: state.state intentionally used
  useEffect(() => {
    const handle = emptyDocWatcherRef.current;
    if (!handle || isActiveRun(state.state)) return;
    void queueWordRead("emptyDocCheck:stateTransition", () => handle.check());
  }, [state.state]);

  // -- Custom submission (build on an existing Word doc) -------------------
  // Always on (no feature flag): whenever a non-empty document is opened that
  // cannot be mapped to a run THIS user can access, offer "build on this
  // document". The document session is keyed by doc_id (which lives in the Word
  // file and persists across users), so a doc drafted by another user hydrates
  // THEIR run_id into ``bootstrapRunId`` — which is not a run this user owns.
  // We therefore don't trust ``bootstrapRunId``/``knownHadContent`` as "this
  // user has a project here"; we PROBE the candidate run's accessibility
  // (getRunSnapshot 200 = ours, 403/404 = foreign/gone) and fall back to the
  // user-scoped live-run list.
  // One-shot per session — the ref latches so it never re-opens after dismiss.
  // biome-ignore lint/correctness/useExhaustiveDependencies: state.runId read inline; one-shot
  useEffect(() => {
    if (sessionLoading || !documentSessionId) return;
    if (importDetectionDoneRef.current) return;
    // A run is already loaded into THIS session's UI → not a candidate.
    if (state.runId) {
      importDetectionDoneRef.current = true;
      return;
    }
    let cancelled = false;
    // Only the Word read goes through queueWordRead; the run lookups below are
    // network calls, and holding the read queue across them blocked every
    // passive Word read (chips, empty-doc check) during cold open.
    void (async () => {
      const { hasContent } = await queueWordRead("customImportDetect", checkDocHasContent);
      if (cancelled || hasContent !== true) return; // empty (or unknown) → not a candidate

      // Is there a run THIS user can access for this doc? Probe the candidate —
      // the restored (doc-keyed, cross-user) ``bootstrapRunId`` first, else the
      // run id stamped in the Word metadata. getRunSnapshot succeeds only for a
      // run this user owns or was granted; a foreign/deleted run throws.
      const candidateRunId = bootstrapRunId || (await getActiveRunId().catch(() => null));
      if (candidateRunId) {
        const accessible = await getRunSnapshot(candidateRunId)
          .then(() => true)
          .catch(() => false);
        if (cancelled) return;
        if (accessible) {
          // This user's own project — leave the normal restore path alone.
          importDetectionDoneRef.current = true;
          return;
        }
        // Foreign/stale pointer (e.g. drafted by another user, reopened under
        // this SSO account) — clear it so it can't wedge this user onto an
        // inaccessible run. The document body is untouched.
        await clearActiveRunId().catch(() => {});
        if (cancelled) return;
      }

      // Belt-and-suspenders: a live run this user owns for this doc (user-scoped
      // ``/runs/active``) means it IS their project — don't offer build-on.
      const docId = getCachedDocId();
      if (docId) {
        const resp = await listActiveRuns({ docId, limit: 1 }).catch(() => null);
        if (cancelled) return;
        if (resp && (resp.runs ?? []).length > 0) {
          importDetectionDoneRef.current = true;
          return;
        }
      }

      // A COMPLETED run this user owns for this doc (``/runs/index`` →
      // ``RunBundleDoc``) is also their project — critically, a custom run is
      // ``completed`` the instant it is registered (no worker), so it never
      // appears in the live-run list above. Without this check a returning
      // writer whose Word ``AsthrRunId`` stamp was lost would be wrongly offered
      // "build on this document" instead of restoring their existing run. Stamp
      // the latest run id so the run-restore effect (useRunControl) re-anchors
      // and hydrates it (custom spine included) rather than re-importing.
      if (docId) {
        const done = await listRuns({ docId, limit: 10 })
          .then(async (resp) => {
            const runs = resp.documents.flatMap((g) => g.runs);
            if (runs.length === 0) return false;
            const latest = [...runs].sort((a, b) => b.completed_at.localeCompare(a.completed_at))[0];
            await setActiveRunId(latest.run_id).catch(() => {});
            return true;
          })
          .catch(() => false);
        if (cancelled) return;
        if (done) {
          importDetectionDoneRef.current = true;
          return;
        }
      }

      if (cancelled) return;
      importDetectionDoneRef.current = true;
      // First-time writer (no drafts at all, e.g. straight from "Try on Word",
      // which opens Word on a document that already has text): there is
      // nothing of theirs to build on, so skip the build-on prompt and show the
      // normal three-step start. The text is replaced when the first draft
      // starts (launchDraft), which the Home scene says up front.
      const firstTimeWriter = await Promise.all([
        listUserRuns(1).then((r) => r.documents.every((g) => g.runs.length === 0)),
        listUserActiveRuns(1).then((r) => (r.runs ?? []).length === 0),
      ])
        .then(([noDone, noLive]) => noDone && noLive)
        .catch(() => false);
      if (cancelled) return;
      // Latch: this is a content-bearing doc with no run this user can access —
      // its persisted AsthrDocumentId may point at another user's runs. The
      // commit handlers rotate the doc_id before startRun so the backend tenancy
      // guard (server/routes/pipeline.py:1847) sees a brand-new document.
      foreignDocDetectedRef.current = true;
      if (firstTimeWriter) {
        starterDocRef.current = true;
        setStarterDoc(true);
        return;
      }
      setImportExistingDocOpen(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionLoading, documentSessionId, bootstrapRunId]);

  // Single selection-settle subscriber: empty-doc probe, then cursor-
  // section detection, strictly serialized through queueWordRead.
  // Previously these were two separate subscribers firing concurrent
  // Word.run() invocations on the same event. checkDocEmpty() reads
  // only the first paragraph (O(1)); detectCursorSectionLight() is a
  // bookmark lookup against ASTHRA_SEC_* bookmarks (O(1)). The
  // !isActiveRun gate keeps the cursor probe quiet during a mid-run
  // draft when wordWriter holds the Office.js queue.
  useEffect(() => {
    const onSelection = () => {
      if (parkWhileWordBusy("emptyDocCheck:selection", onSelection)) return;
      void (async () => {
        await queueWordRead("emptyDocCheck:selection", () => emptyDocWatcherRef.current?.check() ?? Promise.resolve());
        await refreshCursorSectionNumber();
      })();
    };
    const unsubscribe = subscribeSelectionChange(onSelection);
    return unsubscribe;
  }, [refreshCursorSectionNumber]);

  // No timer poll: on Word desktop every tick was a Word round trip (up to 40
  // on a document without section bookmarks) on Word's UI thread, all the
  // time the writer was editing. Selection changes drive the chip; returning
  // to the task pane refreshes it in case a host dropped an event, and the
  // chip click refreshes before opening the TOC.
  useEffect(() => {
    if (isActiveRun(state.state)) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") void refreshCursorSectionNumber();
    };
    const onFocus = () => void refreshCursorSectionNumber();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [state.state, refreshCursorSectionNumber]);

  // Prime cursor-derived scope once on mount/idle so the left chip
  // doesn't stay on "document" until the first selection-change event.
  useEffect(() => {
    void refreshCursorSectionNumber();
  }, [refreshCursorSectionNumber]);

  // Clear the cursor-section chip the moment a run goes active so a stale
  // During mid-run, clear the cursor section so the §X.X.X label from the
  // prior idle browse doesn't linger over drafting. When the run ends, re-read
  // the cursor: the writer usually hasn't moved it, so no selection event
  // would ever restore the label (it stayed "document" after a chat edit).
  const cursorChipRunActiveRef = useRef(false);
  useEffect(() => {
    const active = isActiveRun(state.state);
    if (active) {
      setCursorSectionNumber(null);
    } else if (cursorChipRunActiveRef.current) {
      void refreshCursorSectionNumber();
    }
    cursorChipRunActiveRef.current = active;
  }, [state.state, refreshCursorSectionNumber]);

  // Run-level idle reset shared by every "return this document to idle"
  // path (empty-doc project rotation, and the save-and-start-fresh draft
  // flow). Clears the reducer + all run-scoped shell state (selection,
  // config, plan, spine snapshot, focus) so the next draft starts clean.
  // Deliberately does NOT touch chat/conversation state or the document id
  // — callers that need those (rotation) do them separately.
  // biome-ignore lint/correctness/useExhaustiveDependencies: all referenced values are stable setState setters or refs
  const resetShellToIdle = useCallback(() => {
    dispatch({ type: "RESET_TO_IDLE" });
    setActiveSelection(null);
    setDocConfig(null);
    setSectionCommit(null);
    setPlanData(null);
    setPlanId(null);
    resetDraftFlowState();
    setStartError(null);
    setStopWarning(null);
    setFocusedSectionId(null);
    setPersistedSpineSnapshot(null);
    setRunSubmissionMode(null);
    scaffoldedRef.current = false;
    insertionModeRef.current = "batch";
    liveProjectionRef.current = false;
    setLiveDocLoadBySection(new Map());
    void saveWordInsertionMode("batch");
  }, [resetDraftFlowState]);

  // Writer confirmed "yes, start fresh" — rotate the AsthrDocumentId
  // and reset all in-memory state so the next render bootstraps as if
  // this were a brand new document. The OLD doc_id's backend record is
  // not touched (orphaned but recoverable).
  // biome-ignore lint/correctness/useExhaustiveDependencies: all referenced values are stable module-level functions, setState setters, or refs
  const handleConfirmRotateProject = useCallback(async () => {
    setNewProjectConfirmOpen(false);
    emptyDocWatcherRef.current?.acknowledge();
    let newId = "";
    try {
      newId = await rotateDocumentId();
    } catch (err) {
      console.warn("[VariantCShell] rotateDocumentId failed:", err);
    }
    if (!newId) {
      // Office.js unavailable or rotation threw — nothing safe to do
      // beyond surfacing the failure quietly. The detector will
      // re-arm on the next content/empty cycle.
      return;
    }
    // Reset shell state. Mirrors handleReset's idle-reset shape plus
    // the persistence-seeded fields the bootstrap normally sets. The
    // run-scoped portion is shared with the save-and-start-fresh flow;
    // rotation additionally wipes chat and the document id below.
    resetShellToIdle();
    setPersistedChatMessages([]);
    setPersistedChatHasMore(false);
    setRestoredConversationId(undefined);
    setKnownHadContent(false);
    // Last — flip the cached doc id so any subsequent saves land on
    // the new project's record. The bootstrap-on-mount effect doesn't
    // re-run for the new id (it's gated on []), so we don't issue a
    // hydrate against the new id; that's fine because there's nothing
    // to hydrate — the new id is brand new.
    //
    // Two doc-id stores must move together:
    //   1. ``_cachedDocId`` (module-level in documentSessionService) —
    //      read by every ``scheduleSave(getCachedDocId(), ...)`` callsite,
    //      including the activeSelection persist effect, run_id save,
    //      and spine_snapshot save.
    //   2. ``documentSessionId`` (React state) — read by
    //      ``useChatHistoryView`` to route chat sync writes.
    //
    // Pre-2026-05-10 only #2 was updated, so post-rotation session/run/
    // spine writes silently kept landing on the old doc_id while chat
    // writes landed on the new one. The next reload's hydrate found
    // an empty session for the new id and the spine never restored.
    //
    // Flush first so any debounced patch already queued under the old
    // id (e.g. the RESET_TO_IDLE side-effects above) drains there
    // instead of getting re-targeted to the new id mid-write.
    flushAllPendingSaves();
    setCachedDocId(newId);
    setDocumentSessionId(newId);
  }, []);

  // Writer chose "Cancel — undo": replay Word's undo stack until the
  // body has content again (or until we hit the loop guard). Office's
  // ``context.document.undo()`` is per-step, so a multi-step delete
  // needs a few iterations.
  const handleCancelRestoreProject = useCallback(async () => {
    setNewProjectConfirmOpen(false);
    emptyDocWatcherRef.current?.acknowledge();
    try {
      await Word.run(async (context) => {
        // Bound the loop so a writer who Ctrl+Z'd into a never-restored
        // state doesn't pin the thread. 8 steps covers reasonable
        // multi-stroke deletes; beyond that we give up and leave the
        // doc in whatever state Word landed on.
        for (let i = 0; i < 8; i++) {
          context.document.undo();
          await context.sync();
          const body = context.document.body;
          body.load("text");
          await context.sync();
          if ((body.text ?? "").trim().length > 0) break;
        }
      });
    } catch (err) {
      console.warn("[VariantCShell] undo restore failed:", err);
    }
  }, []);

  // Phase 9 — single resolver for the writer-facing section label.
  // Closes over ``sections``; every shell-side ``EVENT_APPENDED``
  // dispatch with a ``sectionId`` calls this so the persisted entry
  // carries ``§${number}`` instead of the backend module id. The
  // pure helper lives in ``sectionLabel.ts`` so projection + tests
  // ssetDocumentSessionIdresetDraftFlowStatesetKnownHadContentsetRestoredConversationIdsetPersistedChatMessagessetPersistedSpineSnapshotscaffoldedRefinsertionModeRefhare the same format.
  const resolveSectionLabel = useCallback(
    (sectionId: string | null): string | null => formatSectionLabel(sectionId, sections),
    [sections],
  );

  // -- Regenerate-one handlers (Spec 23 P10.5) ----------------------------
  // Regen runs INSIDE the original run_id (no new run is created). See
  // `addin/src/variant-c/hooks/useRegenFlow.ts` — `handleRegenConfirm`
  // POSTs to /api/runs/{run_id}/sections/{module_id}/regenerate and
  // lets the existing SSE subscription carry the new events. The two
  // setters below are threaded in because the SSE subscribe effect can
  // bail out on terminal-run reloads; we clear the gate + bump the
  // nonce on confirm so the EventSource re-attaches in time.
  const {
    regenTarget,
    regenEnabled,
    handleInspectorRegenerate,
    openRegenFor,
    handleRegenConfirm,
    handleRegenCancel,
    regenActivity,
    pauseRegen,
    stopRegen,
    draftNowRegen,
    resumeRegen,
    discardPausedRegen,
    onRegenEvent,
  } = useRegenFlow({
    focusedSectionId,
    docConfig,
    sections,
    runId: state.runId,
    runState: state.state,
    dispatch,
    resolveSectionLabel,
    setSectionStatus,
    setTerminalRunStatus,
    setRunSubscriptionNonce,
    setSubscriptionStartMode: setRunSubscriptionStartMode,
    seedStreamCursor,
    setFocusedSectionId,
  });

  // Bulk "Resolve issues" — kicks the orchestrator on the same run_id and
  // re-subscribes so the flags being fixed update live on the Issues tab.
  const resolveIssuesFlow = useResolveIssuesFlow({
    runId: state.runId,
    setTerminalRunStatus,
    setRunSubscriptionNonce,
    setSubscriptionStartMode: setRunSubscriptionStartMode,
    seedStreamCursor,
    // Every pass is shown in the Fix issues window, opened by the entry point
    // (Start in the window, or fixNow below) — the Issues tab doesn't track it.
    onShowFixProgress: () => {},
  });
  resolveFlowOnEventRef.current = resolveIssuesFlow.onEvent;

  // The server refuses a resolve pass while the run is still drafting (409), so
  // don't walk the writer through the wizard only to drop them back here.
  const [resolveNotice, setResolveNotice] = useState<string | null>(null);
  // Every entry point (Issues tabs, the "Fix flags" chip, a row's retry) goes
  // through here. Returns false when the gate refused.
  const openResolveWizard = useCallback(
    (targets: ResolveSelectionTarget[]): boolean => {
      if (state.state === "mid-run") {
        setResolveNotice(RESOLVE_WHILE_DRAFTING_MESSAGE);
        return false;
      }
      setResolveNotice(null);
      resolveIssuesFlow.clearError();
      setFixPassTargets(null);
      setResolveWizardTargets(targets);
      return true;
    },
    [state.state, resolveIssuesFlow.clearError],
  );
  // One click: fix these flags now, searching the whole library — no window.
  // Progress shows on the rows ("Fixing…") and the changes land in the review
  // queue. The Fix window stays one link away ("Choose sources…").
  const fixNowRef = useRef<(targets: ResolveSelectionTarget[]) => void>(() => {});
  const fixNow = useCallback((targets: ResolveSelectionTarget[]) => fixNowRef.current(targets), []);
  const fixProgress = useMemo<FixProgress | null>(() => {
    if (!fixPassTargets) return null;
    const paused = resolveIssuesFlow.phase === "paused";
    const finished = resolveIssuesFlow.phase === "idle";
    const status: FixProgress["status"] = {};
    const live: FixProgress["live"] = {};
    for (const t of fixPassTargets) {
      for (const id of t.issue_ids) {
        const v = fixStatusFor(id, t.module_id, {
          inFlight: resolveIssuesFlow.inFlightModuleIds,
          outcomes: resolveIssuesFlow.issueOutcomes,
          finished: finished || paused,
        });
        status[id] = paused && v === "no_change" ? "stopped" : v;
      }
      const lines = agentActivityBySection.get(t.module_id);
      if (lines && lines.length > 0) live[t.module_id] = lines[lines.length - 1].text;
    }
    return { status, live, finished, paused, error: resolveIssuesFlow.error };
  }, [
    fixPassTargets,
    resolveIssuesFlow.phase,
    resolveIssuesFlow.inFlightModuleIds,
    resolveIssuesFlow.issueOutcomes,
    resolveIssuesFlow.error,
    agentActivityBySection,
  ]);
  // Closing the window leaves the pass (and its targets) running in the pane,
  // so "Show progress" can open the same view again.
  const closeFixWindow = useCallback(() => {
    setResolveWizardTargets(null);
  }, []);
  const showFixProgress = useCallback(() => {
    if (fixPassTargets) setResolveWizardTargets(fixPassTargets);
  }, [fixPassTargets]);
  // A pass still running when the pane (re)opened: list its flags again so
  // "Show progress" can reopen it — without opening the window by itself.
  const reattachedTargets = resolveIssuesFlow.reattachedTargets;
  useEffect(() => {
    if (!reattachedTargets || !state.runId) return;
    let live = true;
    const toTargets = (sections: Map<string, RunIssueSection>): ResolveSelectionTarget[] =>
      reattachedTargets.map((t) => {
        const sec = sections.get(t.module_id);
        const ids = new Set(t.issue_ids);
        return {
          module_id: t.module_id,
          number: sec?.number ?? t.module_id,
          title: sec?.title ?? "",
          issue_ids: t.issue_ids,
          issues: sec?.issues.filter((i) => ids.has(i.id)).map(toFixIssue),
        };
      });
    getRunIssues(state.runId, "all")
      .then((resp) => {
        if (live) setFixPassTargets((cur) => cur ?? toTargets(new Map(resp.sections.map((s) => [s.module_id, s]))));
      })
      .catch(() => {
        if (live) setFixPassTargets((cur) => cur ?? toTargets(new Map()));
      });
    return () => {
      live = false;
    };
  }, [reattachedTargets, state.runId]);

  // The "Review N changes" strip on the Spine: shown for the last pass's
  // summary until the writer opens the Issues tab from it or hides it.
  const [reviewStripHiddenFor, setReviewStripHiddenFor] = useState<ResolveSummary | null>(null);
  const reviewStripCount =
    resolveIssuesFlow.summary && reviewStripHiddenFor !== resolveIssuesFlow.summary
      ? resolveIssuesFlow.summary.proposals
      : 0;
  // Approved title-page fill → rewrite the cover in Word the same way a
  // drafted title page lands (template cover or the styled title table).
  const writeResolvedTitleFields = useCallback(
    (fields: Record<string, string>) => {
      const tp = docConfig?.title_page as { style?: TitlePageStyle; statement_fields?: string[] } | undefined;
      return enqueueWordWrite<unknown>(() =>
        writeTitlePage(docConfig?.document_type, fields, () =>
          hasTemplateCover(tp) ? Promise.resolve() : updateTitlePageFields(fields, tp?.style, tp?.statement_fields),
        ),
      );
    },
    [docConfig, enqueueWordWrite],
  );
  const resolveError = resolveNotice ?? resolveIssuesFlow.error;
  const dismissResolveError = useCallback(() => {
    setResolveNotice(null);
    resolveIssuesFlow.clearError();
  }, [resolveIssuesFlow.clearError]);
  regenFlowOnEventRef.current = onRegenEvent;

  // Check if any section is currently drafting (regen in progress)
  const isAnyRegenInProgress = sections.some((s) => s.status === "drafting");

  const handleChatRegenReconnect = useCallback(() => {
    setTerminalRunStatus(false);
    setRunSubscriptionStartMode("latest");
    setRunSubscriptionNonce((value) => value + 1);
  }, []);

  // -- Intent-routed chat --------------------------------------------------
  // Default-ON since 2026-09-08. It used to be opt-in via a
  // `chat_intent_routing` flag that nothing seeded, so any environment
  // without the hand-inserted key silently lost the edit-highlight
  // (Approve/Reject) flow. `chat_intent_routing_disabled` is the kill
  // switch: absent/false → routing on (useFlag is closed-by-default).
  const intentRoutingEnabled = !useFlag("chat_intent_routing_disabled");

  // -- Unfinished UI affordances (show.incomplete-actions) ----------------
  // Single kill-switch for CTAs that are visible but not yet functional.
  // OFF — the default, and the value while flags are loading or if the
  // fetch fails — hides them. AppSource certification rejects
  // non-functional buttons, so "hidden" has to be the state that needs no
  // successful server round-trip; see the closed-by-default semantics of
  // useFlag in addin/src/context/FeatureFlagsContext.tsx.
  //
  // Declared here rather than beside ``advanced-commands`` further down
  // because the IdleGreeting render block reads it earlier in this
  // component body, and a const is not hoisted.
  const showIncompleteActions = useFlag("show.incomplete-actions");

  // -- Internal/developer affordances (show.internal-tools) ---------------
  // Separate from show.incomplete-actions: those CTAs are hidden because
  // they do not work yet, these work fine but expose internals writers
  // have no reason to see (provider roster, raw model ids). Same
  // closed-by-default polarity — hidden unless a flag says otherwise.
  //
  // Gates the LIVE/SANDBOX mode pill and the model-backend switcher. The
  // token counter is the remaining affordance flagged in pre-AppSource
  // testing and belongs on this same flag.
  const showInternalTools = useFlag("show.internal-tools");

  // -- Doctype catalog breadth ------------------------------------------
  // The picker shows exactly the doctypes the signed-in user's tier may
  // draft, straight from the backend's tier-filtered GET /doc-types (a
  // pay_per_use gets only the doctypes/doc_pricing.json codes; other tiers get
  // all). No client-side allowlist or feature flag — the server is the
  // authority, and the run-start gate enforces it regardless.
  //
  // Scoped to the selected workspace as well: the same call returns that
  // workspace's private templates (members only), shown on the picker's
  // "Workspace templates" tab.
  const {
    allowedDocCodes: tierAllowedDocCodes,
    privateTemplates,
    regions: submissionRegions,
  } = useAllowedDocCodes(selectedWorkspaceId || null);

  // A private template belongs to the workspace it was picked in — the server
  // refuses a run of it anywhere else. Switching to a workspace that does not
  // have it drops the (not yet started) pick rather than heading for a 403.
  useEffect(() => {
    if (!isPrivateTemplateCode(selectedDocCode) || privateTemplates === null) return;
    if (!privateTemplates.some((t) => t.code === selectedDocCode)) setSelectedDocCode(null);
  }, [selectedDocCode, privateTemplates]);

  const handleChatRunActivity = useCallback(
    (
      activity: {
        moduleId: string;
        kind: "regen" | "adhoc";
        instruction: string;
      } | null,
    ) => {
      updateChatRunActivity(
        activity
          ? {
              ...activity,
              phase: "starting",
              draftSoFar: "",
              startedAt: Date.now(),
            }
          : null,
      );
    },
    [updateChatRunActivity],
  );

  // Writer-facing status line for the live drafting bubble. Reuses
  // TimelineView's streamingStatus/streamingDraft transient row, so a
  // chat-initiated regen/ad-hoc reads exactly like chat streaming.
  // Prefixed with a label ([Redraft] / [Draft content]) so the flow
  // stays visible through the whole run. "Redraft" is a literal —
  // /regen is a command now, not a routed intent.
  const chatRunActivityStatus = useMemo(() => {
    if (!chatRunActivity) return null;
    const label = resolveSectionLabel(chatRunActivity.moduleId) ?? chatRunActivity.moduleId;
    const verb = chatRunActivity.kind === "regen" ? "Rewriting" : "Writing new content for";
    const prefix = `[${chatRunActivity.kind === "regen" ? "Redraft" : intentLabel("draft_content")}] `;
    switch (chatRunActivity.phase) {
      case "retrieving":
        // Stream the live retrieval subline (same detail the spine shows) when
        // available; fall back to the coarse phrase before the first event.
        return `${prefix}${verb} ${label} — ${chatRunActivity.detail ?? "searching your sources…"}`;
      case "drafting":
        return `${prefix}${verb} ${label} — writing…`;
      default:
        return `${prefix}${verb} ${label}…`;
    }
  }, [chatRunActivity, resolveSectionLabel]);

  // Records a proposal-Insert outcome as an ``approval`` EventLog entry
  // (the chat audit trail's durable record of the writer's decision).
  const handleProposalDecision = useCallback(
    (note: string, sectionId: string | null) => {
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "you",
          sectionId,
          sectionLabel: sectionId ? (resolveSectionLabel(sectionId) ?? undefined) : undefined,
          runId: runIdRef.current,
          payload: { kind: "approval", decision: "accepted", note },
        },
      });
    },
    [resolveSectionLabel],
  );

  // -- Highlight edit flow (chat "edit highlight" intent) -----------------
  // Two entries per decision: the durable ``edit_proposal`` (drafted
  // output + outcome — the assistant bubble in the thread) at ``ts``,
  // and the ``approval`` system note (audit invariant) at ``ts + 1``
  // (the same pairing convention askwhy uses so ordering is stable).
  const handleHighlightEditDecision = useCallback(
    (
      decision: "accepted" | "rejected",
      note: string,
      sectionId: string | null,
      proposal: import("./hooks/useHighlightEditFlow").HighlightEditDecisionRecord,
    ) => {
      const ts = Date.now();
      const sectionLabel = sectionId ? (resolveSectionLabel(sectionId) ?? undefined) : undefined;
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts,
          actor: "asthra",
          sectionId,
          sectionLabel,
          runId: runIdRef.current,
          payload: {
            kind: "edit_proposal",
            instruction: proposal.instruction,
            originalText: proposal.originalText,
            replacementText: proposal.replacementText,
            decision,
            outcomeNote: note,
          },
        },
      });
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: ts + 1,
          actor: "you",
          sectionId,
          sectionLabel,
          runId: runIdRef.current,
          payload: { kind: "approval", decision, note, shownOnBubble: true },
        },
      });
      // Auto-save (2026-07-21): persist the accepted edit to the SSOT,
      // scoped to just this section (sectionId is the module id).
      if (decision === "accepted") {
        void autoSaveModuleRef.current?.(sectionId);
        // Re-check open items for the section: the SSOT write-back has landed
        // (and with it the inline ledger reconcile), so nudge the inspector's
        // Data Gaps tab to re-fetch the updated open missing-info + regulator
        // flags for the full section.
        setIssuesRefreshSignal((n) => n + 1);
      }
    },
    [resolveSectionLabel],
  );

  const handleHighlightEditError = useCallback((message: string) => {
    dispatch({
      type: "EVENT_APPENDED",
      entry: {
        ts: Date.now(),
        actor: "asthra",
        sectionId: null,
        runId: runIdRef.current,
        payload: { kind: "agent_note", text: message },
      },
    });
  }, []);

  const highlightEdit = useHighlightEditFlow({
    onDecisionRecorded: handleHighlightEditDecision,
    onError: handleHighlightEditError,
    // @-mention attachments on the edit request resolve against the
    // active source library (mirrors useChatSystem's libraryId pick).
    libraryId: activeSelection?.libraryId ?? (selectedLibraryId || null),
  });

  // Streaming-status line while the rewrite request is in flight —
  // carries the intent label so the classified intent stays visible
  // from submit through the proposal's arrival.
  const highlightEditStatus = useMemo(() => {
    // "revising" is the same streaming call re-run on the writer's follow-up
    // comments, so it counts as in-flight too — it needs a status line and, via
    // composerStreamingStatus below, a working Stop button.
    if (highlightEdit.phase !== "requesting" && highlightEdit.phase !== "revising") return null;
    const target = highlightEdit.activeRequest?.sectionNumber
      ? `§${highlightEdit.activeRequest.sectionNumber}`
      : "the highlighted text";
    // The rewrite streams SSE progress (checking for context, retrieving,
    // rewriting); surface the latest step, falling back to the generic line
    // before the first event lands.
    const base = highlightEdit.progressLabel ?? `Rewriting the highlighted text in ${target}…`;
    return `[${intentLabel("edit_highlight")}] ${base}`;
  }, [highlightEdit.phase, highlightEdit.activeRequest, highlightEdit.progressLabel]);

  // The pending edit proposal, rendered as the newest assistant
  // message in the chat thread (Approve/Reject below the drafted
  // output). Passed to BOTH TimelineView mounts — the intent router
  // allows edit_highlight without a run, so the idle surface must
  // show it too.
  const editProposalNode = highlightEdit.proposal ? (
    <EditProposalMessage
      proposal={highlightEdit.proposal}
      phase={highlightEdit.phase}
      applySteps={highlightEdit.applySteps}
      progressLabel={highlightEdit.progressLabel}
      onApprove={(editedText) => void highlightEdit.approve(editedText)}
      onReject={highlightEdit.reject}
      onRevise={highlightEdit.revise}
    />
  ) : null;

  // -- Ad-hoc draft proposal flow (chat "draft content" intent) -----------
  // The full-section draft arrives via ``adhoc_draft_ready`` (or, after
  // a pane reload, as an undecided ``pending_adhoc_proposal`` in the
  // sections/state snapshot) and renders as an Approve/Reject
  // ProposalCard — the same decision surface the highlight-edit flow
  // uses. Approve is SSOT-first: the decision endpoint commits Mongo,
  // then the hook replaces the section in Word in-place.

  // ModuleId → numbered skeleton section ("9.2") for the Word insert.
  // Narrative module ids ARE skeleton numbers, so when the spine lookup
  // misses (e.g. a loaded terminal run whose sections list is thin) a
  // numeric moduleId is used directly — otherwise the Word write would
  // be silently skipped. Non-numeric modules (TITLE/spec) return null.
  const resolveSectionNumber = useCallback(
    (moduleId: string): string | null => {
      const number = sections.find((s) => s.id === moduleId)?.number;
      if (number && /^\d+(\.\d+)*$/.test(number)) return number;
      return /^\d+(\.\d+)*$/.test(moduleId) ? moduleId : null;
    },
    [sections],
  );

  // Bumped after every decision so the rehydration effect re-checks the
  // snapshot — if another section still has an undecided proposal, its
  // card surfaces next (single-card surface, newest first).
  const [adhocDecisionNonce, setAdhocDecisionNonce] = useState(0);

  // Two entries per decision, mirroring handleHighlightEditDecision:
  // the durable ``adhoc_draft`` (drafted section + outcome — the
  // assistant bubble in the thread) at ``ts``, and the ``approval``
  // system note (audit invariant) at ``ts + 1``.
  const handleAdhocProposalDecision = useCallback(
    (
      decision: "accepted" | "rejected",
      note: string,
      record: import("./hooks/useAdhocProposalFlow").AdhocProposalDecisionRecord,
    ) => {
      const ts = Date.now();
      const sectionLabel =
        resolveSectionLabel(record.moduleId) ?? (record.sectionNumber ? `§${record.sectionNumber}` : undefined);
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts,
          actor: "asthra",
          sectionId: record.moduleId,
          sectionLabel,
          runId: runIdRef.current,
          payload: {
            kind: "adhoc_draft",
            content: record.content,
            instruction: record.instruction,
            wordCount: record.wordCount,
            moduleId: record.moduleId,
            mode: record.mode,
            decision,
            outcomeNote: note,
          },
        },
      });
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: ts + 1,
          actor: "you",
          sectionId: record.moduleId,
          sectionLabel,
          runId: runIdRef.current,
          payload: { kind: "approval", decision, note, shownOnBubble: true },
        },
      });
      // Belt-and-braces spine cleanup: the section_state_changed SSE
      // normally clears the "redraft ready"-style subline, but a missed
      // event must not leave a spinner behind. Accepted decisions also
      // converge the status (edited lifecycle → drafted spine bucket).
      clearSectionActivity(record.moduleId, decision === "accepted" ? "drafted" : undefined);
      // Auto-save (2026-07-21): persist the accepted chat edit to the SSOT,
      // scoped to just this section (its markdown → draft_full + formatting
      // → section_ooxml).
      if (decision === "accepted") {
        void autoSaveModuleRef.current?.(record.moduleId);
      }
      setAdhocDecisionNonce((n) => n + 1);
    },
    [resolveSectionLabel, clearSectionActivity],
  );

  const handleAdhocProposalError = useCallback((message: string) => {
    dispatch({
      type: "EVENT_APPENDED",
      entry: {
        ts: Date.now(),
        actor: "asthra",
        sectionId: null,
        runId: runIdRef.current,
        payload: { kind: "agent_note", text: message },
      },
    });
  }, []);

  const adhocFlow = useAdhocProposalFlow({
    onDecisionRecorded: handleAdhocProposalDecision,
    onError: handleAdhocProposalError,
  });

  // Chat "draft content" → the in-request drafting stream. No run is
  // dispatched, so there is no SSE reattach and no chatRunActivity here; the
  // flow's own ``requesting`` phase and ``progressLabel`` drive the status line.
  const handleFragmentDraftRequested = useCallback(
    (req: {
      runId: string;
      moduleId: string;
      sectionNumber: string | null;
      instruction: string;
      anchorText: string | null;
      attachments?: import("../types").MentionAttachment[];
    }) => {
      void adhocFlow.requestFragment({
        runId: req.runId,
        moduleId: req.moduleId,
        sectionNumber: req.sectionNumber,
        sectionLabel: resolveSectionLabel(req.moduleId) ?? req.moduleId,
        instruction: req.instruction,
        anchorText: req.anchorText,
        libraryId: selectedLibraryId || activeSelection?.libraryId || null,
        docType: activeSelection?.backendDocType ?? null,
        attachments: req.attachments,
      });
    },
    [adhocFlow.requestFragment, resolveSectionLabel, selectedLibraryId, activeSelection],
  );

  // Status line while a fragment draft streams. Mirrors highlightEditStatus —
  // the flow surfaces the same SSE-derived progress label, so the two routed
  // flows read the same way in the composer.
  const fragmentDraftStatus = useMemo(() => {
    if (adhocFlow.phase !== "requesting") return null;
    const base = adhocFlow.progressLabel ?? "Writing the new content…";
    return `[${intentLabel("draft_content")}] ${base}`;
  }, [adhocFlow.phase, adhocFlow.progressLabel]);

  // Live producer: translate the raw ``adhoc_draft_ready`` SSE event
  // into an offer. Kept on a ref because the SSE wrapper is declared
  // above this hook (see adhocProposalOfferRef).
  const offerAdhocFromEvent = useCallback(
    (event: { module_id?: string; run_id?: string; section_response?: unknown }) => {
      const moduleId = event.module_id;
      const runId = event.run_id ?? runIdRef.current;
      if (!moduleId || !runId) return;
      const sr = (event.section_response ?? {}) as Record<string, unknown>;
      const content = typeof sr.draft_text === "string" ? sr.draft_text : "";
      if (!content) return;
      const mode: AdhocProposal["mode"] = sr.mode === "redraft" ? "redraft" : "draft";
      const proposal: AdhocProposal = {
        runId,
        moduleId,
        sectionNumber: resolveSectionNumber(moduleId),
        sectionLabel: resolveSectionLabel(moduleId) ?? moduleId,
        instruction: chatRunActivityRef.current?.instruction ?? "",
        content,
        wordCount: typeof sr.word_count === "number" ? sr.word_count : content.split(/\s+/).length,
        baseSectionVersion: typeof sr.base_section_version === "number" ? sr.base_section_version : null,
        mode,
        // Where a draft fragment splices in — echoed by the runner from the
        // cursor read at send time, because by now the cursor has moved.
        anchorText: typeof sr.anchor_text === "string" ? sr.anchor_text : null,
      };
      // Chat ``/redraft`` (Path B) auto-load: apply the full-section redraft to
      // the live doc with NO Approve/Reject card (falls back to the card on
      // version drift / apply error — see useAdhocProposalFlow.autoApply). The
      // ad-hoc ``draft`` and chat ``edit`` proposals keep the manual card.
      if (REDRAFT_AUTO_LOAD && mode === "redraft") {
        void adhocFlow.autoApply(proposal);
        return;
      }
      adhocFlow.offer(proposal);
    },
    [adhocFlow.offer, adhocFlow.autoApply, resolveSectionNumber, resolveSectionLabel],
  );
  useEffect(() => {
    adhocProposalOfferRef.current = offerAdhocFromEvent;
    return () => {
      adhocProposalOfferRef.current = null;
    };
  }, [offerAdhocFromEvent]);

  // Rehydration producer: after a pane reload (or a decision, via the
  // nonce) check the sections/state snapshot for undecided proposals
  // and offer the newest. The decision endpoint stamps proposals
  // decided, so an approved/rejected card can never come back.
  // biome-ignore lint/correctness/useExhaustiveDependencies: adhocFlow.phase is read but deliberately excluded, and adhocDecisionNonce is a deliberate extra trigger — see the note above the dep array.
  useEffect(() => {
    const runId = state.runId;
    if (!runId || adhocFlow.phase !== "idle") return;
    let cancelled = false;
    void (async () => {
      try {
        const snap = await getSectionState(runId);
        if (cancelled) return;
        const pending = snap.sections
          .filter((s) => s.pending_adhoc_proposal?.content)
          .sort((a, b) =>
            (b.pending_adhoc_proposal?.created_at ?? "").localeCompare(a.pending_adhoc_proposal?.created_at ?? ""),
          );
        const first = pending[0];
        const proposal = first?.pending_adhoc_proposal;
        if (!first || !proposal) return;
        const next: AdhocProposal = {
          runId,
          moduleId: first.module_id,
          sectionNumber: resolveSectionNumber(first.module_id),
          sectionLabel: resolveSectionLabel(first.module_id) ?? first.module_id,
          instruction: proposal.instruction,
          content: proposal.content,
          wordCount: proposal.word_count,
          baseSectionVersion: proposal.base_section_version,
          mode: proposal.mode === "redraft" ? "redraft" : "draft",
          anchorText: proposal.anchor_text ?? null,
        };
        adhocFlow.offer(next);
      } catch {
        // Snapshot fetch failed — the live SSE path still offers new
        // proposals; rehydration just doesn't happen this pass.
      }
    })();
    return () => {
      cancelled = true;
    };
    // adhocFlow.phase is intentionally NOT a dep — re-running on every
    // idle transition would re-offer while the writer is mid-decision;
    // the nonce is the explicit "check again" signal.
    // (The suppression for this sits above the useEffect call, where Biome
    // actually reads it — a biome-ignore here has no effect.)
  }, [state.runId, adhocDecisionNonce, resolveSectionNumber, resolveSectionLabel, adhocFlow.offer]);

  // The pending ad-hoc proposal, rendered as the newest assistant
  // message in the chat thread (Approve/Reject below the drafted
  // section).
  const adhocProposalNode = adhocFlow.proposal ? (
    <AdhocProposalMessage
      proposal={adhocFlow.proposal}
      phase={adhocFlow.phase}
      driftWarning={adhocFlow.driftWarning}
      applySteps={adhocFlow.applySteps}
      onApprove={() => void adhocFlow.approve()}
      onReject={() => void adhocFlow.reject()}
    />
  ) : null;

  // handleCommand — the slash-command dispatcher — is defined
  // later in the shell (just before the JSX return) so it can
  // close over chat, handleStopRun, and planReviewActive — all
  // declared below. Original location; moved 2026-04-23 for the
  // Spec 29 palette-merge commit.

  // -- Freeform chat wiring (Spec 27) — delegated to useChatSystem ------
  const {
    chat,
    handleSendChat,
    handleStopChat,
    selectionCitations: chatSelectionCitations,
    captureSelection,
    capturedSelection,
    dismissSelection,
    syncSelectionFromProbe,
    pendingApproval,
    submitApproval,
    rejectApproval,
    bridgeStatus,
  } = useChatSystem({
    runState: state.state,
    runId: state.runId,
    documentSessionId,
    activeSelection,
    selectedLibraryId,
    identity,
    restoredConversationId,
    sections,
    dispatch,
    intentRoutingEnabled,
    onChatRunActivity: handleChatRunActivity,
    onHighlightEditRequested: highlightEdit.start,
    onFragmentDraftRequested: handleFragmentDraftRequested,
  });

  // -- Context chips (above the composer) --------------------------------
  // True once any section has a draft — gates the chip row and the
  // selection-chip probe subscription (pre-generation there is nothing to
  // act on, so no ambient selection handler is attached).
  const hasDraftedContent =
    (state.runSummary?.draftedSections ?? 0) > 0 ||
    sections.some((s) => s.status === "drafted" || s.status === "needs_approval" || s.status === "flagged");
  // Live text of the writer's chip-eligible highlight (null when none).
  // Drives the selection chips AND the highlighted-text pill: mirrored
  // into the captured selection so the pill shows it immediately and the
  // next send carries it.
  const chipSelectionText = useSelectionChip(hasDraftedContent);
  useEffect(() => {
    syncSelectionFromProbe(chipSelectionText);
  }, [chipSelectionText, syncSelectionFromProbe]);
  // One edit at a time: while a highlight edit, ad-hoc draft, redraft, or
  // its proposal card is in flight, the action chips stay disabled so the
  // writer can't stack a second change on the same text.
  const chipActionsBusy =
    highlightEdit.phase !== "idle" ||
    adhocFlow.phase !== "idle" ||
    adhocFlow.proposal != null ||
    chatRunActivity !== null ||
    isAnyRegenInProgress ||
    resolveIssuesFlow.phase !== "idle";

  // The single "Asthra is working" line, for every path a composer send can
  // take. Chat intent routing (on by default) sends most messages to a routed
  // flow — draft_content / edit_highlight — which never call
  // chat.sendChat, so ``chat.isStreaming`` stays false for them. Deriving the
  // composer's busy state from chat alone therefore left routed requests with
  // no Stop button at all.
  //
  // One memo feeds all three consumers (both TimelineView mounts and
  // CommandRail's ``chatStreamingStatus``, from which it derives ``chatBusy``)
  // so a new flow can't be wired into the transcript and forgotten in the rail.
  // Composer Stop. ``handleStopChat`` alone only covers the plain-chat route;
  // intent routing (on by default) sends most sends to a routed flow that never
  // touches chat.sendChat, which is why routed requests had no working Stop.
  //
  // Each arm is dispatched independently rather than as an if/else chain: a
  // highlight edit and an ad-hoc draft can legitimately overlap, and Stop should
  // halt whatever is actually running. Every arm is a no-op when idle.
  //
  // Regen is deliberately NOT stoppable from here — the chat regeneration flow
  // is being retired, and ``cancelRun`` (the only lever that reaches it) would
  // take down the whole drafting run.
  const highlightEditStreaming = highlightEdit.phase === "requesting" || highlightEdit.phase === "revising";
  // Chat "draft content" is an in-request stream now, not a run, so Stop is an
  // abort — no cancel latch, and no chatRunActivity to unstick. ``/regen``
  // still dispatches a run and still uses the latch below.
  const fragmentDrafting = adhocFlow.phase === "requesting";
  const stoppableRedraftModuleId = chatRunActivity?.kind === "adhoc" ? chatRunActivity.moduleId : null;
  const stoppableActivity =
    chat.isStreaming || highlightEditStreaming || fragmentDrafting || stoppableRedraftModuleId !== null;
  const handleStopComposer = useCallback(() => {
    if (chat.isStreaming) handleStopChat();
    highlightEdit.cancel();
    // Aborts the fragment-draft stream; the route sees the disconnect and
    // stops the agent at its next tool boundary. No-op when idle.
    adhocFlow.cancel();
    // Read through the ref, not the render-time value — Stop can be clicked in
    // the same tick an SSE update lands.
    const activity = chatRunActivityRef.current;
    if (activity?.kind === "adhoc" && state.runId) {
      // Redraft only (the /regen command): the latch is what stops the worker.
      // Fire-and-forget — a failure here (draft already finished, transport
      // blip) must not block the local clear below, or the writer is left with
      // a status line that never ends.
      void cancelAdhocDraft(state.runId, activity.moduleId).catch(() => undefined);
      // Clear locally too. chatRunActivity is otherwise only cleared by one of
      // seven SSE events, so a dropped stream would leave the status line up
      // and ``chipActionsBusy`` stuck on with no way out.
      updateChatRunActivity(null);
    }
    if (!chat.isStreaming) {
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "asthra",
          sectionId: null,
          runId: state.runId ?? null,
          payload: { kind: "agent_note", text: "You stopped the run." },
        },
      });
    }
  }, [chat.isStreaming, handleStopChat, highlightEdit, adhocFlow.cancel, updateChatRunActivity, state.runId]);

  const composerStreamingStatus = useMemo(
    () =>
      chat.isStreaming
        ? buildStreamingStatus(
            chat.liveTool,
            chat.statusLabel,
            chat.streamingIntent ? intentLabel(chat.streamingIntent) : null,
          )
        : (highlightEditStatus ?? fragmentDraftStatus ?? chatRunActivityStatus),
    [
      chat.isStreaming,
      chat.liveTool,
      chat.statusLabel,
      chat.streamingIntent,
      highlightEditStatus,
      fragmentDraftStatus,
      chatRunActivityStatus,
    ],
  );

  // Agent-initiated write approval → the SAME ProposalCard surface
  // writer-initiated highlight edits use, rendered in the chat-thread
  // tail. The WS bridge answering semantics are untouched (approve =
  // {approved:true, content} via submitApproval). Unparseable payloads
  // fall back to UserInputInlineRow below; non-write input types
  // (api_failure, literature approval, generic forms) always stay
  // with UserInputInlineRow.
  const changeSetHandledInThread =
    pendingApproval?.inputType === "change_set_approval" && parseChangeSetPayload(pendingApproval.question) !== null;
  const writeApprovalHandledInThread =
    changeSetHandledInThread ||
    (pendingApproval?.inputType === "write_approval" && parseWriteApprovalPayload(pendingApproval.question) !== null);
  const writeApprovalNode = !pendingApproval ? null : changeSetHandledInThread ? (
    // Agent change set (doc_propose_changes): one card for every change.
    <ChangeSetApprovalMessage
      request={pendingApproval}
      bridgeConnected={bridgeStatus === "connected"}
      onSubmit={submitApproval}
    />
  ) : writeApprovalHandledInThread ? (
    <WriteApprovalMessage
      request={pendingApproval}
      bridgeConnected={bridgeStatus === "connected"}
      onSubmit={submitApproval}
    />
  ) : null;

  // Live activity — the step(s) of the current chat turn still executing,
  // drawn as the open "Working · N steps" timeline. Resolved steps arrive as
  // durable ``chat_tool_step`` EventLog entries and fold into "Took N steps".
  const liveActivityNode =
    chat.liveSteps.length === 0 ? null : (
      <ActionGroup count={chat.liveSteps.length} live startedAt={chat.streamStartedAt}>
        {chat.liveSteps.map((step) => (
          <StepRow
            key={step.stepId}
            name={getToolDisplay(step.tool).label}
            summary={step.summaryOverride ?? describeToolCall(step.tool, step.input ?? {}, false)}
            status="running"
          />
        ))}
      </ActionGroup>
    );

  // Verdict watcher (G-P2-A path c). Polls the review-agent sidecar
  // for per-section verdicts; the useEffect below translates new
  // major/refuse verdicts into APPROVAL_REQUESTED dispatches.
  // Only enabled while a run is in flight — idle/post-run shouldn't
  // poll the sidecar. Also gated behind the sidecar kill switch so we
  // stop calling the review_agent container entirely while it's off.
  const verdictWatcherEnabled = REVIEW_AGENT_ENABLED && (state.state === "mid-run" || state.state === "approval");
  const { verdicts: sectionVerdicts } = useRunVerdictWatcher({
    identity,
    enabled: verdictWatcherEnabled,
  });

  // Spec 23 P2.3 — Word-edit detection. Polls the doc body hash every
  // 30s body-hash polling. The hook's contract is "detect WRITER-
  // initiated edits" (per its docstring). Polling can't distinguish
  // source — every body change is observed equally — so the gate
  // here must encode the assumption "writes during this state are
  // writer-driven, not agent-driven".
  //
  // Off during:
  //   - idle / plan-review: no run context to attribute edits to
  //   - mid-run: agent is streaming content into Word (FixtureRunner
  //     replay OR live WriteGate-mediated section drafts). Every body-
  //     hash change in this window is the agent's write, not the
  //     writer's. Capturing them as "You edited the document" is a
  //     mis-attribution that confuses the writer.
  //   - any time chat.isStreaming: chat-initiated WriteGate writes
  //     also touch the body programmatically. Suppress those for the
  //     same reason.
  //
  // On during: approval / paused / post-run AND chat is idle. The
  // writer's manual Word edits in those windows are correctly
  // captured.
  //
  // # DECISION (2026-05-06): tighten the gate to exclude mid-run +
  // # chat-streaming. Pre-2026-05-06 the gate included mid-run on
  // # the assumption that writers might edit while watching a run
  // # progress. In practice, ALL body-hash deltas during mid-run are
  // # agent writes (sandbox replay or live drafts), and the hook
  // # mis-classified them as writer edits. The hook's docstring
  // # already declared the intended exclusion ("WriteGate-mediated
  // # writes are NOT captured here"); this gate now enforces it.
  const wordEditsEnabled =
    !chat.isStreaming && (state.state === "approval" || state.state === "paused" || state.state === "post-run");
  useWordEdits({
    enabled: wordEditsEnabled,
    agentWriteRef: agentLastWriteAtRef,
    onEdit: useCallback(
      (ts: number, snippet: string | null) => {
        dispatch({
          type: "EVENT_APPENDED",
          entry: {
            ts,
            actor: "you",
            sectionId: focusedSectionId,
            sectionLabel: resolveSectionLabel(focusedSectionId),
            runId: state.runId,
            // Spec 24 T4 — capture the diff snippet on the event so
            // WordDetail can render it without re-fetching the doc.
            // Null → snippet field omitted (panel renders honest
            // "no snippet captured" note).
            payload: snippet ? { kind: "word", snippet } : { kind: "word" },
          },
        });
      },
      [focusedSectionId, state.runId, resolveSectionLabel],
    ),
  });

  // Tracks which (sectionId, verdict) pairs we've already translated
  // into approval dispatches — prevents the popout from re-firing on
  // every poll cycle while the writer hasn't yet resolved it.
  const dispatchedApprovalsRef = React.useRef<Set<string>>(new Set());

  // Latest sections snapshot — read by the verdict watcher to derive
  // the popout label without subscribing to per-subline section churn.
  // Subscribing would re-run the watcher on every text_chunk SSE tick,
  // re-evaluating all verdicts and cascading through setSectionStatus.
  const sectionsRef = useRef(sections);
  useEffect(() => {
    sectionsRef.current = sections;
  });

  // -- Activity rows ---------------------------------------------------------
  // One line in the chat per user or agent action that is not itself a
  // message (ran a review, fixed flags, loaded the document), so the chat
  // reads as the sequence of work, in order, between the messages.
  const logActivity = useCallback(
    (actor: "you" | "asthra", text: string, sectionId: string | null = null) => {
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor,
          sectionId,
          sectionLabel: formatSectionLabel(sectionId, sectionsRef.current),
          runId: state.runId,
          payload: { kind: "activity", text },
        },
      });
    },
    [state.runId],
  );
  const logActivityRef = useRef(logActivity);
  logActivityRef.current = logActivity;

  useEffect(
    () =>
      subscribeAuditStarts((_runId, moduleId) => {
        const label = formatSectionLabel(moduleId, sectionsRef.current) ?? "a section";
        logActivityRef.current("you", `Ran a regulatory review on ${label}.`, moduleId);
      }),
    [],
  );

  // A fix-flags pass: the writer's start, then the agent's outcome once the
  // pass this client started reports its summary (a summary replayed by a
  // Load belongs to a pass already in the history).
  const resolvePassStartedRef = useRef(false);
  const startResolvePass = useCallback(
    (targets: ResolveSelectionTarget[], documentScope: Parameters<typeof resolveIssuesFlow.startResolve>[1]) => {
      const flags = targets.reduce((n, t) => n + t.issue_ids.length, 0);
      const where =
        targets.length === 1
          ? (formatSectionLabel(targets[0].module_id, sectionsRef.current) ?? "1 section")
          : `${targets.length} sections`;
      resolvePassStartedRef.current = true;
      logActivity("you", `Asked to fix ${flags} flag${flags === 1 ? "" : "s"} in ${where}.`);
      return resolveIssuesFlow.startResolve(targets, documentScope);
    },
    [logActivity, resolveIssuesFlow.startResolve],
  );
  fixNowRef.current = (targets) => {
    if (targets.length === 0) return;
    if (state.state === "mid-run") {
      setResolveNotice(RESOLVE_WHILE_DRAFTING_MESSAGE);
      return;
    }
    setResolveNotice(null);
    resolveIssuesFlow.clearError();
    setFixPassTargets(targets);
    // Straight to the window's "Fixing N issues" view (it has progress, so it
    // skips the pick-issues / sources steps).
    setResolveWizardTargets(targets);
    void startResolvePass(targets, []);
  };
  useEffect(() => {
    const summary = resolveIssuesFlow.summary;
    if (!summary || !resolvePassStartedRef.current) return;
    resolvePassStartedRef.current = false;
    const parts = [`Proposed ${summary.proposals} fix${summary.proposals === 1 ? "" : "es"} for review`];
    if (summary.not_in_sources) parts.push(`${summary.not_in_sources} not in the sources`);
    if (summary.failed) parts.push(`${summary.failed} failed`);
    if (summary.stopped) parts.push(`${summary.stopped} stopped`);
    logActivityRef.current("asthra", `${parts.join(" · ")}.`);
  }, [resolveIssuesFlow.summary]);

  // Copy a section or table word for word from a source document (no model):
  // the picker asks the server for a proposal, and the review window opens on it.
  // A whole-section copy into a section this run never drafted: reviewed in
  // the pop-up, then written into Word only (there is no record for it yet).
  const [wordOnlyCopy, setWordOnlyCopy] = useState<{
    sectionNumber: string;
    before: string;
    after: string;
    note: string;
    /** The section as a Word file, from a Word source (inserted as it is). */
    sourceDocx: string | null;
    phase: "reviewing" | "applying";
  } | null>(null);
  // Bumped when that pop-up closes: Word for Mac left its blank overlay
  // painted over the chat, so the chat repaints and pins to the newest message.
  const [chatPinSignal, setChatPinSignal] = useState(0);
  const closeWordOnlyCopy = useCallback(() => {
    setWordOnlyCopy(null);
    setChatPinSignal((n) => n + 1);
  }, []);
  const [copyTarget, setCopyTarget] = useState<{
    moduleId: string;
    number: string;
    title: string;
    placement: "replace_section" | "after_text" | "replace_text";
    /** The highlighted text a ``replace_text`` copy replaces. */
    highlight?: string;
  } | null>(null);
  const copySources = useMemo(
    () => inventoryDtoToView(libraryInventory).documents.map((d) => ({ id: d.id, name: d.name })),
    [libraryInventory],
  );
  const handleCopyDone = useCallback(
    (sourceNote: string) => {
      setCopyTarget(null);
      logActivity("you", `${sourceNote}, for review.`);
      handleViewModeChange("issues");
      setProposalsRefreshSignal((n) => n + 1);
      setReviewWindowSignal((n) => n + 1);
    },
    [logActivity, handleViewModeChange],
  );

  // sections intentionally omitted — read via sectionsRef so per-subline
  // ticks don't re-run verdict translation.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sections read via sectionsRef
  useEffect(() => {
    if (!verdictWatcherEnabled) {
      // Reset between runs so the next run can re-trigger the same
      // section if its verdict matches.
      dispatchedApprovalsRef.current = new Set();
      return;
    }
    for (const [sectionId, record] of Object.entries(sectionVerdicts)) {
      const dispatchKey = `${sectionId}::${record.verdict}::${record.endedAt}`;
      if (dispatchedApprovalsRef.current.has(dispatchKey)) continue;

      const section = sectionsRef.current.find((s) => s.id === sectionId);
      const sectionNumber = section?.number ?? sectionId;

      const approval = translateVerdictToApproval({
        sectionId,
        sectionNumber,
        verdict: record.verdict,
        scorecards: record.scorecards,
      });
      if (!approval) {
        // Pass / minor — no popout. Mark as dispatched anyway so we
        // don't keep re-evaluating identical records.
        dispatchedApprovalsRef.current.add(dispatchKey);
        continue;
      }

      dispatchedApprovalsRef.current.add(dispatchKey);
      setSectionStatus(sectionId, "needs_approval");
      dispatch({ type: "APPROVAL_REQUESTED", approval });
    }
  }, [sectionVerdicts, verdictWatcherEnabled, setSectionStatus, dispatch]);

  // Spec 23 P3 — enrich sections with quality + flaggedCount derived
  // from the sidecar's per-section verdicts. Merge happens at shell
  // level (not in useVariantCRun) because verdicts flow through a
  // different data path than SSE section updates — keeping the
  // merge here preserves the hook's single-input contract.
  //
  // Memoised on (sections, sectionVerdicts) so LoomSpine's memo'd
  // children only re-render when a relevant input actually shifts.
  const enrichedSections = useMemo<SpineSection[]>(() => {
    const hasVerdicts = Object.keys(sectionVerdicts).length > 0;
    // ``regulatoryOpenByModule`` (from the section_issues ledger) is the live,
    // authoritative source for the spine's ⚖ badge — the audit writes
    // kind="regulatory" rows there but the SSE/DB ``regulatorFlagCount`` can lag
    // (it's only set when the audit_complete payload carries the count). Overlay
    // the ledger's OPEN regulatory count once we've fetched it, so an audited
    // section immediately shows its open flags on the spine (and clears when
    // they're resolved). Falls back to the SSE/DB value before the first fetch.
    const hasReg = regulatoryOpenByModule != null;
    // A scoped run lists only its own sections, not the whole template.
    const runSections = filterSectionsToRunScope(sections, state.runModuleScope, state.runId);
    if (!hasVerdicts && !hasReg) return runSections;
    return runSections.map((s) => {
      const record = sectionVerdicts[s.id];
      const next = record ? { ...s, ...deriveQualitySignals(record) } : s;
      if (hasReg) {
        const reg = regulatoryOpenByModule[s.id] ?? 0;
        if (reg !== next.regulatorFlagCount) return { ...next, regulatorFlagCount: reg };
      }
      return next;
    });
  }, [sections, sectionVerdicts, regulatoryOpenByModule, state.runModuleScope, state.runId]);

  // Spec 24 T3 — closure for TimelineView's ApprovalDetail to look
  // up the verdict by sectionId. Memoised on sectionVerdicts so the
  // child's prop only changes when the underlying record set
  // changes (not on every shell re-render).
  const verdictForSection = useMemo(() => (sectionId: string) => sectionVerdicts[sectionId] ?? null, [sectionVerdicts]);

  // Spec 23 P9 — writer-presence signal. Fires ``isBack`` when the
  // writer returns from an absence ≥ 5min during a run. We gate on
  // run-active states (same set as useWordEdits) so idle / plan-
  // review never trip the pill — there's nothing meaningful to
  // summarise in those states.
  const presenceEnabled = state.state === "mid-run" || state.state === "approval" || state.state === "post-run";
  const presence = useWriterPresence({ enabled: presenceEnabled });

  // 2026-05-11 — the previous useDocumentCursor hook here ran a
  // Word.run on every selection event and froze Word on long CSR
  // drafts. It's been replaced by:
  //   * SuggestionChips subscribing to wordProbe.subscribeChipVisibility
  //     (cheap two-stage probe — short-circuits on ctrl+A).
  //   * handleSendChat reading the selection + cursor section on
  //     demand at click time.
  //   * emptyDocDetector being driven explicitly via state-transition
  //     useEffect above.
  // No ambient cursor state lives in the shell anymore.
  //
  // Spec 29 Phase 3 follow-up — classify the Word doc on mount
  // so the idle greeting adapts ("empty" vs "has-content" vs
  // "CER/CSR/PSUR-like"). Read once; doesn't re-run on every
  // render. See useDocumentContext.ts for the DECISION on
  // one-shot reads. ``documentContextNonce`` is bumped once after a Load
  // replaces the body (handleRunLoaded) so a document that was blank at
  // mount stops being latched as "empty" — otherwise any later return to
  // idle renders the full-screen Home scene over a full Word body.
  const [documentContextNonce, setDocumentContextNonce] = useState(0);
  const documentContext = useDocumentContext(documentContextNonce);

  // Phase 9.5 (Bundle B+D follow-up) — persist the idle greeting as
  // an ``agent_note`` event ONCE per session so it lives in the
  // chat history alongside every other writer-visible action.
  // Pre-this change the greeting only rendered as TimelineView's
  // idle banner and disappeared when the first event landed; that
  // broke the "everything in chat" mental model.
  //
  // Decision table lives in ``greetingPolicy.decideGreetingEmission``
  // so the four branches (already-emitted / wait / skip-and-mark /
  // emit) are testable without mounting the shell.
  const greetingEmittedRef = useRef(false);
  useEffect(() => {
    // When the doc is classified (empty OR has-content), the interactive
    // ``IdleGreeting`` surface (checklist / context card) IS the greeting —
    // so we must NOT also seed the plain "Welcome to Asthra AI" agent_note,
    // or it stacks on top of the card. The seeded note is only the fallback
    // for the loading/unavailable case where no IdleGreeting renders.
    const classifiedState = documentContext.state === "ready" ? documentContext.classification?.state : null;
    if (classifiedState === "empty" || classifiedState === "has-content") {
      greetingEmittedRef.current = true;
      return;
    }
    const decision = decideGreetingEmission({
      alreadyEmittedThisSession: greetingEmittedRef.current,
      classificationReady: documentContext.state === "ready",
      log: state.eventLog.entries,
      greetingSentinelId: GREETING_SENTINEL_ID,
    });
    if (decision === "wait") return;
    if (decision === "skip-and-mark") {
      greetingEmittedRef.current = true;
      return;
    }
    // ``emit`` — first-session greeting.
    greetingEmittedRef.current = true;
    dispatch({
      type: "EVENT_APPENDED",
      entry: {
        ts: Date.now(),
        actor: "asthra",
        sectionId: null,
        runId: null,
        payload: {
          kind: "agent_note",
          text: buildIdleGreeting(documentContext.state === "ready" ? documentContext.classification : null),
          commandId: GREETING_SENTINEL_ID,
        },
      },
    });
  }, [documentContext, state.eventLog.entries]);

  // Spec 26 C6 — selection-driven citation lookup (owned by useChatSystem).
  const selectionCitations = chatSelectionCitations;

  // Clickable citation markers in the Word doc. ``citationMarkerWriter``
  // wraps every ``[N]`` token in a tagged inline content control as part of
  // the section write; ``citationMarkerEvents`` subscribes to each control's
  // ``onEntered`` so the cursor entering a marker fires
  // ``selectionCitations.fetchForCitationMarker``. The walker assembles the
  // prose between this marker and the previous one (or the section heading)
  // and pipes it through the same fetch flow the chip uses, so the popover
  // renders identically regardless of trigger.
  useEffect(() => {
    let teardown: (() => Promise<void>) | null = null;
    let cancelled = false;
    void (async () => {
      const { subscribeCitationMarkerEvents } = await import("./services/citationMarkerEvents");
      if (cancelled) return;
      teardown = await subscribeCitationMarkerEvents((event) => {
        void selectionCitations.fetchForCitationMarker({
          sectionNumber: event.sectionNumber,
          controlId: event.controlId,
        });
      });
    })();
    return () => {
      cancelled = true;
      if (teardown) void teardown();
    };
  }, [selectionCitations.fetchForCitationMarker]);

  // "Things want you" count for the pill — sections awaiting
  // approval plus all verdict-flagged items across sections. These
  // are the two distinct writer-attention signals per Spec 23 P8/P9;
  // their sum is what the pill surfaces in one number so the writer
  // doesn't have to decode two counts to decide whether to act.
  const thingsWantYouCount = useMemo(() => {
    const needsApproval = enrichedSections.filter((s) => s.status === "needs_approval").length;
    const flagged = enrichedSections.reduce((sum, s) => sum + (s.flaggedCount ?? 0), 0);
    return needsApproval + flagged;
  }, [enrichedSections]);

  // Clicking the pill body / REVIEW chip: route the writer to the
  // first section that wants them, flip to spine mode so the inspector
  // can open, then acknowledge so the pill dismisses. Falls back to
  // plain acknowledge when nothing actionable is found (shouldn't
  // happen — the pill only renders when count > 0 — but defensive
  // because ``isBack`` and ``count`` are independent signals).
  const handleDigestReview = useCallback(() => {
    const target =
      enrichedSections.find((s) => s.status === "needs_approval") ??
      enrichedSections.find((s) => (s.flaggedCount ?? 0) > 0) ??
      null;
    if (target) {
      if (state.viewMode !== "spine") {
        handleViewModeChange("spine");
      }
      setFocusedSectionId(target.id);
    }
    presence.acknowledge();
  }, [enrichedSections, state.viewMode, handleViewModeChange, presence]);

  // -- Reset to idle (clears run-scoped local state) -----------------------
  // ---- Stop run --------------------------------------------------------
  // Cancel the BACKEND run (releases agent resources) AND reset the
  // shell's UI state. Was a real bug pre-this-commit: handleReset
  // dispatched RESET_TO_IDLE locally but the backend run kept burning
  // tokens silently. cancelRun → DELETE /api/runs/:id is the existing
  // backend stop primitive.
  //
  // What happens after stop:
  //   - SSE stream closes (backend stops emitting)
  //   - Sections that had completed before the stop stay in the doc +
  //     manifest (Phase B will use this for resumption)
  // -- Run control: pause, resume, stop, reset ---------------------------
  // A loaded run's persisted region becomes the selection's, so "Draft more",
  // checkout and the section picker carry the region the run was started with.
  const [runRegion, setRunRegion] = useState<string | null>(null);
  const adoptRunRegion = useCallback((region: string) => {
    setRunRegion(region);
    setSelectedRegion(region);
  }, []);
  // Re-applied whenever the selection lacks it — a session bootstrap can set
  // the selection (without a region) after the run's snapshot was read.
  useEffect(() => {
    if (runRegion && activeSelection && activeSelection.region !== runRegion) {
      setActiveSelection({ ...activeSelection, region: runRegion });
    }
  }, [runRegion, activeSelection]);
  const { handlePauseRun, handleResumePausedRun, handleStopRun, handleResyncHydration, markRunRestored } =
    useRunControl({
      onRunRegion: adoptRunRegion,
      runId: state.runId,
      runState: state.state,
      dispatch,
      setStartError,
      setStopWarning,
      setRunSubscriptionStartMode,
      setRunSubscriptionNonce,
      setRestoreInFlight,
      setRestoreHydrationProgressPct,
      setTerminalRunStatus,
      setRunStartedAt,
      setFocusedSectionId,
      scaffoldedRef,
      insertionModeRef,
      liveProjectionRef,
      enqueueWordWrite,
      resetDraftFlowState,
      setActiveSelection,
      setDocConfig,
      setSectionCommit,
      setPlanData,
      setPlanId,
      setPersistedChatMessages,
      setPersistedSpineSnapshot,
      userIdentifier,
      sections,
      hydrateSections,
      identity,
      sessionLoading,
      bootstrapRunId,
      bootstrapDismissedRunId,
      restoredInsertionMode,
      docConfig,
    });

  // "Draft now" — an override for the sections drafting right now: each stops
  // retrieving and drafts from what it has gathered; later sections run as
  // normal. Settles when a section lands or the run leaves mid-run; reverts if
  // the request fails.
  const handleDraftNowRun = useCallback(() => {
    if (!state.runId || state.state !== "mid-run" || draftNowPending) return;
    setDraftNowPending(true);
    void draftNowRun(state.runId).catch((err) => {
      console.warn("[VariantCShell] draft-now request failed:", err);
      setDraftNowPending(false);
    });
  }, [state.runId, state.state, draftNowPending]);
  useEffect(() => {
    if (state.state !== "mid-run") setDraftNowPending(false);
  }, [state.state]);

  // Resolve the focused section to a SpineSection record. The
  // inspector renders only when both are present (id + section data).
  const focusedSection = useMemo(() => {
    if (!focusedSectionId) return null;
    return sections.find((s) => s.id === focusedSectionId) ?? null;
  }, [focusedSectionId, sections]);

  // Scope chip + TOC should only treat inspector focus as authoritative when
  // the inspector context is actually active in spine view. Otherwise, stale
  // focusedSection from earlier spine interactions can pin scope incorrectly.
  // NOTE: this feeds tocActiveSectionNumber (the TOC's own "current section"
  // row highlight) — it deliberately does NOT include tocHoveredSectionNumber
  // below, so browsing the TOC with the mouse doesn't relabel which row is
  // the writer's actual current section.
  const inspectorScopeSectionNumber =
    state.viewMode === "spine" && state.runId && activeSelection && focusedSection ? focusedSection.number : null;

  // Rail-facing inspector value: a TOC hover takes priority over true
  // inspector focus so the scope chip previews the hovered section while
  // the writer browses. Falls back to inspectorScopeSectionNumber the
  // instant the hover ends (mouse leaves the row) or the TOC closes (see
  // the tocOpen-driven reset effect below), at which point resolveScope
  // falls through to the cursor section as before.
  const commandRailInspectorSectionNumber = tocHoveredSectionNumber ?? inspectorScopeSectionNumber;

  // Active section number for TOC highlighting and suggestion chips.
  // Priority: drafting (SSE-driven) > inspector (explicit) > cursor (passive) > first drafted section (fallback).
  // During a live run the agent's current section is most useful to highlight;
  // outside of a run the inspector/cursor context takes over.
  // In post-run, if neither inspector nor cursor has a section, use the first
  // drafted section so suggestion chips have something to work with.
  const firstDraftedSection = useMemo(() => {
    return sections.find((s) => s.status === "drafted" || s.status === "needs_approval" || s.status === "flagged");
  }, [sections]);

  // Single source of truth for "cursor section, with the post-run fallback
  // applied" — the CommandRail chip (and the resolveScope() it runs
  // internally) and the TOC popup's highlight must agree on this value.
  // Previously the CommandRail prop applied the firstDraftedSection
  // fallback below but tocActiveSectionNumber didn't, so the chip could
  // show a section the TOC wouldn't highlight.
  const effectiveCursorSectionNumber = useMemo<string | null>(
    () => cursorSectionNumber ?? (state.state === "post-run" ? (firstDraftedSection?.number ?? null) : null),
    [cursorSectionNumber, state.state, firstDraftedSection],
  );

  const tocActiveSectionNumber = useMemo<string | null>(
    () => state.activeSection?.sectionNumber ?? inspectorScopeSectionNumber ?? effectiveCursorSectionNumber ?? null,
    [state.activeSection, inspectorScopeSectionNumber, effectiveCursorSectionNumber],
  );

  // -- CommandRail content synthesis -------------------------------------
  // Whole-submission denominator for every in-run progress readout (status
  // rail, post-run banner, spine tab chip, chat stats). `docConfig.modules` IS
  // the server's `module_sections` — the exact same set the drafts card's
  // `sections_total` counts — so a run that drafted its selected 10 of a
  // 96-section doc type reads "10/96", matching the Home/Drafts card instead of
  // "10/10". Null (config not yet loaded) ⇒ callers fall back to the scoped
  // spine length, so progress never divides by zero or regresses.
  //
  // For a CUSTOM (build-on-existing-document) run there is no template
  // denominator — the submission IS the writer's own doc — so this returns
  // null and every readout falls back to ``sections.length`` (the doc's own
  // heading count). See ``submissionSectionTotal``.
  //
  // A run started for picked sections counts against its own scope instead
  // (the spine rows ``enrichedSections`` keeps for it), so 3 picked sections
  // read "0/3", not "0/96".
  const runIsScoped = enrichedSections.length !== sections.length;
  const submissionTotalSections = useMemo<number | null>(
    () => (runIsScoped ? enrichedSections.length : submissionSectionTotal(docConfig, runSubmissionMode)),
    [docConfig, runSubmissionMode, runIsScoped, enrichedSections.length],
  );

  // Single-line "what's the agent doing right now" message. Derived
  // from reducer state + the spine's active section so the rail
  // reads contextually without the rail itself touching state.
  const assistantMessage: string | null = useMemo(() => {
    // # DECISION (2026-04-24, reverted): idle returns null.
    // # WHY: we briefly fed ``chat.lastReply`` into this slot to
    // #      make pre-run chat replies visible. That was wrong —
    // #      lastReply is the FULL agent answer body (often
    // #      multi-paragraph markdown), not a one-line status. The
    // #      rail's assistant-line is a one-line slot designed for
    // #      copy like "Working on §9.2…", not a response body.
    // #      Feeding the full reply in duplicated the timeline row
    // #      AND rendered the markdown as raw text (no markdown
    // #      support in the rail).
    // #
    // #      Correct placement: the timeline's AskWhyDetail is the
    // #      agent-reply surface. It renders markdown (via
    // #      ReactMarkdown + remark-gfm). Errors also land as
    // #      timeline rows via the ``error`` SSE branch in
    // #      handleStreamEvent, so pre-run chat failures are
    // #      visible there too.
    if (state.state === "idle") return null;
    if (state.state === "approval" && state.pendingApproval) {
      const num = state.pendingApproval.sectionNumber;
      return `I stopped at §${num} — your call needed before I continue.`;
    }
    if (state.state === "pausing") {
      const active = sections.find((s) => s.id === activeSectionId);
      if (active) {
        return `Wrapping up §${active.number} ${active.title}, then pausing.`;
      }
      return "Finishing the current module, then pausing at the next safe checkpoint…";
    }
    if (state.state === "paused") {
      return "Run paused. Review the drafted sections or resume when you're ready.";
    }
    // # DECISION (2026-10-22): no running or post-run line. The spine shows
    // #   which sections are working and the run card shows progress and the
    // #   outcome; the line above the composer repeated them (Guru). Only
    // #   states that ask the writer to act (approval, pausing, paused) speak.
    return null;
  }, [state.state, state.pendingApproval, sections, activeSectionId]);

  // Wall-clock tick for the run card's elapsed timer. Re-renders every
  // second; we don't need finer granularity than 1s for "Xm Ys".
  const [elapsedTick, setElapsedTick] = useState(() => Date.now());
  useEffect(() => {
    if (!runStartedAt) return;
    const t = setInterval(() => setElapsedTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [runStartedAt]);

  // -- Mini spine (Chat tab run card) ------------------------------------
  // # DECISION: a drafting run shows in the Chat tab as ONE live card, a
  // #   miniature of the Spine, not one chat message per drafted section.
  // # WHY: Guru (2026-10-16): every drafted section arriving as its own
  // #      chat message is ugly; the sandbox "mini spine" fixed that. Ported
  // #      onto the launch-film Spine so the card matches the Spine tab.
  // # DATE: 2026-10-16
  // Rows are ``enrichedSections`` — the Spine tab's own list, scoped to the
  // run's picked sections — so the card and the Spine agree on scope.
  const capsuleState = capsuleStateFor(state.state, state.error !== null);
  // Where the card sits in the stream: the run's start. A restored run has no
  // live start, so fall back to its run_start entry, then its persisted "Run
  // started" note, then the moment the card first appeared.
  const capsuleAnchorRef = useRef<{ runId: string | null; at: number } | null>(null);
  const capsuleAt = useMemo(() => {
    if (!capsuleState) return null;
    const fromLog = timelineEntries.find((e) => e.payload.kind === "run_start" && e.runId === state.runId)?.ts;
    const fromHistory = (chatHistoryMessages ?? []).find(
      (m) => m.role === "system" && m.run_id === state.runId && (m as { text?: string }).text === RUN_STARTED_TEXT,
    )?.at;
    const known = runStartedAt ?? fromLog ?? fromHistory ?? null;
    if (known !== null) return known;
    if (capsuleAnchorRef.current?.runId !== state.runId) {
      capsuleAnchorRef.current = { runId: state.runId, at: Date.now() };
    }
    return capsuleAnchorRef.current.at;
  }, [capsuleState, timelineEntries, chatHistoryMessages, runStartedAt, state.runId]);
  const handleOpenSectionFromCapsule = useCallback(
    (sectionId: string) => {
      handleViewModeChange("spine");
      setFocusedSectionId(sectionId);
    },
    [handleViewModeChange],
  );
  // A batch that has finished collapses into its ribbon (TimelineView draws
  // one per batch, from the stream's run notes), so the live card shows
  // only while the batch runs or is paused.
  const capsuleLive = capsuleState === "running" || capsuleState === "paused";
  const settledBatch =
    capsuleState === "done" || capsuleState === "failed"
      ? { summary: state.runSummary ?? null, stopped: capsuleState === "failed" }
      : null;
  const inlineCapsule =
    capsuleLive && capsuleAt !== null && enrichedSections.length > 0
      ? {
          at: capsuleAt,
          // TimelineView hands in the batch's newest folded step (its
          // activation / trial-context notes, web searches) as the status line.
          node: (latestStep: RunStep | null) => (
            <RunCapsule
              statusLine={latestStep ? { text: runStepStatusText(latestStep), at: latestStep.at } : null}
              state={capsuleState}
              sections={enrichedSections}
              activeSectionId={activeSectionId}
              agentActivityBySection={agentActivityBySection}
              runTimeMs={runStartedAt ? Math.max(0, elapsedTick - runStartedAt) : null}
              onOpenSpine={() => handleViewModeChange("spine")}
              onOpenSection={handleOpenSectionFromCapsule}
              onPause={capsuleState === "running" ? handlePauseRun : undefined}
              onResume={state.state === "paused" ? handleResumePausedRun : undefined}
            />
          ),
        }
      : null;

  // -- Command palette command list --------------------------------------
  // Built from current state via the pure registry. Only writers in
  // idle / post-run can flip sandbox mode (server returns 409 mid-run
  // anyway). Settings + Library-approval commands omitted — wiring
  // those needs separate plumbing (lift modal state, route to classic
  // settings) that would expand this commit's scope; they appear as

  // -- Approval popout handlers -------------------------------------------
  // "Mark resolved": writer's call landed; spine moves on. Status
  // back to drafted; reducer flips out of approval. The audit
  // record on disk preserves the verdict that triggered this — no
  // information lost by accepting.
  const handleApprovalAccept = useCallback(() => {
    const sectionId = state.pendingApproval?.sectionId;
    if (sectionId) setSectionStatus(sectionId, "drafted");
    dispatch({ type: "APPROVAL_RESOLVED" });
    // Spec 23 P2.1 — log the approval decision to the EventLog so
    // Timeline shows "Approval accepted · §X" alongside the run's
    // other events. Writer-originated (actor="you"); sectionId
    // pins the entry to the approval's target.
    if (sectionId) {
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "you",
          sectionId,
          sectionLabel: resolveSectionLabel(sectionId),
          runId: state.runId,
          payload: { kind: "approval", decision: "accepted" },
        },
      });
    }
  }, [state.pendingApproval, state.runId, setSectionStatus, resolveSectionLabel]);

  // "Open Review": writer wants to act on the findings. Focus the
  // section + inspector opens; we resolve the popout so the spine
  // is unblocked while the writer works in the inspector.
  const handleApprovalOpenReview = useCallback(() => {
    const sectionId = state.pendingApproval?.sectionId;
    if (sectionId) {
      setFocusedSectionId(sectionId);
      setSectionStatus(sectionId, "drafted");
    }
    dispatch({ type: "APPROVAL_RESOLVED" });
    // Spec 23 P2.1 — log as an "edited" decision: the writer chose
    // to dive into the Review tab rather than simply accept. Same
    // event kind; different decision value. Timeline distinguishes
    // them via formatEventTitle.
    if (sectionId) {
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "you",
          sectionId,
          sectionLabel: resolveSectionLabel(sectionId),
          runId: state.runId,
          payload: { kind: "approval", decision: "edited" },
        },
      });
    }
  }, [state.pendingApproval, state.runId, setSectionStatus, resolveSectionLabel]);

  // Spine ``N⚖`` regulator badge → focus the section + open its Review tab so
  // the persisted regulatory findings render. Bumping the signal (rather than
  // only setting focus) makes the tab switch fire even when the section is
  // already focused.
  const handleOpenReview = useCallback((sectionId: string) => {
    setFocusedSectionId(sectionId);
    setReviewOpenSignal((n) => n + 1);
  }, []);
  // Handle kill confirmation — calls handleStopRun from useRunControl,
  // which always lands on the post-run confirmation banner in the spine
  // (even with zero drafted sections), so the run stays open here.
  const handleConfirmKill = useCallback(async () => {
    setKillConfirming(true);
    try {
      await handleStopRun();
      setShowKillConfirmation(false);
    } finally {
      setKillConfirming(false);
    }
  }, [handleStopRun]);

  // Auto-clear startError once the writer changes selection (via reset).
  useEffect(() => {
    if (state.state === "idle" && startError && !activeSelection) {
      setStartError(null);
    }
  }, [state.state, startError, activeSelection]);

  // Clear a lingering stop-unconfirmed warning once a fresh run is under way
  // (a new run id + an active mid-run state) — it referred to the prior run.
  useEffect(() => {
    if (stopWarning && state.runId && state.state === "mid-run") {
      setStopWarning(null);
    }
  }, [stopWarning, state.runId, state.state]);

  // Detect image selection on selection settle. This used to POLL a Word.run
  // every 500 ms forever: harmless on Word for the web, but on Word desktop
  // each probe interrupts Word's UI thread (caret/scroll stutter) and the
  // probes pile up behind a long Load. Event-driven, coalesced, serialized
  // with the other passive reads, and parked while a Load or write owns Word
  // (replayed once it is idle, so the Replace Image chip still catches up).
  useEffect(() => {
    const checkImageSelection: () => Promise<void> = coalesceInFlight(async () => {
      if (parkWhileWordBusy("isImageSelected", () => void checkImageSelection())) return;
      try {
        const selected = await queueWordRead("isImageSelected", isImageSelected);
        setImageSelected(selected);
      } catch {
        // Silent fail — image detection is optional UI enhancement
      }
    });
    void checkImageSelection();
    return subscribeSelectionSettled(() => void checkImageSelection(), 300);
  }, []);

  // Navigate to Images tab when "Replace Image" chip is clicked.
  const handleReplaceImage = useCallback(() => {
    dispatch({ type: "VIEW_MODE_SET", mode: "images" });
  }, []);

  // -- Render --------------------------------------------------------------
  const docMeta = deriveDocCaption(state, activeSelection);

  // Library Hub slide-over control — declared early so draft flow
  // handlers can reference it.
  const openSlideOver = useCallback((mode: HubMode) => {
    setHubSlideOverMode(mode);
    setHubSlideOverOpen(true);
  }, []);

  // ---------------------------------------------------------------------
  // Conversational /draft flow handlers (Spec 29 Phase A).
  // Declared BEFORE ``let body`` because the idle body's TimelineView
  // consumes the computed ``draftFlowCard`` prop directly — TS's
  // temporal-dead-zone check enforces the ordering.
  // ---------------------------------------------------------------------
  // Phase 7.2 — orchestrator: try to start a draft with whatever is
  // currently selected. Used both by the /draft slash command (the
  // initial trigger) AND by the picker callbacks (after each
  // standalone pick lands during a pending-draft-start orchestration).
  //
  // Branches:
  //   * Both selectedDocCode + selectedLibraryId set → synthesise
  //     the LoomFreshSelection and kick off handleStartDraft.
  //     Clears pendingDraftStart on success.
  //   * Only one is set → leaves pendingDraftStart true; opens the
  //     picker for the missing piece. The orchestration card in
  //     chat narrates which piece is still needed.
  //   * Neither → opens the doctype picker first (the canonical
  //     starting point for /draft).
  // biome-ignore lint/correctness/useExhaustiveDependencies: openSlideOver intentionally omitted
  const tryAdvanceDraft = useCallback((): void => {
    const lib = libraries.find((l) => l.id === selectedLibraryId);
    const choice = lookupDoctypeChoice(selectedDocCode);
    if (choice && lib) {
      // B_docMeta — start the draft. Defensive: lookupDoctypeChoice
      // returns null when the catalog doesn't recognise the code,
      // so we proceed only when both lookups succeeded.
      const selection = buildLoomFreshSelection(
        {
          docCode: choice.docCode,
          backendDocType: choice.backendDocType,
          docFramework: choice.docFramework,
          docName: choice.docName,
          region: selectedRegionRef.current,
        },
        { id: lib.id, name: lib.name },
        selectedWorkspaceId,
      );
      selection.folderId = pendingFolderIdRef.current;
      setPendingDraftStart(false);
      setDoctypePickerOpen(false);
      launchDraftRef.current?.(selection);
      return;
    }
    if (!choice) {
      // Doctype missing → open DoctypePicker. The orchestration
      // card already shows "What doctype?" via the pendingDraftStart
      // branch in draftFlowCard.
      setDoctypePickerOpen(true);
      return;
    }
    // choice is set but library is missing → new document step 3.
    setLibraryStepOpen(true);
  }, [selectedDocCode, selectedLibraryId, libraries, selectedWorkspaceId, handleStartDraft]);

  // Spec: Blank-Document Onboarding — checklist callbacks (idle state only).
  // These route through the existing orchestrator, keeping picks separate
  // from the auto-advance trigger (pendingDraftStart).
  // biome-ignore lint/correctness/useExhaustiveDependencies: openSlideOver is stable; selectedLibraryId intentionally unobserved
  const handleChecklistPickDoctype = useCallback(() => {
    setDoctypePickerOpen(true);
    if (!selectedLibraryId) {
      openSlideOver("library-list");
    }
  }, []);

  const handleChecklistPickLibrary = useCallback(() => {
    const mode = selectedLibraryId ? "list" : "library-list";
    openSlideOver(mode);
  }, [selectedLibraryId, openSlideOver]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: setPendingDraftStart is stable
  const handleChecklistReviewAndStart = useCallback(() => {
    setPendingDraftStart(true);
    tryAdvanceDraft();
  }, [tryAdvanceDraft]);

  // The chat setup card's "Next" button: pick a library, or start drafting.
  const handleSetupNextStep = useCallback(
    (step: SetupCommand) => {
      if (step === "library") handleChecklistPickLibrary();
      else handleChecklistReviewAndStart();
    },
    [handleChecklistPickLibrary, handleChecklistReviewAndStart],
  );

  // "Ask about this document" button from ContextCard — focus the composer
  // for conversational chat.
  const handleAskAboutDocument = useCallback(() => {
    commandRailRef.current?.prefillInput("");
  }, []);

  // "Start a fresh draft" — reset picks and launch the guided draft flow.
  // Shared by the HomeScene "＋ New document" CTA and the has-content
  // context card. pendingDraftStart=true arms the orchestration so the
  // picks auto-chain: doctype → library slide-over → section picker →
  // (plan) → run → spine. Without it the flow dead-ends after the doctype
  // pick (handleDoctypePick's advance block is gated on pendingDraftStart).
  // biome-ignore lint/correctness/useExhaustiveDependencies: setPendingDraftStart is stable
  // The folder the writer picked in the "New document" dialog, held across the
  // doctype → library → sections orchestration and injected into the selection
  // that starts the run (useDraftFlow files the doc into it once it exists).
  // Reset each time the flow (re)starts so a stale pick can't leak into it.
  const pendingFolderIdRef = useRef<string | null>(null);

  // Foreign/existing doc: mint a fresh AsthrDocumentId BEFORE any startRun so the
  // backend sees a document with no prior runs and skips the tenancy guard
  // (server/routes/pipeline.py:1847) that otherwise 404s "No document '…'". No-op
  // unless the detector flagged this doc as foreign (foreignDocDetectedRef).
  // Rotation also strips the stale AsthrActiveRunId (wordWriter.ts). The cache-move
  // triad mirrors handleConfirmRotateProject (flush, then move BOTH _cachedDocId and
  // documentSessionId together); NO shell reset here — we're mid doctype→library
  // orchestration and must keep it intact.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setters/refs/module fns are stable
  const rotateForeignDocIfNeeded = useCallback(async () => {
    if (!foreignDocDetectedRef.current) return;
    foreignDocDetectedRef.current = false;
    await performForeignDocRotation({
      rotate: rotateDocumentId,
      flush: flushAllPendingSaves,
      setCachedDocId,
      setDocumentSessionId,
      onError: (err) => console.warn("[VariantCShell] rotateForeignDocIfNeeded failed:", err),
    });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: setPendingDraftStart is stable
  const handleContextCardStartFreshDraft = useCallback(
    async (folderId?: string | null) => {
      await rotateForeignDocIfNeeded();
      pendingFolderIdRef.current = folderId ?? null;
      customImportActiveRef.current = false;
      setSelectedDocCode(null);
      setSelectedRegion(null);
      setRunRegion(null);
      setSelectedLibraryId("");
      setPendingDraftStart(true);
      setDoctypePickerOpen(true);
    },
    [rotateForeignDocIfNeeded],
  );

  // Set true while the writer is in the "build on this document" orchestration
  // (doctype → library) so the terminal step registers a CUSTOM run from the
  // doc's existing headings instead of launching a fresh drafting run.
  const customImportActiveRef = useRef(false);

  // Enter the custom-submission orchestration: reuse the fresh-draft doctype →
  // library flow, but flag it so it ends in ``startCustomFlow`` (see the two
  // orchestration seams below).
  // biome-ignore lint/correctness/useExhaustiveDependencies: setters are stable
  const handleStartCustomImport = useCallback(async () => {
    await rotateForeignDocIfNeeded();
    customImportActiveRef.current = true;
    setSelectedDocCode(null);
    setSelectedRegion(null);
    setRunRegion(null);
    setSelectedLibraryId("");
    setPendingDraftStart(true);
    setDoctypePickerOpen(true);
  }, [rotateForeignDocIfNeeded]);

  // Terminal step of the custom-import orchestration: register the custom run
  // from the live document's outline, then load it through the normal snapshot
  // hydration (which renders the spine and, for custom runs, never re-projects
  // the body — see useRunControl's submission_mode guard).
  const startCustomFlow = useCallback(
    async (selection: LoomFreshSelection) => {
      customImportActiveRef.current = false;
      // Light up the floating "Build on this document" progress card. Steps:
      // 0 outline → 1 create project → 2 build spine → 3 load sections. The two
      // internal phases (outline read, run registration) fire through onStep.
      setCustomImportError(null);
      setCustomImportDone(false);
      setCustomImportStep(0);
      const failCustomImport = (message: string, step: number) => {
        setCustomImportStep(step);
        setCustomImportError(message);
      };
      const result = await startCustomSubmission(selection, (phase) => {
        setCustomImportStep(phase === "outline" ? 1 : 2);
      });
      if (!result) {
        // startCustomSubmission surfaces the specific reason in the startError
        // banner; reflect a generic failure on the card so the writer isn't
        // left with a silent spinner.
        failCustomImport("Couldn't build on this document.", 0);
        return;
      }
      const { runId, outline } = result;
      // Stamp the Word doc so a reload maps straight back to THIS run. The
      // custom run was created under ``getCachedDocId()``; the import's doc-id
      // rotation can leave the Word ``AsthrDocumentId`` on a sibling (null-run)
      // id, so on reopen bootstrap reads that id, finds no run, and re-offers
      // "Build on this document" — orphaning the run. Force the Word id back to
      // the run's own doc_id and stamp the run id (recovery + the import
      // detector's fallback probe). Both fail-soft: a stamp failure at worst
      // reproduces the prior reopen behaviour, never blocks the flow.
      const runDocId = getCachedDocId();
      if (runDocId) {
        await saveDocumentId(runDocId).catch((err) =>
          console.warn("[VariantCShell] startCustomFlow: saveDocumentId failed (non-fatal):", err),
        );
      }
      await setActiveRunId(runId).catch((err) =>
        console.warn("[VariantCShell] startCustomFlow: setActiveRunId failed (non-fatal):", err),
      );
      // The spine IS the writer's existing document — render it straight from
      // the outline we just read, so it never depends on the server snapshot
      // round-trip (which, if it doesn't come back tagged custom, would leave
      // handleRunLoaded on the 96-module doctype template skeleton).
      const customSpineFallback = spineSectionsFromOutline(outline);
      // A custom run is registered as COMPLETED with no drafting worker — the
      // spine is the document's existing content, not something being drafted.
      // Mark it terminal + latest-only up front so the SSE hook never subscribes
      // in "history" mode and paints the live-run "Starting the drafting
      // engine…" booting card while handleRunLoaded hydrates.
      setTerminalRunStatus(true);
      setRunSubscriptionStartMode("latest");
      setCustomImportStep(2); // building the spine
      let config = docConfig;
      if (!config || config.document_type !== selection.backendDocType) {
        try {
          config = await getDocTypeConfig(selection.backendDocType);
        } catch (err) {
          console.warn("[VariantCShell] startCustomFlow: docConfig fetch failed:", err);
          failCustomImport("Couldn't load the document type configuration.", 2);
          return;
        }
      }
      setCustomImportStep(3); // loading sections
      await handleRunLoaded(runId, config, {
        libraryId: selection.libraryId,
        libraryName: selection.libraryName,
        workspaceId: selection.workspaceId ?? null,
        docType: selection.backendDocType,
        // Custom import: an empty ``libraryId`` here means the writer skipped
        // the source library. Tag the mode so handleRunLoaded unsets the
        // library instead of restoring a stale one from the doc's saved props.
        submissionMode: "custom",
        // Authoritative client-side spine — used when the server snapshot
        // doesn't come back tagged custom (see handleRunLoaded).
        customSpineFallback,
      });
      // Success frame, then dismiss (the spine has taken over the pane).
      setCustomImportDone(true);
      setCustomImportStep(CUSTOM_IMPORT_STEP_DEFS.length);
      window.setTimeout(() => {
        setCustomImportStep(-1);
        setCustomImportDone(false);
      }, 900);
    },
    // handleRunLoaded / docConfig / startError read at call time; setters stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startCustomSubmission],
  );

  // Phase 9.5 (Bundle A) — shared emitter for ``library_selected``
  // events. Three callsites in the LibraryHub JSX (dropdown change,
  // explicit "Use library", post-create) each emit the same shape;
  // extracted at the rule-of-three point to keep the chat-event
  // semantics in one place. ``created`` flips the chat verb only
  // (Created vs Selected); same payload otherwise.
  const emitLibrarySelected = useCallback(
    (library: { id: string; name: string }, created: boolean) => {
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "you",
          sectionId: null,
          runId: null,
          payload: {
            kind: "library_selected",
            libraryId: library.id,
            libraryName: library.name,
            created,
          },
        },
      });
    },
    [],
  );

  // Doctype-pick handler. Shared by:
  //   * Standalone DoctypePicker.onSelect (chip click) →
  //     pendingDraftStart=false → just save the pick.
  //   * Mid-/draft DoctypePicker.onSelect → pendingDraftStart=true
  //     → save then advance the orchestration.
  // biome-ignore lint/correctness/useExhaustiveDependencies: openSlideOver intentionally omitted
  const handleDoctypePick = useCallback(
    (choice: DoctypeChoice) => {
      // Region-only pick for a held launch (see launchDraft's region guard):
      // relaunch it with the region, unless the writer went Back and chose a
      // different doctype — then it is an ordinary pick.
      const held = regionRelaunchRef.current;
      regionRelaunchRef.current = null;
      setRegionStepFor(null);
      setSelectedRegion(choice.region ?? null);
      setRunRegion(null);
      if (held && choice.region && held.docCode === choice.docCode) {
        setDoctypePickerOpen(false);
        launchDraftRef.current?.({ ...held, region: choice.region });
        return;
      }
      // Phase 9.5 (Bundle A) — surface the pick in chat as the
      // {writer-said, asthra-acked} tuple before any state mutation
      // so the chat history records the action even if the
      // orchestration short-circuits below. Emitted here (not in the
      // picker) so the picker stays a pure presentation surface.
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "you",
          sectionId: null,
          runId: null,
          payload: {
            kind: "doctype_selected",
            docCode: choice.docCode,
            label: choice.docName,
          },
        },
      });
      // Changing the doctype to a DIFFERENT value invalidates any
      // library already chosen — libraries are scoped to a doctype, so
      // the writer must re-pick one for the new type. First-time
      // selection (no prior doctype) leaves any earlier library pick
      // intact (the /draft library-first path relies on that).
      const doctypeChanged = !!selectedDocCode && selectedDocCode !== choice.docCode;
      if (doctypeChanged) {
        setSelectedLibraryId("");
      }
      setSelectedDocCode(choice.docCode);
      setDoctypePickerOpen(false);
      if (pendingDraftStart) {
        // The setSelectedDocCode hasn't propagated yet — pass the
        // choice forward by calling tryAdvanceDraft on the next tick
        // would be racy. Instead, inline the same decision here using
        // the just-picked choice (no React state read needed). When the
        // doctype just changed, the prior library no longer applies —
        // skip the auto-start and route to the library picker instead.
        const lib = doctypeChanged ? undefined : libraries.find((l) => l.id === selectedLibraryId);
        if (lib) {
          const selection = buildLoomFreshSelection(
            {
              docCode: choice.docCode,
              backendDocType: choice.backendDocType,
              docFramework: choice.docFramework,
              docName: choice.docName,
              region: choice.region,
            },
            { id: lib.id, name: lib.name },
            selectedWorkspaceId,
          );
          selection.folderId = pendingFolderIdRef.current;
          setPendingDraftStart(false);
          launchDraftRef.current?.(selection);
          return;
        }
        // No library yet — new document step 3 (sources).
        setLibraryStepOpen(true);
      }
    },
    [
      pendingDraftStart,
      libraries,
      selectedLibraryId,
      selectedDocCode,
      selectedWorkspaceId,
      handleStartDraft,
    ],
  );

  // Library-pick handler used by the LibraryHub when a writer is
  // mid-/draft. Mirror image of the doctype branch in
  // handleDoctypePick: save the pick, and when pendingDraftStart is
  // true, either start the draft (if doctype already set) or open
  // the doctype picker for the missing piece.
  //
  // Returns true when the handoff was consumed by the orchestrator
  // (caller is mid-/draft); false when there's nothing to advance
  // (standalone library-management mode). Lets Hub callbacks branch.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pendingDraftStart read inline; adding it would cause stale-closure loops in the orchestration pattern
  const advanceDraftWithLibrary = useCallback(
    (library: { id: string; name: string }): boolean => {
      if (!pendingDraftStart) return false;
      setSelectedLibraryId(library.id);
      // No partial save here — persistence flows through the
      // ``activeSelection`` effect once ``handleStartDraft`` lands the
      // full tuple. Saving library alone here was the source of
      // half-populated session records (see Bug A, 2026-05-07).
      const choice = lookupDoctypeChoice(selectedDocCode);
      if (choice) {
        // Writer-readiness gate added 2026-04-28: don't blast into
        // the run if the library hasn't been ratified for THIS
        // doctype yet. The ratified signal today is "the library
        // has a target_doctype set in libraryDoctypeStore" — set
        // either at create-time (LibraryHub create form) or by an
        // explicit click on the LibraryHub's target-doctype pill.
        // Without it, the source-classification vocabulary
        // doesn't narrow correctly and the writer skipped past
        // every confirmation surface (writer report 2026-04-28
        // on CMC: "selected from the dropdown and went straight
        // to the plan — did not wait for doctyping, output
        // doctype, approve").
        //
        // Auto-ratify: if the library has no recorded doctype yet
        // but the chosen docCode is a valid TargetDoctype, write it
        // silently and proceed — no Hub interruption needed. Only
        // fall back to the slide-over when the docCode is outside
        // the known vocabulary (shouldn't happen in normal flows but
        // guards against custom/future codes).
        const selection = buildLoomFreshSelection(
          {
            docCode: choice.docCode,
            backendDocType: choice.backendDocType,
            docFramework: choice.docFramework,
            docName: choice.docName,
            region: selectedRegionRef.current,
          },
          { id: library.id, name: library.name },
          selectedWorkspaceId,
        );
        selection.folderId = pendingFolderIdRef.current;
        setPendingDraftStart(false);
        launchDraftRef.current?.(selection);
        return true;
      }
      // No doctype yet — open DoctypePicker so writer can complete
      // the missing piece.
      setDoctypePickerOpen(true);
      return true;
    },
    [pendingDraftStart, selectedDocCode, selectedWorkspaceId, handleStartDraft],
  );

  // Skip the library step for a build-on-existing-document (custom) run. A
  // source library is optional for a custom submission — the audit library is
  // bootstrapped from the document's own content — so launch the custom flow
  // with an empty library selection. Only reachable while the custom-import
  // orchestration is active (the slide-over renders the skip action only then).
  // biome-ignore lint/correctness/useExhaustiveDependencies: setters are stable
  const handleSkipLibraryForCustom = useCallback(() => {
    const choice = lookupDoctypeChoice(selectedDocCode);
    if (!choice) {
      // No doctype yet — send the writer to pick it first.
      setHubSlideOverOpen(false);
      setDoctypePickerOpen(true);
      return;
    }
    const selection = buildLoomFreshSelection(
      {
        docCode: choice.docCode,
        backendDocType: choice.backendDocType,
        docFramework: choice.docFramework,
        docName: choice.docName,
        region: selectedRegionRef.current,
      },
      { id: "", name: "" }, // no source library
      selectedWorkspaceId,
    );
    selection.folderId = pendingFolderIdRef.current;
    // Unset the library up front so the chip can't briefly show a stale value
    // between skip and hydration (handleStartCustomImport clears it at flow
    // start; this guards the back-into-picker-then-skip path).
    setSelectedLibraryId("");
    setPendingDraftStart(false);
    setHubSlideOverOpen(false);
    launchDraftRef.current?.(selection);
  }, [selectedDocCode, selectedWorkspaceId]);

  // Terminal draft launcher shared by all three orchestration surfaces
  // (tryAdvanceDraft, handleDoctypePick, advanceDraftWithLibrary). Called
  // once the doctype + library tuple is complete.
  //
  //   * Custom-import flow → startCustomFlow (unchanged).
  //   * A fresh draft while THIS document already holds a run that isn't
  //     actively drafting (post-run / paused) → stash the selection and
  //     open the confirm modal. Confirming saves the current doc, resets
  //     to idle, then re-enters here from idle to show the section picker.
  //     Without this the section step is silently swallowed by the
  //     ``state.state === "idle"`` gate on ``sectionPickerVisible``.
  //   * A run that IS actively drafting (mid-run / approval / pausing)
  //     can't be cleanly saved mid-flight → surface the same "stop first"
  //     guidance the /draft command gives, instead of failing silently.
  //   * Idle → straight into handleStartDraft.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setters are stable
  // Gate a custom "build on your own document" run behind the flat unlock,
  // unless the workspace is free or the document is already unlocked. Fails
  // toward the paywall when billing is required and coverage cannot be confirmed
  // — the server gate agrees, so proceeding would only 402 later.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setCustomUnlock is stable
  const maybeGateCustom = useCallback(
    async (selection: LoomFreshSelection) => {
      const workspaceId = selection.workspaceId;
      if (!workspaceId) {
        void startCustomFlow(selection);
        return;
      }
      let required = false;
      try {
        required = (await getWorkspaceBilling(workspaceId)).billing_required;
      } catch {
        required = false; // a lookup blip never fronts a paywall
      }
      if (!required) {
        void startCustomFlow(selection);
        return;
      }
      const docId = getCachedDocId();
      if (!docId) {
        void startCustomFlow(selection);
        return;
      }
      try {
        if (coverageOf(await getDocumentPayment(docId)).whole) {
          void startCustomFlow(selection);
          return;
        }
      } catch {
        // Billing required and coverage unconfirmable → show the paywall rather
        // than dispatch a build the server would refuse.
      }
      setCustomUnlock({ selection, docId, docType: selection.backendDocType });
    },
    [startCustomFlow],
  );

  const launchDraft = useCallback(
    (selection: LoomFreshSelection) => {
      // The region is required to draft: a launch without one (a doctype
      // saved before regions existed) asks for it, then relaunches.
      if (!selection.region) {
        const choice = lookupDoctypeChoice(selection.docCode);
        if (choice) {
          regionRelaunchRef.current = selection;
          setRegionStepFor(choice);
          setDoctypePickerOpen(true);
          return;
        }
      }
      // Refuse a launch that cannot start before asking for a name it would
      // never use.
      const runBusy = state.state !== "idle" && state.state !== "post-run" && state.state !== "paused";
      if (!customImportActiveRef.current && runBusy) {
        setStartError("A run is in progress. Stop it first, then start a new draft.");
        return;
      }
      // Every new run is named. A template draft is named on the section
      // picker, right above Start; a build-on-your-document run has no picker,
      // so ask here, then relaunch with it.
      if (customImportActiveRef.current && !selection.name?.trim()) {
        setNamePromptFor(selection);
        return;
      }
      if (customImportActiveRef.current) {
        void maybeGateCustom(selection);
        return;
      }
      if (state.state === "post-run" || state.state === "paused") {
        setFreshDraftPending(selection);
        return;
      }
      // An idle shell can still sit on a drafted document (opened without its
      // run restoring, or after Reset left the body). Starting over it would
      // scaffold the new draft into the old content — ask to save & clear the
      // same way a post-run document does.
      void (async () => {
        const { hasContent } = await queueWordRead("launchDraft:hasContent", checkDocHasContent).catch(() => ({
          hasContent: null,
        }));
        if (hasContent === true && starterDocRef.current) {
          // Starter document: none of this text is the writer's work and
          // there is no run to save, so clear it and start straight away.
          starterDocRef.current = false;
          setStarterDoc(false);
          emptyDocWatcherRef.current?.acknowledge();
          try {
            await enqueueWordWrite(() => clearDocument());
          } catch (err) {
            console.warn("[VariantCShell] clearing the starter document failed:", err);
          }
          emptyDocWatcherRef.current?.acknowledge();
        } else if (hasContent === true) {
          setFreshDraftPending(selection);
          return;
        }
        // Fresh (template) draft — count progress against the doctype's module
        // total, never a stale "custom" mode left over from a prior build-on run.
        setRunSubmissionMode("standard");
        void handleStartDraft(selection);
      })();
    },
    [state.state, maybeGateCustom, handleStartDraft, enqueueWordWrite],
  );

  // Writer confirmed "Save & start fresh" — persist the current document
  // (the Save-button sequence via autoSaveRef), clear the Word body and its
  // run binding, rotate to a fresh project id, reset the run to idle, then
  // launch the stashed selection. Ordering is load-bearing: save reads
  // state.runId (still set) and the old doc id, then the clear + rotation
  // detach the document from the old project, then RESET_TO_IDLE clears the
  // run, then handleStartDraft re-fetches config and flips
  // sectionPickerActive — by which point the reducer has committed idle so
  // the picker renders.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setters are stable
  const handleConfirmFreshDraft = useCallback(async () => {
    const selection = freshDraftPending;
    if (!selection) return;
    setFreshDraftSaving(true);
    try {
      await autoSaveRef.current?.();
    } catch (err) {
      // handleSave surfaces its own saveError banner; proceed so the
      // writer isn't stranded on the modal.
      console.warn("[VariantCShell] save before fresh draft failed:", err);
    }
    // The clear below empties the body on purpose — keep the empty-doc
    // watcher from reading it as "writer deleted everything" and opening the
    // rotate-project prompt over the picker.
    emptyDocWatcherRef.current?.acknowledge();
    try {
      await enqueueWordWrite(() => clearDocument());
      // Drop the old run binding so a refresh can't restore the saved run
      // into the new project's (blank) document.
      await enqueueWordWrite(() => clearWordHydrationMarker());
      await enqueueWordWrite(() => clearActiveRunId());
    } catch (err) {
      console.warn("[VariantCShell] clearing the document before a fresh draft failed:", err);
    }
    emptyDocWatcherRef.current?.acknowledge();
    let newId = "";
    try {
      newId = await rotateDocumentId();
    } catch (err) {
      console.warn("[VariantCShell] rotateDocumentId before fresh draft failed:", err);
    }
    resetShellToIdle();
    if (newId) {
      // Same doc-id move as handleConfirmRotateProject: a fresh project
      // starts with an empty chat, and both id stores move together after
      // flushing saves still queued under the old id.
      setPersistedChatMessages([]);
      setPersistedChatHasMore(false);
      setRestoredConversationId(undefined);
      setKnownHadContent(false);
      flushAllPendingSaves();
      setCachedDocId(newId);
      setDocumentSessionId(newId);
    }
    setFreshDraftSaving(false);
    setFreshDraftPending(null);
    setRunSubmissionMode("standard");
    void handleStartDraft(selection);
  }, [freshDraftPending, resetShellToIdle, handleStartDraft]);

  // Wire launchDraft through the ref the orchestration callbacks call — they
  // are declared above launchDraft, so the ref avoids a TDZ in their deps.
  useEffect(() => {
    launchDraftRef.current = launchDraft;
    return () => {
      launchDraftRef.current = null;
    };
  }, [launchDraft]);

  // The custom paywall's checkout is run-less; its ``onUnlocked`` (wired far
  // above, before ``startCustomFlow`` exists) dispatches the build through this
  // ref once the payment clears.
  useEffect(() => {
    startCustomFlowRef.current = (selection) => void startCustomFlow(selection);
    return () => {
      startCustomFlowRef.current = null;
    };
  }, [startCustomFlow]);

  // is in flight (pendingDraftStart === true). Tells the writer
  // what's still missing. The actual selection happens in the
  // canonical surfaces (DoctypePicker, LibraryHub) which are
  // opened directly by the orchestrator (no effects). When both
  // doctype + library are set this card is null — the orchestrator
  // synthesises the LoomFreshSelection and starts the run.
  const draftFlowCard = useMemo(() => {
    if (!pendingDraftStart) return null;
    const lib = libraries.find((l) => l.id === selectedLibraryId);
    const haveDoctype = !!selectedDocCode;
    const haveLibrary = !!lib;
    if (!haveDoctype && !haveLibrary) {
      return <AsthraBubbleSurface label="Drafting agent">Let&apos;s pick a doctype first.</AsthraBubbleSurface>;
    }
    if (!haveDoctype) {
      return <AsthraBubbleSurface label="Drafting agent">{`We have ${lib?.name}. What doctype?`}</AsthraBubbleSurface>;
    }
    if (!haveLibrary) {
      return (
        <AsthraBubbleSurface label="Drafting agent">
          {`${selectedDocCode} it is. Pick a library and we'll start.`}
        </AsthraBubbleSurface>
      );
    }
    // Both set — transient state while handleStartDraft fires.
    return null;
  }, [pendingDraftStart, selectedDocCode, selectedLibraryId, libraries]);

  // Drafts tab — run-agnostic. Show whenever there's a scope the
  // server can query (doc / workspace / library). Hoisted above the
  // ``body`` composition because the "drafts" branch needs the
  // scope vars; also used below to gate the TabBar's third tab.
  const draftsDocId = getCachedDocId();
  // Workspace scope follows the picker (selectedWorkspaceId), not
  // activeSelection — the latter only updates on run load / draft start,
  // so it goes stale when the user switches workspace mid-session.
  const draftsWorkspaceId = selectedWorkspaceId ?? activeSelection?.workspaceId ?? null;
  const draftsLibraryId = activeSelection?.libraryId ?? selectedLibraryId ?? null;
  // The ONE review queue: the Issues band, each row's inline change and the Fix
  // window all read and decide from it, so they can never disagree.
  const reviewFlow = useProposalReviewFlow({
    runId: state.runId,
    refreshSignal: proposalsRefreshSignal,
    libraryId: draftsLibraryId,
    writeTitleFields: writeResolvedTitleFields,
    // Custom sections sit under the writer's own headings, which this
    // number-keyed re-render can't find — those keep the "Load it" message.
    reloadSectionInWord:
      runSubmissionMode === "custom"
        ? undefined
        : (moduleId: string) => reprojectSectionIntoLiveDoc(state.runId, moduleId),
    onDecided: () => {
      setIssuesRefreshSignal((n) => n + 1);
      setActionsRefreshSignal((n) => n + 1);
    },
  });
  // Which workspace's runs the Home / Drafts list shows. ``null`` = "All
  // workspaces" (default) so the writer sees every run they own, regardless of
  // which workspace is bound for drafting. Kept separate from
  // ``selectedWorkspaceId`` (drafting/new-doc scope) so browsing runs never
  // changes where a new draft gets created.
  const [draftsScopeWorkspaceId, setDraftsScopeWorkspaceId] = useState<string | null>(null);
  const showDraftsTab = Boolean(draftsDocId || draftsWorkspaceId || draftsLibraryId);
  // Images tab — run-agnostic, like Drafts. Figures are listed per library, so
  // it shows whenever a library is bound (the same id the pane lists from).
  const imagesLibraryId = draftsLibraryId;
  const showImagesTab = Boolean(imagesLibraryId);
  const imagesLibraryName = imagesLibraryId ? (libraries.find((l) => l.id === imagesLibraryId)?.name ?? null) : null;

  // After a Drafts-panel "Load" wipes + reprojects a run into the live
  // doc, sync the side-panel state with the loaded run: reset to idle so
  // RUN_RESTORED is a legal transition, refetch the spine, refetch
  // document chat history, then dispatch RUN_RESTORED so the spine
  // view reflects the run now in the body. The chat refetch is what
  // makes the conversational context of the loaded run reappear — the
  // hook re-seeds when ``persistedChatMessages`` changes and the new
  // ``state.runId`` arrives via RUN_RESTORED.
  const handleRunLoaded = useCallback(
    async (loadedRunId: string, config: DocTypeConfig, context?: RunLoadContext) => {
      const docId = draftsDocId;
      // Rehydrate the shell's doc-type config from the run that was just
      // loaded — Load works after Reset (when docConfig is null), and the
      // spine/inspector/further-drafting all read from docConfig.
      setDocConfig(config);
      // The leaf the LOADED document is. A Load rebinds this Word document to
      // the loaded run's document, so its leaf replaces the one held before the
      // Load. `undefined` = the caller did not look it up (a reload of the
      // current document) — keep the leaf already in state.
      const loadedLeafId = context?.leafDocumentId !== undefined ? context.leafDocumentId : leafDocumentId;
      if (context?.leafDocumentId !== undefined) setLeafDocumentId(context.leafDocumentId);

      // Re-arm live projection for the loaded run. Setting docConfig alone was
      // not enough: liveProjectionRef defaults to false and is zeroed on reset,
      // and a Load does not pass through actuallyStartRun or the useRunControl
      // restore, which are the only other places it is set. A regen issued
      // after a Load therefore short-circuited in useWordWriteQueue and never
      // reached the Word body. The document this Load just rebuilt is exactly
      // the one a re-projection should target, so the answer here is the same
      // predicate the other two seams use.
      liveProjectionRef.current = shouldLiveProject(config, context?.submissionMode);

      // A Drafts "Load" can replace the current document with a different
      // run's .docx. Restore selection context so library + doctype chips
      // reflect the loaded run immediately — preferring the run row's own
      // context (passed by DraftsPanel) over the Word custom-prop
      // round-trip, whose writes are best-effort and can fail on Word web
      // right after a whole-body replace.
      // Custom import that skipped the source library: unset the library
      // explicitly rather than falling through to loadDocSelectionProps(),
      // which would resurrect a stale library from the Word custom prop (a
      // prior run on the same doc). Keep the doctype so its chip resolves; the
      // activeSelection persist effect then writes library_id="" to the session
      // doc + Word props, so a later reload/bootstrap finds no library.
      if (context?.submissionMode === "custom" && !isValidLibraryId(context?.libraryId) && context?.docType) {
        const docCode = context.docType;
        setActiveSelection(
          buildCustomSkipSelection({
            docCode,
            docName: config.display_name ?? lookupDoctypeChoice(docCode)?.docName ?? docCode,
            docFramework: lookupDoctypeChoice(docCode)?.docFramework,
            workspaceId: context.workspaceId ?? null,
          }),
        );
        setSelectedLibraryId("");
        setSelectedDocCode(docCode);
      } else
        try {
          let libraryId: string | null = null;
          let libraryName: string | null = null;
          let workspaceId: string | null = null;
          let docCode: string | null = null;
          let docName: string | null = null;
          let backendDocType: string | null = null;
          if (isValidLibraryId(context?.libraryId) && context?.docType) {
            libraryId = context.libraryId;
            libraryName = context.libraryName || context.libraryId;
            workspaceId = context.workspaceId ?? null;
            docCode = context.docType;
            backendDocType = context.docType;
            docName = config.display_name || null;
          } else {
            const saved = await loadDocSelectionProps();
            if (isValidLibraryId(saved?.libraryId) && saved?.libraryName && saved.docCode && saved.backendDocType) {
              libraryId = saved.libraryId;
              libraryName = saved.libraryName;
              workspaceId = saved.workspaceId ?? null;
              docCode = saved.docCode;
              backendDocType = saved.backendDocType;
              docName = saved.docName ?? null;
            }
          }
          if (libraryId && libraryName && docCode && backendDocType) {
            const resolvedDocName = docName ?? lookupDoctypeChoice(docCode)?.docName ?? docCode;
            const resolvedDocFramework = lookupDoctypeChoice(docCode)?.docFramework;
            const normalizedDocFramework = resolvedDocFramework ?? undefined;

            setActiveSelection({
              workspaceId,
              libraryId,
              libraryName,
              docCode,
              docName: resolvedDocName,
              docFramework: normalizedDocFramework,
              backendDocType,
            });
            // Seed the standalone picker state too. The library chip's value is
            // ``libraries.find(l => l.id === selectedLibraryId)?.name`` and the
            // doctype chip falls back to ``selectedDocCode`` — neither reads
            // ``activeSelection`` directly — so without these two setters the
            // chips stay on their "pick a…" placeholders even after a Load that
            // restored ``activeSelection``. Mirrors useSessionBootstrap.
            setSelectedLibraryId(libraryId);
            setSelectedDocCode(docCode);

            if (docId) {
              scheduleSave(docId, {
                workspace_id: workspaceId,
                library_id: libraryId,
                library_name: libraryName,
                doc_code: docCode,
                doc_name: resolvedDocName,
                doc_framework: resolvedDocFramework ?? null,
                backend_doc_type: backendDocType,
              });
            }
          }
        } catch (err) {
          console.warn("[VariantCShell] handleRunLoaded: selection restore failed (non-fatal):", err);
        }
      // Build from a fresh config skeleton, not the current in-memory
      // sections. Drafts Load can target an older run while the shell is
      // still showing a newer run's drafted rows; starting from ``sections``
      // would leak those stale statuses into the loaded run until refresh.
      // Scoped to the loaded document's eCTD leaf, so a leaf document's spine
      // lists only that leaf's sections (unchanged for every other doctype).
      const baseSections = spineSectionsFromConfig(
        (scopedCatalog(config, loadedLeafId) as DocTypeConfig | null) ?? config,
      );

      // Prefer the loaded run's own snapshot as the authority for row data.
      // The doc-level spine endpoint is only a fallback because it can lag or
      // describe a different run for the same document session.
      //
      // The fallback is now requested ONLY when the snapshot fails, where
      // previously both went out in parallel. GET /sessions/{docId}/spine
      // answers 404 for any document without draft-run history — correct
      // server behaviour, but firing it unconditionally meant a 404 on
      // every load even when the snapshot had already answered. The
      // browser logs a failed request to the console whether or not the
      // code handles the rejection, so not making the call is the only
      // way to keep the console clean.
      //
      // Cost: the fallback is sequential rather than parallel now, so the
      // path where the snapshot fails is one round-trip slower. That path
      // is the exception, and it was already the slow one.
      let nextSections = baseSections;
      const snapshot = await getRunSnapshot(loadedRunId).catch((err) => {
        console.warn("[VariantCShell] handleRunLoaded: getRunSnapshot failed (non-fatal):", err);
        return null;
      });
      if (snapshot?.region) adoptRunRegion(snapshot.region);
      if (snapshot && snapshot.submission_mode === "custom") {
        // Custom submission: the spine IS the existing document's heading
        // outline, in document order — bypass the template-config merge and
        // number-sort (mapSpineDTO) and take the server rows verbatim. Server
        // rows are preferred over the client fallback because they carry the
        // persisted template match (matchedModuleId/Title/matchResolved).
        nextSections = mapCustomSpineDTO(snapshot);
      } else if (context?.submissionMode === "custom" && context.customSpineFallback?.length) {
        // Custom submission whose server snapshot didn't come back tagged
        // custom (null snapshot / cold start / dropped field / deploy lag).
        // Render the spine from the client-side outline the import already read
        // — green where the heading has content — instead of silently falling
        // back to the 96-module doctype template skeleton below. Placed BEFORE
        // the generic snapshot branch so reconcileSpineWithRunStatus (which is
        // keyed to template module ids) never runs against CUSTOM-N rows.
        nextSections = context.customSpineFallback;
      } else if (snapshot) {
        nextSections = mapSpineDTO(
          {
            doc_id: snapshot.doc_id,
            run_id: snapshot.run_id,
            run_status: snapshot.status,
            spine: snapshot.spine,
          },
          nextSections,
        );
        // Reconcile against the authoritative run status so sections in
        // completed_module_ids are always shown as drafted, even when the
        // spine DTO snapshot lags behind the backend.
        nextSections = reconcileSpineWithRunStatus(nextSections, snapshot);
        nextSections = withReadyTimes(nextSections, snapshot.events);
      } else if (docId) {
        const spine = await fetchSpine(docId).catch((err) => {
          console.warn("[VariantCShell] handleRunLoaded: fetchSpine fallback failed (non-fatal):", err);
          return null;
        });
        if (spine) {
          nextSections = mapSpineDTO(spine, nextSections);
        }
      }
      // Record the run's submission mode so the progress denominator counts
      // against the right total: a custom run against its own heading count
      // (submissionTotalSections → null → falls back to sections.length), a
      // template run against the doctype's module total.
      const isCustomRun = snapshot?.submission_mode === "custom" || context?.submissionMode === "custom";
      setRunSubmissionMode(isCustomRun ? "custom" : "standard");
      hydrateSections(nextSections);
      if (docId) {
        try {
          const page =
            (await fetchRunChat(loadedRunId, { limit: CHAT_PAGE_SIZE })) ??
            (await fetchDocumentChat(docId, { limit: CHAT_PAGE_SIZE }));
          setPersistedChatMessages(page?.messages ?? []);
          setPersistedChatHasMore(page?.hasMore ?? false);
        } catch (err) {
          console.warn("[VariantCShell] handleRunLoaded: chat fetch failed (non-fatal):", err);
        }
      }
      // Sync the auto-restore dedupe so the run-restore effect in
      // useRunControl doesn't re-pick this same run id after
      // hydrateSections changes sections.length and re-fires it. Without
      // this, the effect can trigger a second loadRunIntoLiveDoc round —
      // another body-clear window in which the empty-doc watcher can pop
      // the rotate-project modal and (if confirmed) null docConfig,
      // leaving the Drafts panel Load buttons permanently disabled.
      // markRunRestored also clears the in-memory dismissal for this run;
      // clear the persisted marker too so a deliberate Load-then-reload of
      // a previously-reset run isn't re-suppressed.
      markRunRestored(loadedRunId);
      // The body now holds the loaded run — re-classify the document so
      // the mount-time "empty" verdict (a brand-new Word doc) can't route
      // a later idle transition to the full-screen Home scene. Deferred
      // until the live-doc settle window closes (spine "Load doc" brackets
      // beginLiveDocLoad across this call); runs immediately otherwise.
      deferUntilLiveDocSettled("document-context-refresh", () => {
        setDocumentContextNonce((n) => n + 1);
      });
      // A loaded run never replays its run_metadata event, so take its
      // (trial, session) from the snapshot — the citations lookup ("View
      // citations") keys on it. useRunControl persists it to the session.
      const loadedTrial = snapshot?.trial || snapshot?.project_name;
      if (loadedTrial && snapshot?.session_id) {
        adoptIdentity({ trial: loadedTrial, session: snapshot.session_id });
      }
      if (docId) {
        // Persist the loaded run id so bootstrap restore chooses this run
        // on refresh instead of a stale previously-saved run_id.
        scheduleSave(docId, {
          run_id: loadedRunId,
          dismissed_run_id: null,
        });
      }
      if (state.state !== "idle") {
        dispatch({ type: "RESET_TO_IDLE" });
      }
      dispatch({ type: "RUN_RESTORED", runId: loadedRunId });
      // Scope the spine to the run's own sections (snapshot rows = scope).
      if (snapshot && snapshot.submission_mode !== "custom" && snapshot.spine.length > 0) {
        dispatch({ type: "RUN_SCOPE_SET", runId: loadedRunId, moduleIds: snapshot.spine.map((r) => r.id) });
      }
      // Transition to the correct post-load state based on the run's actual
      // status. Without this, the shell stays stuck in "mid-run" indefinitely
      // for completed runs and the SSE subscription fires against an evicted
      // Redis stream.
      if (snapshot) {
        const runStatus = snapshot;
        const toPostRun = restoresToPostRun(runStatus);
        const toIdle = restoresToIdle(runStatus);
        if (toPostRun || toIdle) {
          // Prevent the SSE hook from subscribing — the Redis stream for a
          // terminal run is likely evicted and a subscribe attempt 404s.
          // ``latest`` too, so a later re-subscribe on this run can't seed the
          // fresh-run "Starting the drafting engine…" card.
          setTerminalRunStatus(true);
          setRunSubscriptionStartMode("latest");
        }
        if (toPostRun) {
          // Completed — OR failed/cancelled with at least one drafted
          // section (same rule as the bootstrap restore in useRunControl;
          // see restoresToPostRun). The old "any terminated run → idle"
          // branch here wiped runId after a successful Load, and on a
          // document that was blank at mount the idle shell renders the
          // full-screen Home scene — the body was full but the pane went
          // Home instead of the spine.
          //
          // Tally from the section list hydrateSections just received —
          // the same list the spine footer and tab chip count — instead
          // of the run-scoped, drift-prone modules_total/modules_completed
          // scalars that made the rail say "Done. 7/7" against a "2/96"
          // footer after loading a run from the Drafts tab.
          dispatch({
            type: "RUN_COMPLETED",
            summary: deriveRunSummaryFromSpine(nextSections, runStatus.elapsed_seconds),
          });
          if (runStatus.status === "failed") {
            dispatch({ type: "RUN_FAILED", error: RUN_FAILED_AFTER_DRAFTING_ERROR });
          }
        } else if (toIdle) {
          // Terminated with nothing drafted — there is no run surface to
          // show, so idle (and Home on a blank doc) is the honest state.
          dispatch({ type: "RESET_TO_IDLE" });
        } else if (runStatus.status === "paused") {
          // Paused — land on Resume straight from the snapshot, as the
          // bootstrap restore does (useRunControl). Left in "mid-run", the
          // SSE hook subscribed in "history" mode and replayed the whole
          // stream through the live handlers: rows flashed "running · 0s",
          // DRAFT NOW/PAUSE showed, and only the replayed `paused` event
          // settled it. RUN_PAUSED makes the subscribe effect skip; Resume
          // re-subscribes from "latest" itself.
          setRunSubscriptionStartMode("latest");
          dispatch(
            runStatus.failure_notice
              ? { type: "RUN_PAUSED", notice: runStatus.failure_notice }
              : { type: "RUN_PAUSED" },
          );
        }
      } else if (context?.submissionMode === "custom") {
        // Custom run whose snapshot was unavailable (null / cold start): the
        // spine still rendered from the client fallback above, and the run was
        // registered COMPLETED with no worker. Surface the completed post-run
        // state — tallied from that fallback spine — so the "Run complete ·
        // N/M drafted" banner and status rail agree with the green spine
        // instead of being silently skipped by the ``if (snapshot)`` gate.
        setTerminalRunStatus(true);
        dispatch({
          type: "RUN_COMPLETED",
          summary: deriveRunSummaryFromSpine(nextSections),
        });
      }
      // A Drafts / Home Load just rebuilt this document from a FINISHED run:
      // the live document owns it now, so hand it to the auto-save watcher —
      // the same markSyncReady + "live" pointer the spine "Load doc" sets.
      // Without this, nothing the writer typed in a document opened this way
      // was ever saved, and every Load brought the deleted text back. A run
      // still drafting (Join live) keeps the server as owner.
      if (context?.bodyLoaded && snapshot && restoresToPostRun(snapshot)) {
        try {
          await markSyncReady(loadedRunId);
          await setDraftPointer("live");
          setSyncGateNonce((n) => n + 1);
        } catch (err) {
          console.warn("[VariantCShell] handleRunLoaded: auto-save hand-off failed (non-fatal):", err);
        }
      }
      // Land on the populated spine. A Drafts-tab Load otherwise leaves
      // viewMode on "drafts" (RUN_RESTORED/RUN_COMPLETED never touch it),
      // so the writer sees no sign the spine hydrated. No-op when already
      // on the spine (the spine Load-doc path).
      dispatch({ type: "VIEW_MODE_SET", mode: "spine" });
    },
    [
      draftsDocId,
      hydrateSections,
      state.state,
      setPersistedChatMessages,
      setPersistedChatHasMore,
      markRunRestored,
      leafDocumentId,
      adoptRunRegion,
      adoptIdentity,
    ],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: insertionModeRef read is intentionally non-reactive
  const handleLoadDraftedDocument = useCallback(async () => {
    console.log(
      `[load-draft] handleLoadDraftedDocument invoked insertionMode=${insertionModeRef.current} runId=${state.runId ?? "none"} docConfigPresent=${docConfig !== null}`,
    );
    // Both insertion modes now rebuild the live doc IN PLACE (fetchRunDocument →
    // hydrateLiveDocFromBase64, per-section loadRunIntoLiveDoc fallback below).
    // Previously section-mode routed to an external browser window instead —
    // that predates the post-run "Load doc" button being exposed for section
    // mode, where an inline re-pull (reconcile the body with the server's
    // canonical version, or restore it after a clear) is what the writer wants.
    // The rebuild only fires from the post-run banner (run complete; the banner
    // is suppressed mid-draft), so it can't clobber a still-streaming body.
    if (!state.runId || !docConfig) {
      console.log(
        `[load-draft] ABORTED missing prerequisite runIdPresent=${!!state.runId} docConfigPresent=${!!docConfig}`,
      );
      setStartError("Couldn't load drafted document into Word. Run id or document config is missing.");
      return;
    }
    // Closures below lose the narrowing on `state.runId`.
    const loadRunId = state.runId;
    // Prevent concurrent invocations (double-click, rapid re-render, or
    // auto-restore re-trigger) from running simultaneous load+sync sequences.
    if (loadDraftInFlightRef.current) {
      console.log("[load-draft] already in flight — skipping");
      return;
    }
    // Formatted EndNote citations live only in this Word document; a Load
    // replaces it. Warn once (the next click proceeds).
    if (!endNoteLoadAckRef.current) {
      const endNoteFields = await enqueueWordWrite(() => countEndNoteFieldsIn()).catch(() => 0);
      if (endNoteFields > 0) {
        endNoteLoadAckRef.current = true;
        setStartError(endNoteOverwriteMessage(endNoteFields, "document"));
        return;
      }
    }
    endNoteLoadAckRef.current = false;
    loadDraftInFlightRef.current = true;
    // A full Load rebuilds every section, so any pending/failed per-section
    // "loading into Word…" trackers are now moot — clear them.
    setLiveDocLoadBySection(() => new Map());
    setLoadDraftInFlight(true);
    // Persist edits still waiting on the auto-save debounce (an approved chat
    // edit seconds ago) BEFORE the Load reads the SSOT — otherwise Load fetches
    // the pre-edit body and a resolved flag comes back. Bounded; never throws.
    // Runs before the load guard below, which would hold the save off.
    await flushPendingAutoSaves();
    // Hold the live-doc "loading/settling" guard across the WHOLE load window —
    // hydrate + handleRunLoaded + _waitForDocumentLoaded — not just the insert.
    // Balanced in the finally after the settle wait. Nests with the loader's own
    // begin/end (ref-counted), so background re-projection stays deferred until
    // the freshly loaded doc has actually settled.
    beginLiveDocLoad();
    // Kick off the floating LoadProgressCard: step 0 (Preparing), clear any
    // stale error/done from a prior attempt.
    setLoadDocStepIndex(0);
    setLoadDocError(null);
    setLoadDocDone(false);
    emptyDocWatcherRef.current?.acknowledge();
    pauseSync();
    const loadStartedAt = Date.now();
    // Flipped true once the draft is in Word AND the success-path steps
    // completed (either the main path or the per-section fallback). Drives the
    // terminal "Loaded ✓" frame in the finally block.
    let loadSucceeded = false;
    // Custom (build-on-existing-document) runs take a dedicated Load path — the
    // whole-file hydrate would insert a doc-type rebuild that can't place the
    // writer's ``CUSTOM-N`` sections (blank doc), and the template per-section
    // fallback would orphan every one of them. Remembered so the catch below
    // never runs the destructive template fallback on a custom run.
    let isCustomRun = false;
    try {
      rememberRunRecoveryHint(state.runId, "shell.load-drafted-document");
      setLoadDocStepIndex(1); // Downloading your draft
      // One snapshot fetch decides the path AND (for custom) supplies the spine
      // (real number/title per CUSTOM-N id). Non-fatal on failure → template path.
      const loadSnapshot = await getRunSnapshot(state.runId).catch((err) => {
        console.warn("[load-draft] getRunSnapshot failed (non-fatal):", err);
        return null;
      });
      isCustomRun = loadSnapshot?.submission_mode === "custom";
      if (isCustomRun) {
        setLoadDocStepIndex(2); // Rebuilding in Word (custom per-section)
        const customSpine = (loadSnapshot?.spine ?? []).map((s) => ({
          id: s.id,
          number: s.number,
          title: s.title,
          isTitle: s.is_title ?? undefined,
        }));
        console.log(`[load-draft] custom submission — loadCustomRunIntoLiveDoc rows=${customSpine.length}`);
        const customResult = await enqueueWordWrite(() => loadCustomRunIntoLiveDoc(loadRunId, customSpine));
        console.log(
          `[load-draft] loadCustomRunIntoLiveDoc done sectionsWritten=${customResult.sectionsWritten}/${customResult.sectionsAttempted}`,
        );
      } else {
        // Prefer the canonical run-scoped .docx, read THROUGH DocService — the
        // document read/write API (Mongo stays the SSOT behind it) — then import
        // whole into Word. Fallback to section replay only when a run document is
        // unavailable.
        console.log(`[load-draft] fetchRunDocument starting runId=${state.runId}`);
        const blob = await fetchRunDocument(state.runId, { includeProvenance: false });
        setLoadDocStepIndex(2); // Rebuilding in Word (whole-file hydrate or per-section fallback)
        if (blob) {
          console.log(
            `[load-draft] fetchRunDocument returned blob sizeBytes=${blob.size} type=${blob.type || "unknown"}`,
          );
          const base64 = await blobToBase64(blob);
          console.log(`[load-draft] blobToBase64 done base64Length=${base64.length}`);
          // Pass the shared style spec so built-in styles (Normal, Heading 1-3)
          // are normalized to the server's before the insert — matching the
          // DraftsPanel Load path, so a spine Load renders with the same styles
          // and title as the download rather than drifting. The skeleton feeds
          // heading-number recovery + the native-numbering rebuild (hydrate
          // STEP3/3.5) when Word web drops the list numbering.
          // Serialised with every other Word write: a section re-projection
          // still rendering while the body is replaced wrote its remaining
          // paragraphs above the title page.
          const hydrateResult = await enqueueWordWrite(() =>
            hydrateLiveDocFromBase64(base64, {
              styleSpec: docConfig.live_doc_style_spec,
              skeleton: docConfig.skeleton,
              headingNumbering: docConfig.heading_numbering,
            }),
          );
          console.log(
            `[load-draft] hydrateLiveDocFromBase64 done sectionsWritten=${hydrateResult.sectionsWritten} bookmarksPlanted=${hydrateResult.bookmarksPlanted} plantedSections=${hydrateResult.plantedSections.join(",") || "none"}`,
          );
        } else {
          console.log(
            `[load-draft] fetchRunDocument returned null (404 — no bundle yet) — falling back to loadRunIntoLiveDoc runId=${state.runId}`,
          );
          const fallbackResult = await enqueueWordWrite(() => loadRunIntoLiveDoc(loadRunId, docConfig));
          console.log(
            `[load-draft] loadRunIntoLiveDoc done sectionsWritten=${fallbackResult.sectionsWritten} scaffolded=${fallbackResult.scaffolded}`,
          );
        }
      }
      setLoadDocStepIndex(3); // Restoring session (spine + state hydration)
      await handleRunLoaded(state.runId, fullDocConfig ?? docConfig);
      console.log(`[load-draft] handleRunLoaded done elapsedMs=${Date.now() - loadStartedAt}`);
      setLoadDocStepIndex(4); // Loading the spine (sync-engine startup)
      // The draft is now in Word for this run — mark the document ready so sync is
      // allowed (and stays allowed across reopens), then start the engine. It's
      // still paused (line ~2210) so no cycle fires against the settling body; the
      // finally block's resumeSync(runId) below unpauses + rebaselines the draft.
      // Done only on the SUCCESS path: a failed load never enables sync, so the
      // pre-load placeholder is never pushed.
      await markSyncReady(state.runId);
      // The drafted document is now in Word — the live document owns it again.
      // Flip the ownership pointer back to "live" so sync is allowed (and stays
      // allowed across a reopen) after the draft-start handler set it "server".
      await setDraftPointer("live");
      startSyncEngineForCurrentRun();
      loadSucceeded = true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.log(`[load-draft] FAILED elapsedMs=${Date.now() - loadStartedAt} error="${message}"`);
      // A custom run has no template fallback: loadCustomRunIntoLiveDoc is the
      // only path that can place its CUSTOM-N sections, and it already force-
      // cleared. Running loadRunIntoLiveDoc here would scaffold the doc-type
      // skeleton and orphan every section — re-blanking the doc. Surface the
      // error instead (its own guard also aborts before clearing when the SSOT
      // is all-placeholder, protecting the writer's live content).
      if (isCustomRun) {
        console.log(`[load-draft] custom run — no template fallback; surfacing error runId=${state.runId}`);
        setStartError(`Couldn't load drafted document: ${message}`);
        setLoadDocError(`Couldn't load drafted document: ${message}`);
        return;
      }
      // The whole-file hydrate clears the body BEFORE inserting, so a
      // mid-insert failure (Word web degrades across repeated Replace
      // inserts) leaves an EMPTY live doc — the worst outcome. Mirror the
      // DraftsPanel Load path: rebuild per-section from the backend SSOT so
      // the writer still gets content, then complete the success-path steps.
      try {
        console.log(`[load-draft] attempting per-section fallback after whole-file failure runId=${state.runId}`);
        setLoadDocStepIndex(2); // Rebuilding in Word (per-section replay)
        const fallbackResult = await enqueueWordWrite(() => loadRunIntoLiveDoc(loadRunId, docConfig));
        console.log(
          `[load-draft] fallback loadRunIntoLiveDoc done sectionsWritten=${fallbackResult.sectionsWritten} scaffolded=${fallbackResult.scaffolded}`,
        );
        setLoadDocStepIndex(3); // Restoring session
        await handleRunLoaded(state.runId, fullDocConfig ?? docConfig);
        setLoadDocStepIndex(4); // Loading the spine
        await markSyncReady(state.runId);
        await setDraftPointer("live");
        startSyncEngineForCurrentRun();
        loadSucceeded = true;
      } catch (fallbackErr) {
        const fallbackMessage = fallbackErr instanceof Error ? fallbackErr.message : String(fallbackErr);
        console.log(`[load-draft] per-section fallback ALSO failed error="${fallbackMessage}"`);
        setStartError(`Couldn't load drafted document: ${message}`);
        // Surface on the floating card too, frozen on the step that failed.
        setLoadDocError(`Couldn't load drafted document: ${message}`);
      }
    } finally {
      // Poll Word until the loaded document is actually rendered (paragraph
      // count > 1) before re-enabling sync.  A fixed delay is unreliable —
      // large documents can take longer than 3 s to insert.  We check every
      // 500 ms, give up after 15 s, then resume regardless so the engine
      // isn't stuck paused forever.
      //
      // Word for the web only: there the numbering / heading styles keep
      // re-attaching after the insert's sync returns. Word desktop has applied
      // the whole insert by the time hydrate's last sync resolves, and each poll
      // reads every paragraph's style on Word's UI thread. A failed Load has
      // nothing to wait for.
      if (loadSucceeded && !isWordDesktop()) {
        const waitStartedAt = Date.now();
        await _waitForDocumentLoaded(15_000);
        console.log(`[load-draft] _waitForDocumentLoaded settled waitedMs=${Date.now() - waitStartedAt}`);
      }
      // Pass runId so resumeSync re-seeds from DocService's current blocks
      // instead of reconciling the next cycle against a blank baseline —
      // otherwise every reload mints fresh block_ids for content DocService
      // already has stored, orphaning the previous generation (its deletion
      // is never computed since the engine has no record it existed).
      resumeSync(state.runId);
      loadDraftInFlightRef.current = false;
      setLoadDraftInFlight(false);
      // Settle window is over — release the guard and drain any re-projection
      // that was deferred while the doc was loading.
      endLiveDocLoad();
      if (loadSucceeded) {
        logActivity("you", "Loaded the drafted document into Word.");
        // Terminal "Loaded ✓" frame, then auto-dismiss — mirrors handleSave.
        setLoadDocStepIndex(LOAD_STEP_DEFS.length);
        setLoadDocDone(true);
        setTimeout(() => {
          setLoadDocStepIndex(-1);
          setLoadDocDone(false);
        }, 1500);
      }
      // On failure the index stays frozen on the failed step and loadDocError
      // is set, so the card holds until the writer dismisses it.
      console.log(`[load-draft] handleLoadDraftedDocument complete totalElapsedMs=${Date.now() - loadStartedAt}`);
    }
  }, [state.runId, docConfig, fullDocConfig, handleRunLoaded, startSyncEngineForCurrentRun, enqueueWordWrite, logActivity]);

  const handleOpenDocument = useCallback(() => {
    if (!state.runId) {
      setStartError("Couldn't open document. Run id is missing.");
      return;
    }
    try {
      rememberRunRecoveryHint(state.runId, "shell.open-document");
      const url = runDocumentUrl(state.runId, false);
      console.info("[VariantCShell/OpenDocument] click", {
        runId: state.runId,
        includeProvenance: false,
        url,
      });
      openExternal(url);
      console.info("[VariantCShell/OpenDocument] dispatched", {
        runId: state.runId,
      });
    } catch (err) {
      console.error("[VariantCShell/OpenDocument] failed", {
        runId: state.runId,
        error: err instanceof Error ? err.message : String(err),
      });
      setStartError(`Open document failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [state.runId]);

  // Inspector drawer — shared across every spine surface (idle restored
  // run AND mid-run / approval / post-run). Previously this JSX lived only
  // inside the run-active branch, so a restored run that landed in "idle"
  // with a populated spine showed the rows but had NOTHING to consume the
  // ``focusedSectionId`` set on row click → "clicking a section does
  // nothing". Hoisting it here, gated only on the two things DraftTab's
  // ``getSectionContent(runId, section.id)`` actually needs (a focused
  // section + a runId), fixes idle and tolerates a null ``activeSelection``
  // (docCode degrades to citations-unavailable rather than blocking).
  const inspectorDocCode = activeSelection?.docCode ?? selectedDocCode ?? "";

  // "Resolve with asthra" from the Issues tab — drop the writer into the chat
  // with the issue's paragraph highlighted as context (exactly like manually
  // selecting a paragraph and telling asthra what to do), AND pre-fill the
  // composer with an issue-typed instruction (buildResolveInstruction): a
  // missing-info sweep for data gaps, or a resolve-this-specific-flag directive
  // carrying the finding's own context + recommended fix for regulatory items.
  // The instruction is seeded, not auto-sent — the writer reviews and presses
  // send, then asthra's rewrite lands the edit and the backend records the
  // issue as resolved-by-asthra.
  const handleResolveGapWithAsthra = useCallback(
    (issue: SectionIssue) => {
      // Resolve the section from the issue's own module_id so this handler
      // serves both the per-section inspector and the submission-wide Issues
      // tab (where nothing is focused). Every SectionIssue carries module_id.
      const sec = sections.find((s) => s.id === issue.module_id) ?? null;
      if (!sec) return;
      // Switch to the chat view so the selection pill + input are visible.
      handleViewModeChange("timeline");
      void (async () => {
        // Highlight the marker's whole paragraph in Word (fall back to the
        // section heading when the marker can't be located), then capture it
        // into the chat selection-context pill.
        const found = await selectMarkerParagraph(issue.marker_text);
        if (!found) await navigateToSection(sec.number);
        await captureSelection();
        commandRailRef.current?.prefillInput(buildResolveInstruction(issue, sec));
      })();
    },
    [sections, captureSelection, handleViewModeChange],
  );

  // A source library is required to draft anything — Asthra only acts on
  // trusted source data (mirrors the chat gate). For a custom submission that
  // skipped the library step this is false, so drafting stays disabled until a
  // library is attached.
  const hasSourceLibrary = useMemo(
    () => isValidLibraryId(activeSelection?.libraryId) || isValidLibraryId(selectedLibraryId),
    [activeSelection?.libraryId, selectedLibraryId],
  );
  // Custom submission: whether the focused section can be drafted (it needs a
  // saved instruction — there's no template instruction to fall back on — and a
  // source library). Standard runs are always draftable (template-driven).
  const isCustomRun = runSubmissionMode === "custom";
  const focusedCustomHasInstruction = Boolean(focusedSection?.instruction?.trim());
  // Whether ANY custom section carries an instruction — gates the post-run
  // "Draft more sections" button (nothing to draft until at least one does).
  const anyCustomSectionHasInstruction = useMemo(
    () => (isCustomRun ? sections.some((s) => Boolean(s.instruction?.trim())) : false),
    [isCustomRun, sections],
  );

  const inspectorNode =
    focusedSection && state.runId ? (
      <Inspector
        section={focusedSection}
        runId={state.runId}
        docCode={inspectorDocCode}
        identity={identity}
        openReviewSignal={reviewOpenSignal}
        issuesRefreshSignal={issuesRefreshSignal}
        actionsRefreshSignal={actionsRefreshSignal}
        auditActivityLines={agentActivityBySection.get(focusedSection.id) ?? EMPTY_ACTIVITY_LINES}
        onResolveGapWithAsthra={handleResolveGapWithAsthra}
        onResolveSelectedGaps={openResolveWizard}
        onFixGapsNow={fixNow}
        isCustomSubmission={runSubmissionMode === "custom"}
        onInstructionSaved={(sectionId, instruction) => setSectionInstruction(sectionId, instruction)}
        onClose={() => setFocusedSectionId(null)}
        sections={enrichedSections}
        onNavigate={setFocusedSectionId}
        onRegenerate={
          regenEnabled &&
          (focusedSection.status === "drafted" ||
            focusedSection.status === "needs_approval" ||
            focusedSection.status === "flagged")
            ? handleInspectorRegenerate
            : undefined
        }
        onLoadIntoWord={
          // Custom runs place sections by the writer's own heading (CUSTOM-N
          // never matches a heading), which this number-keyed re-projection
          // can't do — their Load path is loadCustomRunIntoLiveDoc.
          !isCustomRun &&
          !loadDraftInFlight &&
          (focusedSection.status === "drafted" ||
            focusedSection.status === "needs_approval" ||
            focusedSection.status === "flagged")
            ? () => {
                // Loading by hand ends the hold — the audit's own
                // audit_complete then re-projects its flags as usual.
                if (state.runId) dropHeldSectionInsert(state.runId, focusedSection.id);
                void reprojectSectionIntoLiveDoc(state.runId, focusedSection.id);
              }
            : undefined
        }
        onRunReview={
          focusedSection.status === "drafted" ||
          focusedSection.status === "needs_approval" ||
          focusedSection.status === "flagged"
            ? () => {
                if (!state.runId) return;
                // Same call as the Issues tab's "Re-run review": custom sections
                // carry their real heading so the audit can sync + re-project.
                const custom =
                  focusedSection.id.startsWith("CUSTOM-") && focusedSection.number
                    ? { sectionNumber: focusedSection.number, title: focusedSection.title }
                    : undefined;
                void startAudit(state.runId, focusedSection.id, custom);
              }
            : undefined
        }
        isLoadingIntoWord={liveDocLoadBySection.get(focusedSection.id)?.state === "loading"}
        onShowInWord={() => {
          navigateToSection(focusedSection.number).catch(() => {});
        }}
        onStopSection={regenActivity?.sectionId === focusedSection.id ? stopRegen : undefined}
        onGenerate={
          regenEnabled &&
          focusedSection.status !== "drafted" &&
          focusedSection.status !== "needs_approval" &&
          focusedSection.status !== "flagged" &&
          // Custom submission: the spine is the writer's OWN heading outline with
          // no doctype-template instruction to draft from, so Generate lights up
          // only once the writer has authored an instruction for this section
          // (Instructions tab) AND a source library is attached. Standard runs
          // are template-driven and always draftable.
          (isCustomRun ? focusedCustomHasInstruction && hasSourceLibrary : true)
            ? () => {
                // Close the Inspector before drafting begins, mirroring the
                // Regenerate flow (useRegenFlow.handleRegenConfirm), which
                // clears focusedSectionId once its run kicks off.
                setFocusedSectionId(null);
                void handleGenerateSection(focusedSection.id);
              }
            : undefined
        }
        isRegenInProgress={isAnyRegenInProgress}
        onCopyFromSource={
          focusedSection.status === "drafted" ||
          focusedSection.status === "needs_approval" ||
          focusedSection.status === "flagged"
            ? () =>
                setCopyTarget({
                  moduleId: focusedSection.id,
                  number: focusedSection.number,
                  title: focusedSection.title,
                  placement: "after_text",
                })
            : undefined
        }
      />
    ) : null;

  // Run controls (Pause · Stop run · Draft now) — shown inside the Spine
  // header under the progress bar (design board 09).
  // A regenerate's controls take the spine header's place (design board 16).
  const regenSpineHeader = regenActivity ? (
    <RegenControlBar
      activity={regenActivity}
      withNotice={false}
      onPause={pauseRegen}
      onStop={stopRegen}
      onDraftNow={draftNowRegen}
      onResume={resumeRegen}
      onDiscard={discardPausedRegen}
    />
  ) : null;
  const spineControls =
    state.state === "mid-run" || state.state === "approval" || state.state === "pausing" || state.state === "paused" ? (
      <SpineControlBar
        runState={state.state === "paused" ? "paused" : state.state === "pausing" ? "pausing" : "mid-run"}
        onPause={state.state === "mid-run" || state.state === "approval" ? handlePauseRun : undefined}
        onResume={state.state === "paused" ? handleResumePausedRun : undefined}
        onDraftNow={state.state === "mid-run" ? handleDraftNowRun : undefined}
        draftNowPending={draftNowPending}
        onShowKillConfirmation={() => setShowKillConfirmation(true)}
        disabled={state.state === "pausing"}
        notice={state.pauseNotice ?? null}
      />
    ) : null;

  let body: React.ReactNode;
  // Plan-review takes precedence over idle picker — once the writer
  // has clicked Start Draft and we're either fetching or have a plan
  // in hand, the picker is no longer the right surface. The reducer
  // stays at "idle" because no run is in flight yet; the inline
  // pre-spine view sits in this gap honestly.
  // Plan-review activates either via the fresh-draft entry path
  // (state still idle, picker → plan flow) OR via the Phase 9.4
  // "Draft more sections" post-run flow (draftingMore explicitly
  // set by the writer; planData carried over from the prior run).
  // Both render the same LoomPlanReview component; the difference
  // is whether ``lockedModuleIds`` is pre-seeded.
  const planReviewActive =
    (state.state === "idle" && !!activeSelection && (planLoading || !!planData || !!planError)) ||
    (draftingMore && !!activeSelection && !!planData);

  // Section-picker takes precedence over LoomFresh but yields to plan-
  // review — it's the surface between "doc + library chosen" and "plan
  // requested / started". Cleared on commit (→ plan or run) or cancel
  // (→ back to LoomFresh).
  const sectionPickerVisible =
    (state.state === "idle" || (state.state === "post-run" && draftingMore)) &&
    !!activeSelection &&
    sectionPickerActive &&
    !planLoading &&
    !planData &&
    !planError;

  // Memoised selection tree + gate meta — derived from docConfig so
  // LoomSelection's useMemo boundaries don't re-fire when unrelated
  // shell state changes.
  // The writer picked which eCTD leaf this document IS.
  //
  // The identity is PERSISTED, not just held in state: the draft view, the
  // renderer and the sibling lookup all read it back off the document record,
  // and a document that never got one renders the whole doctype with no reason
  // to re-ask. A failure is surfaced rather than swallowed — a silently-lost
  // identity is indistinguishable from a stored one.
  //
  // The server does not take the id on trust: `server/leaf_identity.py` stores
  // it only when the doctype declares that leaf, and declines otherwise.
  const handleLeafSelect = useCallback(
    (nextLeafId: string) => {
      setLeafDocumentId(nextLeafId);
      // Ticks belonging to the leaf just left would be sent to the run and
      // drafted into the wrong document.
      setSectionCommit(null);
      if (!documentSessionId) return;
      void upsertDocumentSession(documentSessionId, leafIdentityPatch(nextLeafId, null)).catch((err: unknown) => {
        console.error("[VariantCShell] could not record the leaf identity", err);
      });
    },
    [documentSessionId],
  );

  // The pickers read the leaf-scoped `docConfig`; only the leaf CHOICES need
  // the whole doctype's partition.
  const leafOptionsForPicker = useMemo(() => leafOptions(fullDocConfig), [fullDocConfig]);
  const selectionTree = useMemo<SelectionNode[]>(
    () => (docConfig ? buildSelectionTree(docConfig) : []),
    [docConfig],
  );
  const selectionGates = useMemo<GateMeta[]>(
    () => (docConfig ? buildGateMeta(docConfig) : []),
    [docConfig],
  );
  // Already-drafted section ids for the draft-more picker. They stay
  // SELECTABLE (re-drafting overwrites them) and are surfaced as an
  // informational "drafted" hint rather than a hard lock — the backend
  // continue_run_sections path resets + re-drafts any drafted module the
  // writer re-selects.
  // Alongside each id: when its draft landed and its open review findings,
  // for the picker's Drafted tab ("accepted 11:37am · 2 issues").
  const { draftedSectionIdsForPicker, draftedMetaForPicker } = useMemo(() => {
    const ids: string[] = [];
    const meta: Record<string, DraftedSectionMeta> = {};
    if (!draftingMore || !docConfig) return { draftedSectionIdsForPicker: ids, draftedMetaForPicker: meta };
    const draftedModules = new Set(computeLockedModuleIds(sections));
    const sectionById = new Map(sections.map((s) => [s.id, s]));
    for (const [moduleId, moduleMeta] of Object.entries(docConfig.modules ?? {})) {
      if (!draftedModules.has(moduleId)) continue;
      const sectionId = (moduleMeta.sections ?? [])[0] ?? moduleId;
      ids.push(sectionId);
      const spine = sectionById.get(moduleId);
      meta[sectionId] = { acceptedAt: spine?.acceptedAt, issueCount: spine?.flaggedCount };
    }
    return { draftedSectionIdsForPicker: ids, draftedMetaForPicker: meta };
  }, [draftingMore, docConfig, sections]);
  // Hard-locked (never selectable) section ids for the Section Picker. Drafted
  // sections used to be locked here, which meant they could never be re-drafted
  // from "Draft more" — but the backend `continue_run_sections` path resets and
  // re-drafts any drafted module the writer re-selects, so drafted sections now
  // flow through `draftedSectionIdsForPicker` (selectable, with a "drafted" chip
  // and an overwrite confirmation on Start) instead. Nothing is hard-locked
  // today; the prop stays wired so a genuinely non-selectable status could be
  // added here later without touching the picker.
  const lockedSectionIdsForPicker = useMemo<string[]>(() => [], []);
  const initialStrategy: WriteStrategy = docConfig?.generation?.insertion_mode === "batch" ? "batched" : "live";

  // Per-section drafted counter — drives the TabBar's live Spine
  // progress chip ("5/38") and some downstream hints. StatusStripC's
  // phase-lifecycle derivation retired in Spec 29 Phase 1.
  const draftedCount = sections.filter((s) => s.status === "drafted").length;
  // Sections whose drafting failed (server module_error) — no body produced.
  const failedCount = sections.filter((s) => s.failed && s.status === "flagged").length;
  // A wrapped-up section landed → the Draft now override has done its job.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on count change only
  useEffect(() => {
    setDraftNowPending(false);
  }, [draftedCount]);

  if (starting) {
    body = (
      <div className={styles.startingOverlay}>
        <div className={styles.startingMark}>{draftingMore ? "Drafting more sections…" : "Starting your run…"}</div>
        {draftingMore && <div className={styles.statusChip}>Backend busy</div>}
        <div className={styles.startingHint}>
          {draftingMore
            ? "Waiting for the continuation request to finish before the next run can start."
            : "Spinning up the agent and reading the library. The spine populates as soon as the first section starts."}
        </div>
      </div>
    );
  } else if (sectionPickerVisible && activeSelection) {
    // Spec 23 P10.3 — unified Selection picker. Sits between
    // LoomFresh and LoomPlanReview. Commit → plan-or-run; Cancel →
    // back to LoomFresh.
    body = (
      <LoomSelection
        // Remount per document so the section defaults (computed once on mount)
        // recompute against the newly-scoped tree when the writer switches
        // documents. Constant for single-document doctypes (leafDocumentId stays
        // null), so their behavior is unchanged.
        key={leafDocumentId ?? "__root__"}
        tree={selectionTree}
        gates={selectionGates}
        leaves={leafOptionsForPicker}
        leafDocumentId={leafDocumentId}
        onLeafSelect={handleLeafSelect}
        docCode={activeSelection.docCode}
        docStandard={activeSelection.docFramework ?? ""}
        // Region ("US · FDA") and library ride as tags so the writer sees where
        // the agents were told this is filed, and from what, before drafting.
        extraTags={[regionTag(activeSelection.region, submissionRegions), activeSelection.libraryName].filter(
          (t): t is string => !!t,
        )}
        initialStrategy={initialStrategy}
        // "Draft more" starts from the undrafted sections, never the previous
        // batch's ticks — those are the sections it just drafted, and a commit
        // restored from the session record can survive into this picker.
        initialChecked={draftingMore ? undefined : sectionCommit?.explicitSectionIds}
        // "Draft more" opens with nothing ticked; "Select all not drafted"
        // is one click away.
        defaultEmpty={draftingMore}
        lockedSectionIds={lockedSectionIdsForPicker}
        draftedSectionIds={draftedSectionIdsForPicker}
        draftedMeta={draftedMetaForPicker}
        naming={pickerNaming}
        onStart={handlePickerCommit}
        onCancel={handleSectionCancel}
      />
    );
  } else if (planReviewActive && activeSelection) {
    body = (
      <LoomPlanReview
        plan={planData}
        loading={planLoading}
        apiHealthy={planApiHealthy}
        error={planError}
        docCode={activeSelection.docCode}
        libraryName={activeSelection.libraryName}
        onApprove={handlePlanApprove}
        onModify={handlePlanModify}
        onSkip={handlePlanSkip}
        // Spec 23 P10.4 — thread the LoomSelection strategy into the
        // narrative builder so the "Planner says" line previews how
        // the run will feel. Absent when the writer took the legacy-
        // fallback path (no config → no picker commit).
        strategy={sectionCommit?.strategy}
        // Spec 23 PR-A — pass docConfig.gates so the plan rows
        // render in coloured gate packets matching the picker.
        gates={docConfig?.gates}
        // PlanHighlights "deps pulled in" stat — diff between
        // resolver-implied and explicit picks. Zero when no
        // sectionCommit (legacy fallback) or no implied modules.
        impliedCount={
          sectionCommit
            ? Math.max(0, sectionCommit.resolvedSectionIds.length - sectionCommit.explicitSectionIds.length)
            : 0
        }
        // New compact header pill — same handler as Modify, just
        // surfaced separately in the new layout.
        onBackToPicker={handlePlanModify}
        // Phase 9.4 — locked module ids surface ONLY when the
        // post-run "Draft more sections" flow is active. Undefined
        // in the fresh-draft path → LoomPlanReview behaves exactly
        // as before (no rows locked, all modules actionable).
        lockedModuleIds={lockedModuleIdsForNextPlan ?? undefined}
      />
    );
    // Spec 29 Phase A (2026-04-23) — the /draft flow is now
    // conversational, rendered INSIDE the idle TimelineView via the
    // ``pinnedAgentCard`` prop. The LoomFresh full-body takeover is
    // retired from the draft path; LoomFresh the component is still
    // in the repo but no longer rendered. The former
    // ``draftPickerActive`` branch that lived here is gone.
  } else if (state.viewMode === "drafts") {
    // Batch-SSOT Drafts tab — run-agnostic. Renders the same
    // panel regardless of the run lifecycle (idle / mid-run /
    // approval / post-run), so the writer can browse prior runs
    // and pull bundles without leaving the shell.
    body = (
      // The Drafts tab reuses the fresh-doc Home scene verbatim (hero + card
      // grid), so the two surfaces stay pixel-identical. ``enableRunActions``
      // turns on the per-card ⋯ menu (Open in Word / Download bundle), which acts
      // on each document's latest run — one card per document, as on Home.
      <HomeScene
        userIdentifier={userIdentifier ?? null}
        docConfig={fullDocConfig}
        workspaceId={draftsWorkspaceId}
        workspaces={workspaces}
        scopeWorkspaceId={draftsScopeWorkspaceId}
        currentDocId={draftsDocId}
        onScopeChange={setDraftsScopeWorkspaceId}
        onBeforeLoad={() => emptyDocWatcherRef.current?.acknowledge()}
        onRunLoaded={handleRunLoaded}
        onNewDoc={handleContextCardStartFreshDraft}
        refreshSignal={draftsRefreshNonce}
        libraryNameById={libraryNameById}
        enableRunActions
        onBack={
          state.state === "idle" &&
          ((documentContext.state === "ready" && documentContext.classification?.state === "empty") || starterDoc)
            ? () => handleViewModeChange("timeline")
            : undefined
        }
      />
    );
  } else if (state.viewMode === "images") {
    // Image search — run-agnostic, like Drafts. Browse the library's figures
    // and insert one at the cursor (insert is shell-owned via handleImageInsert).
    // When an image is selected, the pane enters replace mode so the Insert
    // button replaces the selected image instead of inserting a new one.
    body = (
      <ImageSearchPane
        libraryId={imagesLibraryId}
        libraryName={imagesLibraryName}
        onInsert={(title, replaceMode) => void handleImageInsert(title, replaceMode)}
        replaceMode={imageSelected}
      />
    );
  } else if (state.state === "idle") {
    const classification = documentContext.state === "ready" ? documentContext.classification : null;

    if (classification?.state === "empty" || starterDoc) {
      // New Home scene: empty-document landing page with document list.
      // Replaces the old onboarding checklist (landingpage.txt spec).
      // This is a full-screen, dedicated experience with no TabBar/ContextChips.
      // A refused start (library not ready, POST /runs failure) lands back
      // here, so its reason must show here too — not only on the has-content
      // timeline below.
      body = (
        <>
          {startError && (
            <Notice tone="danger" title="Couldn't start the run.">
              {startError}
            </Notice>
          )}
          <HomeScene
            userIdentifier={userIdentifier ?? null}
            docConfig={fullDocConfig}
            workspaceId={draftsWorkspaceId}
            workspaces={workspaces}
            scopeWorkspaceId={draftsScopeWorkspaceId}
            currentDocId={draftsDocId}
            onScopeChange={setDraftsScopeWorkspaceId}
            onBeforeLoad={() => emptyDocWatcherRef.current?.acknowledge()}
            onRunLoaded={handleRunLoaded}
            onNewDoc={handleContextCardStartFreshDraft}
            replacesExistingText={starterDoc}
            refreshSignal={draftsRefreshNonce}
            libraryNameById={libraryNameById}
            onSeeAllDrafts={() => handleViewModeChange("drafts")}
          />
        </>
      );
    } else if (state.viewMode === "spine" && sections.length > 0) {
      // Defensive: render the inspector drawer here too so the idle spine
      // surface isn't a dead-click. ``inspectorNode`` self-gates on
      // ``state.runId``, which is null in idle today (RESET_TO_IDLE →
      // initialState), so it stays inert unless a future idle path carries a
      // runId. The load-bearing fix for "nothing opens" lives in the
      // run-active branch below (post-run / mid-run), where the guard no
      // longer hard-requires ``activeSelection``.
      body = (
        <div style={SPINE_DOCK_STYLE}>
          <LoomSpine
            controls={spineControls}
            header={regenSpineHeader}
            afterRows={regenActivity ? <RegenNotice /> : null}
            sections={enrichedSections}
            templateSectionCount={submissionSectionTotal(docConfig, runSubmissionMode)}
            fixingModuleIds={resolveIssuesFlow.inFlightModuleIds}
            fixingFlagCount={resolveIssuesFlow.progress.flags}
            activeSectionId={activeSectionId}
            liveDocLoadBySection={liveDocLoadBySection}
            agentActivityBySection={agentActivityBySection}
            latestRunActivity={latestRunActivity}
            runFailed={state.error !== null}
            pauseAnimations={true}
            streamError={streamError}
            initStatus={initStatus}
            initSideNote={auditLibraryInitNote}
            onSectionFocus={setFocusedSectionId}
            onOpenReview={handleOpenReview}
            pendingApproval={state.pendingApproval}
            onApprovalAccept={handleApprovalAccept}
            onApprovalOpenReview={handleApprovalOpenReview}
            countersBySection={state.eventLog.counters}
          />
          {inspectorNode}
        </div>
      );
    } else {
      // Existing TimelineView + IdleGreeting (has-content context card, or
      // loading-classification fallback). Byte-identical to pre-HomeScene
      // behavior for non-empty docs.
      const hasDoctype = !!selectedDocCode;
      const hasLibrary = !!selectedLibraryId;
      const doctypeLabel = selectedDocCode ? (lookupDoctypeChoice(selectedDocCode)?.docName ?? null) : null;
      const libraryName = selectedLibraryId ? (libraries.find((l) => l.id === selectedLibraryId)?.name ?? null) : null;

      // The has-content card is an opener, not a fixture: TimelineView pins a
      // persistent greeting under every chat turn, so once the writer has a
      // conversation going (or the doc is bound to a project — a Load that
      // landed idle, Stop/Reset) it kept resurfacing below each reply.
      const hasChatActivity =
        timelineEntries.length > 0 || (chatHistoryMessages?.length ?? 0) > 0 || chat.isStreaming;
      const isBoundToProject = sections.length > 0;
      const greetingContent: React.ReactNode =
        classification && classification.state === "has-content" && !hasChatActivity && !isBoundToProject ? (
          <IdleGreeting
            classification={classification}
            hasDoctype={hasDoctype}
            hasLibrary={hasLibrary}
            doctypeLabel={doctypeLabel}
            libraryName={libraryName}
            onPickDoctype={handleChecklistPickDoctype}
            onPickLibrary={handleChecklistPickLibrary}
            onReviewAndStart={handleChecklistReviewAndStart}
            onAskAboutDocument={handleAskAboutDocument}
            showAskAboutDocument={showIncompleteActions}
            onStartFreshDraft={handleContextCardStartFreshDraft}
          />
        ) : null;

      body = (
        <>
          {startError && (
            <div className={styles.errorBanner}>
              <strong>Couldn&apos;t start the run.</strong> {startError}
            </div>
          )}
          {workspaceFetchError && (
            <div className={styles.errorBanner}>
              <strong>Couldn&apos;t load workspaces.</strong> {workspaceFetchError}
            </div>
          )}
          <TimelineView
            entries={timelineEntries}
            setupDocument={setupDocument}
            filterSectionLabel={state.timelineFilter.sectionId}
            replayCursor={state.replayCursor}
            runId={state.runId}
            verdictForSection={verdictForSection}
            streamingStatus={composerStreamingStatus}
            streamingDraft={chat.isStreaming ? chat.streamingDraft : chatRunActivity?.draftSoFar || null}
            streamStartedAt={chat.isStreaming ? chat.streamStartedAt : (chatRunActivity?.startedAt ?? null)}
            onProposalDecision={handleProposalDecision}
            proposalMessage={adhocProposalNode ?? editProposalNode ?? writeApprovalNode}
            liveActivity={liveActivityNode}
            pendingApproval={state.pendingApproval}
            onApprovalAccept={handleApprovalAccept}
            onApprovalOpenReview={handleApprovalOpenReview}
            // Spec: Blank-Document Onboarding — render the checklist/card as
            // ONE persistent interactive message so the writer's doctype /
            // library picks update it in place (steps flip green) instead of
            // tearing it down once pick events land in the log. The ephemeral
            // ``greeting`` carries only the fallback welcome text shown while
            // classification is still loading (no checklist/card yet).
            // greeting={greetingContent ? null : buildIdleGreeting(null)}
            persistentGreeting={greetingContent}
            // Spec 29 Phase A — conversational /draft flow renders
            // as a pinned card at the top of the timeline. Null
            // when the flow is idle; TimelineView falls back to
            // the greeting.
            pinnedAgentCard={draftFlowCard}
            persistedMessages={chatHistoryMessages}
            hasMoreHistory={chatHasMoreOlder}
            isLoadingHistory={chatIsLoadingOlder}
            onLoadOlder={chatLoadOlder}
            now={new Date()}
            onSetupNextStep={handleSetupNextStep}
            onReply={handleReply}
            pinToLatestSignal={chatPinSignal}
          />
        </>
      );
    }
  } else {
    // mid-run / approval / post-run share the run surface. Spec 29
    // promotes TabBar (shell-level) to switch between Chat (Timeline)
    // and Spine. The body computed here contains the active tab's
    // content only — the TabBar and ContextStrip render above this
    // in the shell return block. EventLegendStrip and
    // SpineTimelineToggle retired with Spec 29 Phase 1.
    const runBodyIsSpine = state.viewMode === "spine";
    const runBodyIsIssues = state.viewMode === "issues";
    // Rule lives in postRunBanner.ts — a regen keeps the reducer in ``post-run``
    // while a section actively drafts, so the banner cannot key off state alone.
    const showPostRunBanner = shouldShowPostRunBanner({
      isSpineView: runBodyIsSpine,
      runState: state.state,
      hasRunSummary: Boolean(state.runSummary),
      sections,
    });
    body = (
      <>
        {runBodyIsSpine ? (
          <div style={SPINE_DOCK_STYLE}>
            <LoomSpine
              controls={spineControls}
              header={regenSpineHeader}
              afterRows={regenActivity ? <RegenNotice /> : null}
              sections={enrichedSections}
              templateSectionCount={submissionSectionTotal(docConfig, runSubmissionMode)}
              fixingModuleIds={resolveIssuesFlow.inFlightModuleIds}
              fixingFlagCount={resolveIssuesFlow.progress.flags}
              activeSectionId={activeSectionId}
              liveDocLoadBySection={liveDocLoadBySection}
              agentActivityBySection={agentActivityBySection}
              latestRunActivity={latestRunActivity}
              runFailed={state.error !== null}
              pauseAnimations={state.state === "paused" || state.state === "pausing"}
              runDurationMs={state.runSummary?.durationMs}
              streamError={streamError}
              initStatus={initStatus}
              initSideNote={auditLibraryInitNote}
              onSectionFocus={setFocusedSectionId}
              onOpenReview={handleOpenReview}
              pendingApproval={state.pendingApproval}
              onApprovalAccept={handleApprovalAccept}
              onApprovalOpenReview={handleApprovalOpenReview}
              countersBySection={state.eventLog.counters}
              complete={
                showPostRunBanner
                  ? { issuesOpen: issuesOpenCount, onReviewIssues: () => handleViewModeChange("issues") }
                  : null
              }
            />
            {inspectorNode}
          </div>
        ) : runBodyIsIssues ? (
          <SubmissionIssuesTab
            runId={state.runId}
            onResolveWithAsthra={handleResolveGapWithAsthra}
            onOpenCountChange={setIssuesOpenCount}
            onResolveSelected={openResolveWizard}
            onFixNow={fixNow}
            reviewFlow={reviewFlow}
            issuesRefreshSignal={issuesRefreshSignal}
            proposalsRefreshSignal={proposalsRefreshSignal}
            reviewWindowSignal={reviewWindowSignal}
            libraryId={draftsLibraryId}
            onProposalApplied={() => setIssuesRefreshSignal((n) => n + 1)}
            resolveSummary={resolveIssuesFlow.summary}
            onDismissResolveSummary={resolveIssuesFlow.dismissSummary}
            onShowFixResults={fixPassTargets && resolveWizardTargets === null ? showFixProgress : undefined}
            // A running pass is shown only in the Fix issues window: no
            // per-row "Fixing…" / live agent lines in this list.
            liveIssueOutcomes={resolveIssuesFlow.issueOutcomes}
            writeTitleFields={writeResolvedTitleFields}
          />
        ) : (
          <TimelineView
            entries={timelineEntries}
            setupDocument={setupDocument}
            filterSectionLabel={state.timelineFilter.sectionId}
            replayCursor={state.replayCursor}
            runId={state.runId}
            verdictForSection={verdictForSection}
            inlineCapsule={inlineCapsule}
            settledBatch={settledBatch}
            onOpenSpine={() => handleViewModeChange("spine")}
            // Spec 29 — live chat streaming indicator. The hook
            // exposes liveTool; buildStreamingStatus maps it
            // to a writer-facing phrase. Null when not streaming.
            // Intent-routed chat reuses the same transient row for
            // a chat-initiated regen / ad-hoc draft (live drafting
            // bubble) and the highlight-rewrite wait state.
            streamingStatus={composerStreamingStatus}
            streamingDraft={chat.isStreaming ? chat.streamingDraft : chatRunActivity?.draftSoFar || null}
            streamStartedAt={chat.isStreaming ? chat.streamStartedAt : (chatRunActivity?.startedAt ?? null)}
            onProposalDecision={handleProposalDecision}
            // Text-edit proposals — ad-hoc section draft, highlight-edit
            // rewrite, OR agent write approval, all through the shared
            // ProposalCard. They can't normally co-occur (the WS gate
            // blocks the tool loop while a chat turn runs); newest-
            // decision-surface-first if they do.
            proposalMessage={adhocProposalNode ?? editProposalNode ?? writeApprovalNode}
            liveActivity={liveActivityNode}
            // Spec 29 Phase 2 — inline approval row at the top of
            // the Timeline. Shares the same pending-approval state
            // the spine-side ApprovalPopout reads; both dispatch
            // the same handlers so clicking either resolves the
            // approval globally.
            pendingApproval={state.pendingApproval}
            onApprovalAccept={handleApprovalAccept}
            onApprovalOpenReview={handleApprovalOpenReview}
            // Flat chronological history — persisted messages merge
            // with the live stream inside TimelineView (deduped by
            // ``at:role``). Null when no document is selected;
            // TimelineView renders the live stream only in that case.
            persistedMessages={chatHistoryMessages}
            hasMoreHistory={chatHasMoreOlder}
            isLoadingHistory={chatIsLoadingOlder}
            onLoadOlder={chatLoadOlder}
            // Phase 7.5 — current-time anchor for day-banner labels
            // ("Today" / "Yesterday" / weekday). Pass a single
            // ``now`` so grouping decisions and labels stay
            // consistent within a render.
            now={new Date()}
            onSetupNextStep={handleSetupNextStep}
            onReply={handleReply}
            pinToLatestSignal={chatPinSignal}
          />
        )}
        {/* The Inspector docks over the spine (inside SPINE_DOCK_STYLE
            above). Focus state is preserved across tab flips; re-opening
            the Spine tab re-shows it. Shared ``inspectorNode`` renders it
            identically here and in the idle restored-run spine branch. */}
        {/* Post-run banner only on Spine view. Chat view does not show it.
            Suppressed while a section is drafting — a regen keeps the reducer
            in ``post-run`` (see postRunBanner.ts). */}
        {showPostRunBanner && (
          // Run complete footer (design board 14). "Load into Word" re-pulls the
          // run's canonical server body and rebuilds the live doc
          // (handleLoadDraftedDocument); safe and idempotent for both modes.
          // "Draft more sections" PRESERVES library + doctype + plan and locks
          // drafted rows in the picker: no destructive reset on this surface.
          <FooterBar
            note={failedCount > 0 ? `${failedCount} ${failedCount === 1 ? "section" : "sections"} failed to draft.` : undefined}
          >
            <Button variant="secondary" disabled={loadDraftInFlight} onClick={() => void handleLoadDraftedDocument()}>
              {loadDraftInFlight ? "Loading…" : "Load into Word"}
            </Button>
            {(() => {
              const busy = starting;
              // Custom submission: "Draft more" needs an instruction on at least
              // one section and a source library (the picker then restricts to
              // instruction-bearing sections). Standard runs always can.
              const customBlocked = isCustomRun && !(anyCustomSectionHasInstruction && hasSourceLibrary);
              return (
                <Button
                  variant="secondary"
                  icon="plus"
                  disabled={busy || customBlocked}
                  title={
                    customBlocked
                      ? !hasSourceLibrary
                        ? "Attach a source library first — the drafting agent works only from your sources."
                        : "Add a drafting instruction to at least one section (Instructions tab) to draft a custom submission."
                      : undefined
                  }
                  onClick={isCustomRun ? () => setCustomDraftPickerActive(true) : handleDraftMoreSections}
                >
                  {busy ? "Drafting more sections…" : "Draft more sections"}
                </Button>
              );
            })()}
          </FooterBar>
        )}
      </>
    );
  }

  // Spec 29 — Chat is the default tab; Spine tab materialises only
  // during a run. Tab state is driven by ``state.viewMode`` in the
  // reducer (timeline → "chat" semantically) so existing /spine /
  // /timeline slash commands continue to work without translation.
  const activeTab: TabId =
    state.viewMode === "spine"
      ? "spine"
      : state.viewMode === "issues"
        ? "issues"
        : state.viewMode === "drafts"
          ? "drafts"
          : state.viewMode === "images"
            ? "images"
            : "chat";
  const showSpineTab =
    state.state === "mid-run" ||
    state.state === "approval" ||
    state.state === "pausing" ||
    state.state === "paused" ||
    state.state === "post-run" ||
    (state.state === "idle" && sections.length > 0 && !!activeSelection && !!docConfig);
  // The submission-wide Issues tab is meaningful whenever the Spine is — i.e.
  // whenever a run's sections exist to carry gap/regulatory issues.
  const showIssuesTab = showSpineTab;

  // Home scene active: idle state + empty document classification (landing page).
  // Used to hide TabBar, ContextChipsBar, and CommandRail on the clean landing view.
  const isHomeSceneActive =
    state.state === "idle" &&
    ((documentContext.state === "ready" && documentContext.classification?.state === "empty") || starterDoc);
  const takeoverActive = starting || sectionPickerVisible || planReviewActive || resolveWizardTargets !== null;

  // Context-strip slots — workspace + doctype + library + active
  // section. Resolved values render as chips; unresolved ones show
  // muted hints. When unresolved AND the writer is in idle (where
  // picking something is meaningful), the chip is clickable and
  // opens LoomFresh via the same /draft pathway the slash command
  // uses — chip-click and /draft converge on one flow. Mid-run /
  // approval / post-run states omit the click handler; the chips
  // are informational there, not actionable.
  const idleAndAvailable = state.state === "idle" && !pendingDraftStart;
  const hasDocType = !!(selectedDocCode || activeSelection?.docCode);
  // Phase 7.2 — standalone chip-click handlers. Doctype and library
  // are independent picks; clicking one chip never opens the other
  // surface or advances any orchestration (the guided draft flow uses
  // pendingDraftStart to ask for both).
  // biome-ignore lint/correctness/useExhaustiveDependencies: setState setters are guaranteed stable by React
  const openDoctypePickerStandalone = useCallback(() => {
    setPendingDraftStart(false);
    setDoctypePickerOpen(true);
  }, []);
  const [workspacePickerOpen, setWorkspacePickerOpen] = useState<boolean>(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: setState setters are guaranteed stable by React
  const openWorkspacePicker = useCallback(() => {
    setPendingDraftStart(false);
    setWorkspacePickerOpen(true);
  }, []);
  const openLibraryPicker = useCallback(() => {
    if (!hasDocType) {
      setDoctypePickerOpen(true);
      return;
    }
    openSlideOver("library-list");
  }, [hasDocType, openSlideOver]);

  // Phase 4 / 7.2 — chips reflect the writer's standalone picks,
  // not just the committed activeSelection. ``selectedDocCode``
  // (independent state, set by DoctypePicker) feeds the doctype
  // chip's tentative value when no active draft has committed yet.
  const tentativeDocType = !activeSelection?.docCode && selectedDocCode ? selectedDocCode : null;
  const committedLibraryName = libraries.find((l) => l.id === selectedLibraryId)?.name ?? null;
  const libraryIsTentative =
    !!committedLibraryName && (!activeSelection || activeSelection.libraryId !== selectedLibraryId);

  // Spec 34 — chat-first Library Hub slide-over state. All entry
  // points (/library, /upload, inspect chevron, create button) now
  // route through openSlideOver; the legacy LibraryHub modal has
  // been removed.
  const [hubSlideOverOpen, setHubSlideOverOpen] = useState(false);
  const [hubSlideOverMode, setHubSlideOverMode] = useState<HubMode>("list");
  // Library in use before the writer opened another one (›) in the Hub;
  // restored if the Hub closes without "Use". Null = nothing to restore.
  const preInspectLibraryIdRef = useRef<string | null>(null);

  // Fetch the active run's audit library so the Hub can offer a Source↔Audit
  // toggle. Only when the Hub is open and a run exists; the panel self-hides when
  // the feature is off or the run has no audit library.
  const loadHubAuditLibrary = useCallback(() => {
    const runId = state.runId ?? null;
    if (!runId) {
      setHubAuditLibrary(null);
      return;
    }
    getRunAuditLibrary(runId)
      .then(setHubAuditLibrary)
      .catch(() => setHubAuditLibrary(null));
  }, [state.runId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch when the Hub opens or the run changes
  useEffect(() => {
    if (hubSlideOverOpen) loadHubAuditLibrary();
  }, [hubSlideOverOpen, loadHubAuditLibrary]);
  // While the Hub is open and a build is in flight, poll so the Hub's audit-library
  // view shows live step + counter progress (the in-inspector panel self-polls;
  // the Hub was previously fetch-on-open only). Mirrors the 6s inspector cadence.
  const hubAuditBuilding =
    hubAuditLibrary?.audit_library?.status === "acquiring" || hubAuditLibrary?.audit_library?.status === "processing";
  useEffect(() => {
    if (!hubSlideOverOpen || !hubAuditBuilding) return undefined;
    const t = setInterval(loadHubAuditLibrary, 6000);
    return () => clearInterval(t);
  }, [hubSlideOverOpen, hubAuditBuilding, loadHubAuditLibrary]);
  // Names blocked from the last add-batch because a document with the
  // same name already exists in the library. Non-null → show the popup.
  const [duplicateUploadNames, setDuplicateUploadNames] = useState<string[] | null>(null);
  // The held-back files themselves, so the popup can offer "Replace".
  const duplicateUploadRef = useRef<{ files: File[]; libraryId: string } | null>(null);
  // "Upload updated version": in progress / done / failed, one at a time.
  const [sourceReplaced, setSourceReplaced] = useState<SourceReplacedState | null>(null);
  // Documents whose updated version is in flight: their rows show a spinner.
  const [replacingDocIds, setReplacingDocIds] = useState<ReadonlySet<string>>(() => new Set());
  // Guards against a doc-delete firing twice for the same doc_id (rapid
  // double-click, or a duplicate dispatch from the row's nested delete
  // button) while the first DELETE call is still in flight.
  const deletingDocIdsRef = useRef<Set<string>>(new Set());
  setPendingDraftStart;

  // UI-only build: no server record of uploads yet, so the Uploads / Failed
  // tabs show this pane's live queue plus the library's own parse states.
  const uploadBatches = NO_UPLOAD_BATCHES;

  // The upload queue: a few files in flight at a time. Uploads keep going if
  // the writer switches library; the slots carry their library and are filtered.
  const uploadQueueDeps = useMemo<UploadQueueDeps>(
    () => ({
      upload: uploadFile,
      libraryNames: async (libraryId) => (await getDocuments(libraryId)).map((d) => d.name),
      newId: () => crypto.randomUUID(),
      now: () => Date.now(),
    }),
    [],
  );
  const selectedLibraryIdRef = useRef(selectedLibraryId);
  selectedLibraryIdRef.current = selectedLibraryId;
  const uploadQueue = useLibraryUploadQueue({
    deps: uploadQueueDeps,
    onDuplicates: (names, files, libraryId) => {
      duplicateUploadRef.current = { files, libraryId };
      setDuplicateUploadNames(names);
    },
    // Files went in: re-read the library (throttled by the queue), if it is
    // the one on screen.
    onUploaded: (libraryId) => {
      if (libraryId !== selectedLibraryIdRef.current) return;
      invalidateResolveGuard();
      void refreshLibraryInventory();
    },
  });
  const libraryUploadSlots = useMemo(
    () => uploadQueue.slots.filter((s) => s.library_id === selectedLibraryId),
    [uploadQueue.slots, selectedLibraryId],
  );

  // Derive ``DocumentRow[]`` for the slide-over by merging the
  // active upload session with the cached inventory. Files still
  // waiting their turn are counted in the upload status, not listed
  // as rows. Merge is a pure function, so re-evaluating is cheap.
  const hubRows = useMemo(
    () =>
      mergeUploadsAndInventory({
        uploads: libraryUploadSlots.filter((s) => s.status !== "waiting"),
        inventory: inventoryDtoToView(libraryInventory),
      }),
    [libraryUploadSlots, libraryInventory],
  );

  const liveUploadForLibrary = useMemo(() => liveUpload(libraryUploadSlots), [libraryUploadSlots]);
  const sendingHere = (liveUploadForLibrary?.finished ?? true) === false;
  const activeWorkspace = workspaces.find((w) => w.id === selectedWorkspaceId) ?? null;
  const activeLibraryForChips = libraries.find((l) => l.id === selectedLibraryId) ?? null;
  // Upstream's ``doc_processed_count`` can trail a just-finished upload, so
  // the library we hold a live inventory for never reads fewer files than
  // its Ready rows (the list otherwise says "Empty" over a Ready document).
  const hubLibraries = useMemo(() => {
    const ready = hubRows.filter((r) => r.processing_status === "ready" || r.processing_status === "limited").length;
    if (!selectedLibraryId || ready === 0) return libraries;
    return libraries.map((l) =>
      l.id === selectedLibraryId && (l.document_count ?? 0) < ready ? { ...l, document_count: ready } : l,
    );
  }, [libraries, selectedLibraryId, hubRows]);

  const handleNewHubAddFiles = useCallback(
    async (files: File[]) => {
      if (!selectedLibraryId || files.length === 0) return;
      const libraryId = selectedLibraryId;
      await uploadQueue.addFiles(libraryId, files);
    },
    [selectedLibraryId, uploadQueue.addFiles],
  );

  const retryFailedUploads = useCallback(() => {
    if (selectedLibraryId) uploadQueue.retryFailed(selectedLibraryId);
  }, [selectedLibraryId, uploadQueue.retryFailed]);
  // Upload ``file`` as the updated version of ``docId`` (same name). The server
  // deletes the old document only once the new one is processed.
  const replaceHubDoc = useCallback(
    async (libraryId: string, docId: string, name: string, file: File) => {
      setReplacingDocIds((ids) => new Set(ids).add(docId));
      try {
        const result = await replaceDocument(libraryId, docId, file);
        setSourceReplaced({
          status: "done",
          name: result.name,
          oldDocId: result.old_doc_id,
          newDocId: result.new_doc_id,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        setSourceReplaced({ status: "failed", name, message });
      } finally {
        setReplacingDocIds((ids) => {
          const next = new Set(ids);
          next.delete(docId);
          return next;
        });
      }
      if (libraryId === selectedLibraryIdRef.current) {
        invalidateResolveGuard();
        void refreshLibraryInventory();
      }
    },
    [invalidateResolveGuard, refreshLibraryInventory],
  );
  const replaceDuplicateUploads = useCallback(async () => {
    const held = duplicateUploadRef.current;
    duplicateUploadRef.current = null;
    setDuplicateUploadNames(null);
    if (!held) return;
    let documents: { id: string; name: string }[] = [];
    try {
      documents = await getDocuments(held.libraryId);
    } catch (error) {
      const name = held.files[0]?.name ?? "";
      setSourceReplaced({ status: "failed", name, message: "The library could not be read." });
      console.error("Failed to list documents for replace:", error);
      return;
    }
    for (const file of held.files) {
      const existing = documents.find((d) => sameName(d.name, file.name));
      if (existing) await replaceHubDoc(held.libraryId, existing.id, existing.name, file);
    }
  }, [replaceHubDoc]);
  const deleteHubDoc = useCallback(
    async (row: DocumentRow) => {
      if (!selectedLibraryId) return;
      // A row with no doc_id is only this pane's upload slot (failed, or sent
      // but not yet in the library): nothing to delete upstream, so take the
      // slot out instead of ignoring the click.
      if (!row.doc_id) {
        if (row.client_id && row.processing_status === "failed_upload") uploadQueue.removeFailed(row.client_id);
        else uploadQueue.forget(selectedLibraryId, { filename: row.filename });
        return;
      }
      // A delete is already in flight for this doc — ignore the
      // duplicate call instead of hitting the API twice.
      if (deletingDocIdsRef.current.has(row.doc_id)) return;
      deletingDocIdsRef.current.add(row.doc_id);
      try {
        await deleteDocument(selectedLibraryId, row.doc_id);
        // Drop it from the in-memory inventory immediately so the
        // Hub list reflects the delete right away, instead of
        // waiting on the full SSE re-resolve below. Its upload slot
        // goes too, or it would come back as an "indexing" row.
        removeDocumentFromInventory(row.doc_id);
        uploadQueue.forget(selectedLibraryId, { docId: row.doc_id, filename: row.filename });
        invalidateResolveGuard();
        // Restart a stream already in flight: it began before the delete.
        await refreshLibraryInventory({ force: true });
      } catch (error) {
        console.error("Failed to delete document:", error);
      } finally {
        deletingDocIdsRef.current.delete(row.doc_id);
      }
    },
    [
      selectedLibraryId,
      removeDocumentFromInventory,
      invalidateResolveGuard,
      refreshLibraryInventory,
      uploadQueue.removeFailed,
      uploadQueue.forget,
    ],
  );
  // Retry on a file the library stored but couldn't parse: ask it to read the
  // stored file again, then re-read the library (throttled by a short wait so
  // a "Retry all" of hundreds re-reads once, not per file).
  const reparseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryParse = useCallback(
    async (row: DocumentRow) => {
      if (!selectedLibraryId || !row.doc_id) return;
      await reprocessDocument(selectedLibraryId, row.doc_id);
      if (reparseTimerRef.current) clearTimeout(reparseTimerRef.current);
      reparseTimerRef.current = setTimeout(() => {
        invalidateResolveGuard();
        void refreshLibraryInventory();
      }, 1500);
    },
    [selectedLibraryId, invalidateResolveGuard, refreshLibraryInventory],
  );
  const hubLibraryTabs = useMemo<HubListTabs | undefined>(() => {
    if (!selectedLibraryId) return undefined;
    const libraryName = activeLibraryForChips?.name ?? "library";
    return {
      live: liveUploadForLibrary,
      failedCount: failedItems(hubRows, uploadBatches).total,
      uploads: (
        <HubUploadsTab
          libraryName={libraryName}
          live={liveUploadForLibrary}
          batches={uploadBatches}
          rows={hubRows}
          onRetryFailed={retryFailedUploads}
        />
      ),
      failed: (
        <HubFailedTab
          libraryName={libraryName}
          rows={hubRows}
          batches={uploadBatches}
          onRetryUpload={(clientId) => {
            if (selectedLibraryId) uploadQueue.retryFailed(selectedLibraryId, clientId);
          }}
          onRemoveUpload={uploadQueue.removeFailed}
          onRetryParse={retryParse}
          onRemoveDoc={deleteHubDoc}
        />
      ),
    };
  }, [
    selectedLibraryId,
    activeLibraryForChips?.name,
    liveUploadForLibrary,
    uploadBatches,
    hubRows,
    retryFailedUploads,
    uploadQueue.retryFailed,
    uploadQueue.removeFailed,
    retryParse,
    deleteHubDoc,
  ]);

  // --- Table-artefact builder: tag tables → transform → preview → save CSV ---
  const [artefactBuilderOpen, setArtefactBuilderOpen] = useState(false);
  const [artefactBuilding, setArtefactBuilding] = useState(false);
  const [artefactBuildError, setArtefactBuildError] = useState<string | null>(null);
  const [artefactStages, setArtefactStages] = useState<ArtefactStageEvent[]>([]);
  const [artefactPreview, setArtefactPreview] = useState<TableArtefactPreview | null>(null);
  const [artefactSaving, setArtefactSaving] = useState(false);
  const [artefactSaveError, setArtefactSaveError] = useState<string | null>(null);

  const handleCreateArtefact = useCallback(() => {
    setArtefactBuildError(null);
    setArtefactStages([]);
    setArtefactPreview(null);
    setArtefactBuilderOpen(true);
  }, []);

  const handleCloseArtefact = useCallback(() => {
    setArtefactBuilderOpen(false);
    setArtefactPreview(null);
    setArtefactBuilding(false);
    setArtefactBuildError(null);
    setArtefactStages([]);
    setArtefactSaving(false);
    setArtefactSaveError(null);
  }, []);

  const handleBuildArtefact = useCallback(
    (tables: MentionAttachment[], instruction: string) => {
      if (!selectedLibraryId) return;
      setArtefactBuilding(true);
      setArtefactBuildError(null);
      setArtefactStages([]);
      streamTableArtefact(selectedLibraryId, tables, instruction, (event) => {
        setArtefactStages((prev) => [...prev, event]);
      })
        .then((preview) => {
          setArtefactPreview(preview);
          setArtefactBuilderOpen(false); // hand off to the preview modal
        })
        .catch((e: unknown) => {
          setArtefactBuildError(e instanceof Error ? e.message : "Could not build the table.");
        })
        .finally(() => setArtefactBuilding(false));
    },
    [selectedLibraryId],
  );

  const handleApproveArtefact = useCallback(
    (filename: string) => {
      if (!selectedLibraryId || !artefactPreview) return;
      if (artefactPreview.saved_file?.status === "saved") {
        // Already saved when built: keeping it is just closing the preview.
        setArtefactPreview(null);
        void refreshLibraryInventory();
        return;
      }
      setArtefactSaving(true);
      setArtefactSaveError(null);
      uploadContent(selectedLibraryId, filename, artefactPreview.csv, "text/csv")
        .then(() => {
          setArtefactSaving(false);
          setArtefactPreview(null); // closes the modal
          return refreshLibraryInventory();
        })
        .catch((e: unknown) => {
          setArtefactSaving(false);
          setArtefactSaveError(e instanceof Error ? e.message : "Could not save the CSV.");
        });
    },
    [selectedLibraryId, artefactPreview, refreshLibraryInventory],
  );

  const handleDiscardArtefact = useCallback(() => {
    const docId = artefactPreview?.saved_file?.asthra_doc_id;
    if (!selectedLibraryId || !docId) {
      setArtefactPreview(null);
      return;
    }
    setArtefactSaving(true);
    setArtefactSaveError(null);
    deleteDocument(selectedLibraryId, docId)
      .then(() => {
        setArtefactSaving(false);
        setArtefactPreview(null);
        return refreshLibraryInventory();
      })
      .catch((e: unknown) => {
        setArtefactSaving(false);
        setArtefactSaveError(e instanceof Error ? e.message : "Could not delete the saved table.");
      });
  }, [selectedLibraryId, artefactPreview, refreshLibraryInventory]);

  const contextWorkspace: ContextSlot = {
    value: activeWorkspace?.name ?? null,
    hint: "pick a workspace",
    // Workspace chip → opens WorkspacePicker dialog so the writer
    // can SWITCH workspaces. Previously routed to runDraftCommand
    // (sent them to the doctype picker), which mismatched the
    // writer's mental model — clicking a workspace label means
    // "change workspace", not "start drafting".
    onClick: idleAndAvailable ? openWorkspacePicker : undefined,
  };
  const contextDocCode = activeSelection?.docCode ?? tentativeDocType;
  // Private workspace templates carry an opaque ``WS_<hash>`` code; show the
  // template's display_name instead (live list first, then the remembered map).
  const contextDocTypeValue =
    contextDocCode && isPrivateTemplateCode(contextDocCode)
      ? privateTemplates?.find((t) => t.code === contextDocCode)?.display_name ||
        privateTemplateName(contextDocCode) ||
        contextDocCode
      : contextDocCode;
  const contextDocType: ContextSlot = {
    value: contextDocTypeValue,
    hint: "pick a doctype",
    // Phase 7.2 — doctype chip click opens DoctypePicker standalone
    // (no library auto-open). The pick lands as ``selectedDocCode``;
    // The draft orchestration picks it up when the writer starts drafting.
    onClick: idleAndAvailable ? openDoctypePickerStandalone : undefined,
    tentative: !activeSelection?.docCode && !!tentativeDocType,
  };
  const contextLibrary: ContextSlot = {
    value: committedLibraryName,
    hint: "pick a library",
    // Library chip → library-only flow. (Doctype chip still
    // routes to openDraftPicker since doctype selection implies
    // intent to start a draft.)
    onClick: idleAndAvailable ? openLibraryPicker : undefined,
    tentative: libraryIsTentative,
  };
  const contextActiveSection = state.activeSection?.sectionNumber
    ? {
        number: state.activeSection.sectionNumber,
        live: state.state === "mid-run" || state.state === "approval",
      }
    : null;

  // Spine-tab progress chip: "5/96" when both counts are known — the
  // denominator is the whole submission (falls back to the scoped spine
  // length when docConfig hasn't loaded), matching the drafts card.
  const spineCounter =
    showSpineTab && sections.length > 0 ? `${draftedCount}/${submissionTotalSections ?? sections.length}` : null;
  // Issues-tab badge: only the ledger's open count is authoritative (event
  // counters over-/under-count vs the open/resolved/dismissed/stale lifecycle),
  // so drive it off ``issuesOpenCount``. Hide the chip at zero — a "0" badge is
  // noise, not a call to action.
  const issuesCounter: string | null =
    showIssuesTab && issuesOpenCount != null && issuesOpenCount > 0 ? `${issuesOpenCount}` : null;

  // Proactively fetch the open-issue count for the badge whenever the Issues tab
  // is available, re-running when the ledger may have changed (issuesRefreshSignal
  // bumps after an approved edit). Race-guarded so a stale response can't clobber
  // a newer count. SubmissionIssuesTab reports in-tab dismiss/reopen via
  // onOpenCountChange, so the badge stays live between these fetches.
  const issuesCountReqRef = useRef(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: issuesRefreshSignal is a deliberate extra trigger — it is not read in the body, it exists to force a re-fetch after an approved edit.
  useEffect(() => {
    if (!showIssuesTab || !state.runId) {
      setIssuesOpenCount(null);
      setRegulatoryOpenByModule(null);
      return;
    }
    const reqId = ++issuesCountReqRef.current;
    void getRunIssues(state.runId, "open")
      .then((resp) => {
        if (issuesCountReqRef.current !== reqId) return;
        setIssuesOpenCount(resp.status_counts.open);
        // Bridge the ledger's per-module OPEN regulatory counts onto the spine.
        // ``open_counts`` is always the OPEN tally regardless of the status
        // filter, keyed by module_id (== SpineSection.id). A module absent from
        // the map has no open issues → 0, so this also clears a section whose
        // flags were resolved.
        const regByModule: Record<string, number> = {};
        for (const [mid, counts] of Object.entries(resp.open_counts ?? {})) {
          regByModule[mid] = counts?.regulatory ?? 0;
        }
        setRegulatoryOpenByModule(regByModule);
      })
      .catch(() => {
        if (issuesCountReqRef.current === reqId) setIssuesOpenCount(null);
      });
  }, [showIssuesTab, state.runId, issuesRefreshSignal]);

  // ---_contextActiveSection----------------------------------------------
  // Slash-command dispatcher. Declared here (late) because it closes
  // over chat, handleStopRun, planReviewActive — each of which is
  // defined earlier in the shell but after the place the dispatcher
  // used to live. The rail does the parsing; the shell is
  // authoritative on what each intent does.
  // ---------------------------------------------------------------------
  // Insert or replace a figure in the doc: fetch its bytes via the cached
  // /api/find-image proxy as a data-URI, then insert at cursor or replace
  // selected image via Office.js. When replaceMode is true and an image is
  // selected, replace it; otherwise insert as a new image. The result is
  // surfaced as an agent_note (the inserted image is the main signal).
  const handleImageInsert = useCallback(
    async (title: string, replaceMode?: boolean) => {
      const note = (text: string) =>
        dispatch({
          type: "EVENT_APPENDED",
          entry: {
            ts: Date.now(),
            actor: "asthra",
            sectionId: null,
            runId: state.runId,
            payload: { kind: "agent_note", text },
          },
        });
      if (!title || !imagesLibraryId) {
        note("Couldn't insert the figure — select a library first.");
        return;
      }
      try {
        const dataUri = await fetchImageDataUri(imagesLibraryId, title);
        let ok = false;
        if (replaceMode && imageSelected) {
          ok = await replaceSelectedImage(dataUri);
          if (!ok) {
            note("Couldn't replace the figure — no image is selected.");
          }
        } else {
          ok = await insertInlineImageAtSelection(dataUri);
          if (!ok) {
            note("Couldn't insert the figure — place your cursor in the document body and try again.");
          }
        }
        if (ok) {
          note(
            replaceMode && imageSelected
              ? `Replaced the selected image with "${title}".`
              : `Inserted "${title}" into the document.`,
          );
          setImageSelected(false);
        }
      } catch {
        note("Couldn't insert the figure — the image couldn't be fetched.");
      }
    },
    [state.runId, imagesLibraryId, imageSelected],
  );

  // The spine section the writer's cursor is in (falling back to the focused
  // section). Read on demand — the add-in keeps no ambient cursor state (it
  // froze Word on long drafts). Shared by every section-scoped chip.
  // Set when the last click-time section read hit the timeout, so the
  // caller can say "Word didn't respond" instead of "put your cursor…".
  const sectionReadTimedOutRef = useRef(false);
  const noSectionMessage = useCallback(
    () =>
      sectionReadTimedOutRef.current
        ? "Word didn't respond in time — try again in a moment."
        : "Put your cursor in a drafted section, then try again.",
    [],
  );

  // The section number the last cursor read found in Word, even when that
  // section is not in this run's spine (never drafted).
  const lastCursorSectionNumberRef = useRef<string | null>(null);
  const resolveCursorSection = useCallback(async () => {
    const log = getLogger("addin.chips");
    const startedAt = Date.now();
    // Through queueWordRead like every other read — the background refresh
    // is coalesced (refreshCursorSectionNumber), so the queue stays short.
    // Bounded: a Word read that never returns must not wedge the chip (and,
    // through the rail's in-flight guard, every other chip).
    let timedOut = false;
    const cursorNumber = await Promise.race([
      detectCursorSectionNumberWithFallback(queueWordRead, { maxWalk: CURSOR_SECTION_READ_MAX_WALK }),
      new Promise<null>((resolve) =>
        window.setTimeout(() => {
          timedOut = true;
          resolve(null);
        }, CURSOR_SECTION_READ_TIMEOUT_MS),
      ),
    ]);
    sectionReadTimedOutRef.current = timedOut && cursorNumber === null;
    lastCursorSectionNumberRef.current = cursorNumber;
    // This read walks further (CURSOR_SECTION_READ_MAX_WALK), so it finds
    // sections the scope chip's capped background probe can miss — share the
    // answer with the chip.
    if (cursorNumber && !isActiveRun(runStateRef.current)) {
      setCursorSectionNumber(cursorNumber);
    }
    log.info("chip.section_resolved", {
      section: cursorNumber,
      ms: Date.now() - startedAt,
      timedOut: sectionReadTimedOutRef.current,
    });
    return cursorNumber
      ? (sections.find((s) => s.number === cursorNumber) ?? null)
      : (sections.find((s) => s.id === focusedSectionId) ?? null);
  }, [sections, focusedSectionId]);

  // Full-section redraft of the section the cursor is in, via the
  // proposal-mode ad-hoc pipeline (``adhocDraftSection`` mode "redraft"):
  // the configured instructions stay in effect, ``guidance`` rides along,
  // and the result lands as an Approve/Reject card — no spine flip, SSOT
  // untouched until approve. Single path for both ``/regen <comments>``
  // and the "Regenerate this section" chip. Returns null on a clean start
  // or a writer-facing error.
  //
  // NB: this is NOT the Inspector's Regenerate button (useRegenFlow →
  // regenerateSection), which re-runs drafting and writes the section
  // directly without an approve step.
  const redraftCursorSection = useCallback(
    async (guidance: string): Promise<string | null> => {
      if (!state.runId || state.state !== "post-run") {
        return "Redrafting needs a completed run — finish or load one first.";
      }
      const targetSection = await resolveCursorSection();
      if (!targetSection) {
        return noSectionMessage();
      }
      if (targetSection.status === "drafting") {
        return "That section is mid-draft — wait for it to settle, then try again.";
      }
      // Mark the redraft in flight BEFORE the request: its terminal SSE event
      // clears this, and a fast redraft could otherwise finish before the
      // activity was recorded — leaving it (and the busy chips) stuck.
      handleChatRunActivity({
        moduleId: targetSection.id,
        kind: "regen",
        instruction: guidance,
      });
      try {
        // SSE reattach for terminal runs — same as the chat
        // draft_content path.
        handleChatRegenReconnect();
        const started = await adhocDraftSection(state.runId, targetSection.id, {
          instruction: guidance,
        });
        const scopeNote = sourceScopeNote(started?.source_scope, `§${targetSection.number}`);
        if (scopeNote) {
          dispatch({
            type: "EVENT_APPENDED",
            entry: {
              ts: Date.now(),
              actor: "asthra",
              sectionId: targetSection.id,
              sectionLabel: `§${targetSection.number}`,
              runId: runIdRef.current,
              payload: { kind: "agent_note", text: scopeNote },
            },
          });
        }
        return null;
      } catch (err) {
        handleChatRunActivity(null);
        const detailObj = (err as { detail?: { error?: string } }).detail;
        if (detailObj?.error === "no_draft_to_redraft") {
          return "That section hasn't been drafted yet — nothing to redraft.";
        }
        const detail = err instanceof Error ? err.message : String(err);
        return `Couldn't start the redraft: ${detail}`;
      }
    },
    [state.runId, state.state, resolveCursorSection, noSectionMessage, handleChatRegenReconnect, handleChatRunActivity],
  );

  // "Regenerate this section" chip — the SAME workflow as the Inspector's
  // Regenerate button on the Spine: the RegenOne dialog (optional steering
  // instructions) → handleRegenConfirm → regenerateSection. Opened for the
  // cursor's section; switches to the Spine, where the regeneration shows.
  const handleRegenerateSectionChip = useCallback(async (): Promise<string | null> => {
    if (!state.runId || !regenEnabled) {
      return "Regenerating needs a completed run — finish or load one first.";
    }
    const target = await resolveCursorSection();
    if (!target) return noSectionMessage();
    if (target.status === "drafting") {
      return "That section is mid-draft — wait for it to settle, then try again.";
    }
    // Same eligibility as the Inspector's Regenerate button.
    if (target.status !== "drafted" && target.status !== "needs_approval" && target.status !== "flagged") {
      return `§${target.number} hasn't been drafted yet — nothing to regenerate.`;
    }
    handleViewModeChange("spine");
    openRegenFor(target.id);
    return null;
  }, [state.runId, regenEnabled, resolveCursorSection, noSectionMessage, handleViewModeChange, openRegenFor]);

  // "Insert from source" chip — the section panel's Insert from source for the
  // cursor section: pick a source document, the section or table in it, and
  // where it goes; the copy arrives as a change to review.
  const handleInsertFromSourceChip = useCallback(async (): Promise<string | null> => {
    if (!state.runId || state.state !== "post-run") {
      return "Inserting from a source needs a completed run — finish or load one first.";
    }
    const target = await resolveCursorSection();
    const drafted =
      target && (target.status === "drafted" || target.status === "needs_approval" || target.status === "flagged");
    // A section this run never drafted is still replaced — in Word only.
    const number = target?.number ?? lastCursorSectionNumberRef.current;
    if (!number) return noSectionMessage();
    setCopyTarget({
      moduleId: drafted && target ? target.id : "none",
      number,
      title: target?.title ?? "",
      placement: "replace_section",
    });
    return null;
  }, [state.runId, state.state, resolveCursorSection, noSectionMessage]);

  // "Replace with text from a source" chip — any section or table of any
  // source document in place of the highlight. Works in any section, even one
  // this run never drafted (then only Word changes, and the writer is told).
  const handleReplaceFromSourceChip = useCallback(async (): Promise<string | null> => {
    if (!state.runId) return "Replacing from a source needs a draft — load one first.";
    const text = (chipSelectionText ?? "").trim();
    if (!text) return "Highlight the text to replace first.";
    const target = await resolveCursorSection();
    setCopyTarget({
      moduleId: target?.id ?? "none",
      number: target?.number ?? "",
      title: target?.title ?? "",
      placement: "replace_text",
      highlight: chipSelectionText ?? text,
    });
    return null;
  }, [state.runId, chipSelectionText, resolveCursorSection]);

  // "Resolve flags" chip — the Issues tab's bulk "Resolve selected" scoped to
  // the cursor section: every open issue in it goes to the same
  // Fix issues window (issues → sources → Start → resolve pass on the spine,
  // fixes land as proposals to review).
  const handleResolveSectionFlagsChip = useCallback(async (): Promise<string | null> => {
    if (state.state === "mid-run") return RESOLVE_WHILE_DRAFTING_MESSAGE;
    if (!state.runId || state.state !== "post-run") {
      return "Fixing flags needs a completed run — finish or load one first.";
    }
    const target = await resolveCursorSection();
    if (!target) return noSectionMessage();
    try {
      const { issues } = await getSectionIssues(state.runId, target.id, "open");
      const open = issues.filter((i) => i.status === "open");
      if (open.length === 0) return `§${target.number} has no open flags.`;
      // Same gate as the Issues tabs (shared mid-run refusal).
      const opened = openResolveWizard([
        {
          module_id: target.id,
          number: target.number,
          title: target.title,
          issue_ids: open.map((i) => i.id),
          issues: open.map(toFixIssue),
        },
      ]);
      return opened ? null : RESOLVE_WHILE_DRAFTING_MESSAGE;
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      return `Couldn't load the flags for §${target.number}: ${detail}`;
    }
  }, [state.runId, state.state, resolveCursorSection, noSectionMessage, openResolveWizard]);

  // "See all flags" chip — the cursor section's Issues tab in the Inspector
  // (same surface the spine's flag badge opens). The Inspector only renders
  // in the Spine view, so switch there first — the chips live in the chat
  // view, where opening it alone showed nothing.
  const handleSeeSectionFlagsChip = useCallback(async (): Promise<string | null> => {
    if (!state.runId) return "Flags need a run — load one first.";
    const target = await resolveCursorSection();
    if (!target) return noSectionMessage();
    handleViewModeChange("spine");
    handleOpenReview(target.id);
    return null;
  }, [state.runId, resolveCursorSection, noSectionMessage, handleViewModeChange, handleOpenReview]);

  // ───────────────────────────────────────────────────────────────────
  // Save button — flushes pending session patches and any buffered
  // write-back-bridge edits in one go. Gives the writer a single
  // affordance for "all my changes are committed" without depending on
  // debounce windows. ``saveState`` drives the TabBar label so a brief
  // "Saved" confirmation flashes after a successful flush.
  // ───────────────────────────────────────────────────────────────────
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  // Auto-save (the watcher) state, shown on the same button: "Saving…" while a
  // background save runs, "Not saved" + the reason when it skipped a section.
  const autoSaving = useSyncStatus();
  const slowWordWrite = useSlowWordWrite();
  const autoSaveNotice = useSaveNotice();
  const [saveError, setSaveError] = useState<string | null>(null);
  // Live progress for the floating SaveProgressCard: which checkpoint is in
  // flight (0 prepare → 1 read → 2 save → 3 issues; 4 = all done) and the 0–100
  // percent driving the progress bar. The whole save is one bulk request now, so
  // the bar is eased optimistically during the in-flight save and snapped to 100
  // on completion (no per-section counter).
  const [saveStepIndex, setSaveStepIndex] = useState(-1);
  const [savePercent, setSavePercent] = useState(0);
  // Re-entrancy guard: the Save button double-click AND the post-approve
  // auto-save can otherwise overlap, interleaving Word reads.
  const saveBusyRef = useRef(false);
  const handleSave = useCallback(async () => {
    if (saveBusyRef.current) return;
    saveBusyRef.current = true;
    const _saveT0 = performance.now();
    // Braced body so the console call is a statement rather than the
    // arrow's return value — see the same note in writeBackBridge.ts.
    // A concise arrow survives the production console strip.
    const _sstep = (m: string): void => {
      console.info(`[Save·shell +${Math.round(performance.now() - _saveT0)}ms] ${m}`);
    };
    _sstep("Save clicked");
    setSaveState("saving");
    setSaveError(null);
    setSaveStepIndex(0); // Preparing
    setSavePercent(8);
    try {
      flushAllPendingSaves();
      // Suspend the typing auto-save (syncEngine) for the duration: it runs its
      // own whole-document Word read OUTSIDE the single-writer queue, so a
      // keystroke ≤1.5 s before the click would race the Save scan with a
      // second full read. Save is a complete sweep, so nothing pending is lost.
      // Resumed in ``finally`` (same pair the Load path uses).
      pauseSync();
      _sstep("flushed pending auto-saves + paused typing auto-save; loading writeBackBridge");
      const { captureAndSaveSections } = await import("./services/writeBackBridge");
      // SSOT write-through (2026-07-14): capture the live Word content of every
      // inserted section and persist the CHANGED ones straight to Mongo —
      // markdown to ``draft_full`` (the markdown-only SSOT, 2026-08-05; the
      // formatting overlay was dropped as the main Save hang). Changed-sections-
      // only, last-write-wins; the ledger reconcile marks writer-deleted markers
      // as resolved. The document is scanned ONCE (text only) behind the write
      // queue (via ``enqueueWordWrite``) so the read can't interleave with
      // in-flight section inserts; the per-section ``/edit`` writes run outside
      // the lock. ``onProgress`` drives the floating SaveProgressCard.
      if (state.runId) {
        const runId = state.runId;
        // Hand the live spine's section numbers so the SSOT fetch and the Word
        // document scan run CONCURRENTLY (halves the pre-save wait). Read via
        // ref so handleSave isn't rebuilt on every spine update.
        const knownSectionNumbers = sectionsRef.current
          .map((s) => s.number)
          .filter((n): n is string => !!n && n !== "TITLE");
        const counts = await captureAndSaveSections(runId, {
          enqueueWordWrite,
          knownSectionNumbers,
          onProgress: (p) => {
            if (p.phase === "reading") {
              setSaveStepIndex(1); // Reading your document
              setSavePercent(p.percent);
            } else {
              setSaveStepIndex(2); // Saving changes
              setSavePercent(p.percent);
            }
          },
        });
        console.info(
          `[Save] sections → SSOT: ${counts.saved} saved, ${counts.skippedUnchanged} unchanged, ` +
            `${counts.skippedMissing} missing, ${counts.skippedTrackChanges} track-changes, ` +
            `${counts.conflicted} changed-by-someone-else, ${counts.failed} failed`,
        );
        // Failure surfacing (stabilization, 2026-07-24): a Save that could
        // not compare (fetch failed) or dropped sections must NOT flash
        // "Saved" — that reads as durability the SSOT doesn't have.
        if (counts.captureFailed) {
          // Word-side capture threw (e.g. a RichApi InvalidArgument while
          // reading section bodies) — the request never reached the server, so
          // don't mislabel it as a connectivity problem.
          setSaveError("Couldn't read the document to save — please try again. If it persists, reload the doc.");
          setSaveState("error");
          return;
        }
        if (counts.fetchFailed) {
          setSaveError("Save couldn't reach the server — nothing was compared. Try again.");
          setSaveState("error");
          return;
        }
        // Reported before ``failed``, and worded nothing like it. A failure means
        // *try again*; a conflict means somebody else's work is in that section
        // and yours is not. Telling a writer to retry a conflict is how the other
        // edit gets destroyed on the second press — which, before this Save
        // presented a version guard at all, is what happened on the first.
        if (counts.conflicted > 0) {
          const many = counts.conflictedSections.length !== 1;
          setSaveError(
            `Section${many ? "s" : ""} ${counts.conflictedSections.join(", ")} ` +
              `${many ? "were" : "was"} changed by someone else while you were editing, so ` +
              `${many ? "they were" : "it was"} not overwritten. Your text is still in this ` +
              `document — reload to see their version before saving again.`,
          );
          setSaveState("error");
          return;
        }
        if (counts.failed > 0) {
          setSaveError(
            `Save failed for section${counts.failedSections.length === 1 ? "" : "s"} ` +
              `${counts.failedSections.join(", ")} — content is still in the doc; try Save again.`,
          );
          setSaveState("error");
          return;
        }
        if (counts.skippedMissing > 0) {
          // Benign but worth telling the writer: those sections weren't
          // found in the open doc (renamed heading / never inserted), so
          // their SSOT copy was left untouched.
          console.warn(`[Save] ${counts.skippedMissing} section(s) not found in the open doc — SSOT left as-is`);
        }
        if (counts.skippedTrackChanges > 0) {
          // Track Changes is ON, so ``paragraph.text`` can fold in deleted text;
          // without an OOXML capture to derive the accepted text those sections
          // were left as-is rather than saving revision-polluted markdown.
          console.warn(`[Save] ${counts.skippedTrackChanges} section(s) skipped — Track Changes is on`);
          setSaveError(
            `${counts.skippedTrackChanges} edited section${counts.skippedTrackChanges === 1 ? "" : "s"} couldn't be saved while Track Changes is on — accept or reject your changes (or turn Track Changes off), then Save again.`,
          );
          setSaveState("error");
          return;
        }
        if (counts.skippedFigurePending > 0) {
          // A picture the writer inserted is still loading in Word — saving the
          // section now would drop it, so it was left unsaved.
          setSaveError(
            `${counts.skippedFigurePending} section${counts.skippedFigurePending === 1 ? " wasn't" : "s weren't"} saved because a picture is still loading in Word — wait a moment, then Save again.`,
          );
          setSaveState("error");
          return;
        }
        // The server reconciled the issue ledger on each ``/edit`` (writer-
        // deleted markers → resolved, new ones → open); re-fetch so the Issues
        // badge + Data Gaps panel reflect it without waiting for another
        // trigger (matches the accepted-edit path's refresh).
        setSaveStepIndex(3); // Updating issues
        setSavePercent(92);
        setIssuesRefreshSignal((n) => n + 1);
        _sstep(`persist done: ${counts.saved} saved, ${counts.failed} failed — refreshing issues`);
      }
      setSaveStepIndex(4); // all done → "Saved ✓"
      setSavePercent(100);
      setSaveState("saved");
      // The whole document is now saved — an older auto-save notice is moot.
      clearSaveNotice();
      _sstep("Save complete ✓");
      window.setTimeout(() => {
        setSaveState("idle");
        setSaveStepIndex(-1);
      }, 1500);
    } catch (err) {
      console.warn("[Save] flush failed:", err);
      setSaveError("Save failed — try again.");
      setSaveState("error");
    } finally {
      resumeSync(state.runId);
      saveBusyRef.current = false;
    }
  }, [state.runId, enqueueWordWrite]);

  // Optimistic ease for the Save bar: the persist is ONE bulk request with no
  // intermediate acks, so while the "Saving changes" step is in flight nudge the
  // bar toward ~88% (it snaps to 100 on completion). Cheap timer, cleared when
  // the step advances or the save ends.
  useEffect(() => {
    if (saveState !== "saving" || saveStepIndex !== 2) return;
    const id = window.setInterval(() => {
      setSavePercent((p) => (p < 88 ? Math.min(88, p + 3) : p));
    }, 160);
    return () => window.clearInterval(id);
  }, [saveState, saveStepIndex]);

  // Section-scoped auto-save: an accepted highlight edit or full-section
  // proposal marks just its own section for the auto-save watcher, which saves
  // it (markdown → ``draft_full``) once the write queue is idle and the
  // debounce settles — so several approvals in a row are one save, and the
  // save shares the watcher's skipped-section notice.
  const autoSaveModule = useCallback(
    async (moduleId: string | null) => {
      if (!markSectionChanged(state.runId, moduleId)) {
        // No module id to scope to — fall back to the full-document sweep so
        // an approval never silently skips persistence.
        void autoSaveRef.current?.();
      }
    },
    [state.runId],
  );

  // Post-approve auto-save: chat-driven section changes (adhoc /
  // redraft / edit proposals + highlight edits) trigger a Save
  // automatically so the SSOT stays proactively synced. Ref
  // pattern because the decision handlers are declared ~2600 lines
  // above these callbacks (same as adhocProposalOfferRef).
  useEffect(() => {
    autoSaveRef.current = handleSave;
    autoSaveModuleRef.current = autoSaveModule;
    return () => {
      autoSaveRef.current = null;
      autoSaveModuleRef.current = null;
    };
  }, [handleSave, autoSaveModule]);

  const topBarChips: ContextChip[] = [
    {
      key: "doctype",
      value: contextDocType.value ?? "Document type",
      title: contextDocType.value ? `Document type: ${contextDocType.value}` : "Pick a document type",
      onClick: openDoctypePickerStandalone,
    },
    {
      key: "library",
      value: activeLibraryForChips?.name ?? "Library",
      title: !hasDocType ? "Select the Document Type first" : (activeLibraryForChips?.name ?? "Pick a library"),
      onClick: () => openSlideOver(selectedLibraryId ? "list" : "library-list"),
      disabled: !hasDocType,
    },
    {
      key: "workspace",
      value: activeWorkspace?.name ?? "Workspace",
      title: activeWorkspace ? `Workspace: ${activeWorkspace.name}` : "Pick a workspace",
      onClick: openWorkspacePicker,
    },
  ];

  return (
    <div className={styles.root}>
      <TopBarC
        mode={appConfig?.sandbox ? "sandbox" : "live"}
        onModeToggle={state.state === "idle" || state.state === "post-run" ? handleModeToggle : undefined}
        modeToggling={modeToggling}
        // Phase 4 — visible logout affordance. Re-uses the same
        // ``handleLogout`` previously reachable only via the
        // ``/logout`` slash command.
        onLogout={handleLogout}
        // Billing & usage surface — plan, provider portal, payment history.
        onOpenBilling={() => setBillingOpen(true)}
        onChangePassword={() => setChangePasswordOpen(true)}
        // Identity label so the writer can confirm WHICH account is
        // signed in. Sourced from App via prop drilling — App owns
        // the auth SSOT (getSessionUsername); the shell just relays.
        userIdentifier={userIdentifier ?? null}
        // LIVE/SANDBOX pill + model-backend switcher — internal
        // plumbing, hidden unless show.internal-tools says otherwise.
        showInternalTools={showInternalTools}
        // "DEV" instead of "LIVE" on the pill when this bundle is served by
        // the Vite dev server (pnpm dev) rather than the compose build.
        devBuild={isDevBuild}
        // Live token counter in header. Shows real-time accumulation
        // during active runs; displays zero when idle.
        liveTokens={liveTokens}
        // Context chips (doctype · library · workspace) per the design's
        // context bar. Omitted during full-body takeovers (starting overlay /
        // section picker / plan review) and on Home. Library chip opens
        // LibraryHubSlideOver.
        chips={!takeoverActive && !isHomeSceneActive ? topBarChips : undefined}
        onDocumentSettings={openDoctypePickerStandalone}
        documentSettingsHint={
          contextDocType.value
            ? [contextDocType.value, selectedRegion].filter(Boolean).join(" · ")
            : null
        }
        onShowIntro={() => setIntroOpen(true)}
        onSave={handleSave}
        saveDisabled={isActiveRun(state.state) || saveState === "saving"}
      />
      {/* The section detail (Inspector) covers the tab row too, as on the
          design's section boards; Back returns to the Spine tab. */}
      {!takeoverActive &&
        (showSpineTab || showIssuesTab || showDraftsTab || showImagesTab) &&
        !isHomeSceneActive &&
        !(activeTab === "spine" && focusedSectionId) && (
        <TabBar
          activeTab={activeTab}
          onTabChange={(tab) =>
            handleViewModeChange(
              tab === "spine"
                ? "spine"
                : tab === "issues"
                  ? "issues"
                  : tab === "drafts"
                    ? "drafts"
                    : tab === "images"
                      ? "images"
                      : "timeline",
            )
          }
          showSpine={showSpineTab}
          showIssues={showIssuesTab}
          issuesCounter={issuesCounter}
          showDrafts={showDraftsTab}
          showImages={showImagesTab}
          spineCounter={spineCounter}
          // Phase 9.1 — pulsing indicator on the Spine tab while a
          // run is drafting. Mid-run is the only state that emits
          // continuous activity; approval pauses progress (writer
          // intervention required) and post-run is terminal — both
          // suppress the dot. Drives off the same lifecycle
          // discriminator the rest of the shell uses.
          spineActive={isActiveRun(state.state)}
          onSave={handleSave}
          saveBusy={saveState === "saving"}
          saveLabel={
            saveState === "saved"
              ? "Saved"
              : saveState === "error"
                ? "Save failed — retry"
                : autoSaving
                  ? "Saving…"
                  : autoSaveNotice
                    ? "Not saved"
                    : "Save"
          }
          saveError={saveState === "error" ? saveError : autoSaveNotice ? describeSaveNotice(autoSaveNotice) : null}
          // Block Save while a live draft is in flight — snapshotting a
          // half-built draft would leave the persisted state mid-section.
          // ``isActiveRun`` covers mid-run + pausing; idle/approval/
          // paused/post-run leave the button enabled.
          saveDisabled={isActiveRun(state.state)}
          saveDisabledTooltip="Come back when the draft is complete to save work on top of it!"
        />
      )}
      {introOpen && (
        <div className="as-intro-overlay">
          <FirstRun onContinue={() => setIntroOpen(false)} />
        </div>
      )}
      {/* "Updating the document" pill for a slow add-in write. Not where the
          pane already narrates it: the Save / Load cards, a run's spine. */}
      {slowWordWrite !== null &&
        saveState === "idle" &&
        loadDocStepIndex === -1 &&
        !isActiveRun(state.state) && <WordBusyStrip label={slowWordWrite} />}
      {/* Floating Save progress card — narrates the whole-document sweep
          (prepare → read → save → update issues → done) so a multi-second Save
          is legible instead of a frozen "Saving…" label. */}
      {saveState !== "idle" && (
        <SaveProgressCard
          steps={buildSaveSteps(saveStepIndex)}
          error={saveState === "error" ? saveError : null}
          done={saveState === "saved"}
          percent={savePercent}
          onDismiss={() => {
            setSaveState("idle");
            setSaveStepIndex(-1);
            setSavePercent(0);
            setSaveError(null);
          }}
        />
      )}
      {/* Floating Load progress card — narrates the spine "Load doc" sweep
          (prepare → download → rebuild → restore → spine) so a multi-second
          load is legible instead of a dimmed button. */}
      {loadDocStepIndex !== -1 && (
        <LoadProgressCard
          steps={buildLoadSteps(loadDocStepIndex)}
          error={loadDocError}
          done={loadDocDone}
          onDismiss={() => {
            setLoadDocStepIndex(-1);
            setLoadDocError(null);
            setLoadDocDone(false);
          }}
        />
      )}
      {/* Floating "Build on this document" progress card — narrates the custom
          import sweep (read outline → create project → build spine → load
          sections) after the writer picks a doctype + library. Renders in place
          of the live-run booting card, which the custom flow suppresses. */}
      {customImportStep !== -1 && (
        <LoadProgressCard
          steps={buildCustomImportSteps(customImportStep)}
          error={customImportError}
          done={customImportDone}
          header="Building your document"
          errorHeader="Couldn't build your document"
          doneHeader="Ready"
          onDismiss={() => {
            setCustomImportStep(-1);
            setCustomImportError(null);
            setCustomImportDone(false);
          }}
        />
      )}
      {/* Bulk resolve control bar — pause/stop/resume a resolve pass. Separate
          from SpineControlBar (which controls the drafting run) because a
          resolve pass never enters the reducer's mid-run state. */}
      {!takeoverActive && (activeTab === "spine" || activeTab === "issues" || resolveError) && (
        <ResolveControlBar
          phase={resolveIssuesFlow.phase}
          progress={resolveIssuesFlow.progress}
          remainingCount={resolveIssuesFlow.remainingCount}
          onPause={resolveIssuesFlow.pause}
          onStop={resolveIssuesFlow.stop}
          onResume={resolveIssuesFlow.resume}
          onDiscard={resolveIssuesFlow.discard}
          error={resolveError}
          onDismissError={dismissResolveError}
          reviewCount={reviewStripCount}
          onReview={() => {
            setReviewStripHiddenFor(resolveIssuesFlow.summary);
            handleViewModeChange("issues");
            setReviewWindowSignal((n) => n + 1);
          }}
          onDismissReview={() => setReviewStripHiddenFor(resolveIssuesFlow.summary)}
          onShowProgress={fixPassTargets && resolveWizardTargets === null ? showFixProgress : undefined}
        />
      )}
      {/* Single-section regen control bar — pause/stop/resume a regenerate.
          Separate from SpineControlBar because a regen runs on a completed run
          and the reducer stays in post-run. */}
      {!takeoverActive && !(activeTab === "spine" && regenActivity) && (
        <RegenControlBar
          activity={regenActivity}
          onPause={pauseRegen}
          onStop={stopRegen}
          onDraftNow={draftNowRegen}
          onResume={resumeRegen}
          onDiscard={discardPausedRegen}
        />
      )}
      {/* Variant D Phase 3 + Phase 4 — persistent library bar between
          the tab strip and chat content. Renders on the Chat tab when
          there is EITHER a committed activeSelection OR a lined-up
          library (selectedLibraryId resolves to a known library
          object). Phase 4 added the lined-up case so the bar tracks
          the same in-progress signal the ContextStrip's tentative
          chip already surfaces; ``tentative`` styles the bar
          accordingly. Hidden during full-body takeovers for the
          same reason TabBar/ContextStrip hide. */}

      {/* Mode-toggle error banner. Renders when the backend rejects
          a sandbox/live flip (typically the 409 active-run guard).
          Without this, the pill silently doesn't change and the
          writer thinks it's broken. Dismissable. */}
      {modeToggleError && (
        <div
          role="alert"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            background: "var(--danger-soft)",
            borderBottom: "1px solid color-mix(in srgb, var(--danger) 35%, transparent)",
            color: "var(--danger)",
            fontSize: "11.5px",
            fontFamily: fonts.body,
            flexShrink: 0,
          }}
        >
          <span style={{ flex: 1 }}>{modeToggleError}</span>
          <button
            type="button"
            onClick={() => dismissModeToggleError()}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--danger)",
              cursor: "pointer",
              fontSize: "14px",
              padding: "0 4px",
            }}
            aria-label="Dismiss mode toggle error"
          >
            ×
          </button>
        </div>
      )}
      {planModeToggleError && (
        <div
          role="alert"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            background: "var(--danger-soft)",
            borderBottom: "1px solid color-mix(in srgb, var(--danger) 35%, transparent)",
            color: "var(--danger)",
            fontSize: "11.5px",
            fontFamily: fonts.body,
            flexShrink: 0,
          }}
        >
          <span style={{ flex: 1 }}>{planModeToggleError}</span>
          <button
            type="button"
            onClick={() => dismissPlanModeToggleError()}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--danger)",
              cursor: "pointer",
              fontSize: "14px",
              padding: "0 4px",
            }}
            aria-label="Dismiss plan mode toggle error"
          >
            ×
          </button>
        </div>
      )}
      {/* Stop-unconfirmed warning. Raised by handleStopRun when the DELETE
          cancel could not be confirmed against the backend after retries —
          the run may still be drafting server-side. Amber (warn), not red:
          the local UI did reset, but the writer should retry the stop. */}
      {stopWarning && (
        <div
          role="alert"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            background: "var(--warning-soft)",
            borderBottom: "1px solid color-mix(in srgb, var(--warning) 40%, transparent)",
            color: "var(--warning)",
            fontSize: "11.5px",
            fontFamily: fonts.body,
            flexShrink: 0,
          }}
        >
          <span style={{ flex: 1 }}>{stopWarning}</span>
          <button
            type="button"
            onClick={() => setStopWarning(null)}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--warning)",
              cursor: "pointer",
              fontSize: "14px",
              padding: "0 4px",
            }}
            aria-label="Dismiss stop warning"
          >
            ×
          </button>
        </div>
      )}
      {body}
      {/* 2026-05-06 — bridge-driven user-input requests (the agent
          asking for approval before doing something — e.g.
          LiteratureSearchGate from "Find newer evidence" chip,
          WriteGate from drafting, future request_user_input calls).
          Sourced from useUserInputBridge.pendingApproval, NOT from
          state.pendingApproval (which is for section-write approvals
          rendered inside TimelineView).

          # DECISION: render at shell-level, above CommandRail.
          # WHY: section-write approvals (state.pendingApproval) are
          #   already rendered inside TimelineView/LoomSpine. But
          #   agent-initiated user_input requests (literature search
          #   approval, generic free_text, future request_user_input
          #   kinds) come from useUserInputBridge — a distinct state
          #   source. Wiring them inside TimelineView would cross-
          #   couple two distinct approval pathways and violate SRP.
          #   Rendering at shell level above the rail keeps the
          #   boundary clean: TimelineView owns section-write
          #   approvals; this row owns bridge-driven approvals. The
          #   placement above the rail also sits the textarea right
          #   where the writer's next action is required.
          # ALTERNATIVE: route bridge approvals through state.* via
          #   a reducer action — rejected; doubles the SSOT (bridge
          #   already owns the state, reducer would mirror it).
          # DATE: 2026-05-06

          UserInputInlineRow returns null when ``request`` is null,
          so no chrome leaks into the layout when nothing's pending.
          Write approvals with a parseable payload render IN the chat
          thread through the shared ProposalCard (writeApprovalNode)
          instead — suppressed here so the decision has one surface. */}
      <UserInputInlineRow
        request={writeApprovalHandledInThread ? null : pendingApproval}
        onApprove={submitApproval}
        onReject={rejectApproval}
      />
      {/* DisclosureBanner retired with Spec 29 Phase 1; its copy
          relocates into the agent's greeting message (Phase 3).
          CommandRail is the chat surface below the body.

          # DECISION (2026-05-06): hide CommandRail ONLY when the
          # LoomSpine surface is actually rendering (writer is on
          # the Spine tab during an active or completed run). At
          # idle / plan-review / pre-run, the rail must be visible
          # so the writer can type /draft, /library, or chat with
          # the assistant. The initial value of state.viewMode is
          # "spine" (see state.ts:289) — gating on viewMode alone
          # would hide the rail at idle and break the welcome
          # surface. Gating on (viewMode === "spine" && runId) ties
          # the hide condition to the actual LoomSpine render, since
          # LoomSpine is only mounted when state.state is run-
          # related (and runId is non-null in those states).
          # WHY: the rail's affordances are all conversational —
          # slash commands, chat send, suggestion chips, citation
          # popover. None belong on the spine view (section-status
          # canvas). But idle / plan-review / post-run pre-tab
          # surfaces still need the rail.
          # ALTERNATIVE: change initialState.viewMode from "spine"
          # to "timeline". Rejected — would change first-tab default
          # for active runs too, which might surprise writers who
          # expect spine-first when a run starts.
          # DATE: 2026-05-06
          #
          # The rail is ALSO hidden on the run-agnostic browsing
          # tabs — "drafts" and "images" — where it is not a chat
          # surface and would otherwise render over the panel body.
          # Likewise on "issues" (2026-09-29): the chat status line and
          # selection chips leaked under the issue list; chat lives in
          # the Chat tab only. */}
      {!(state.viewMode === "spine" && state.runId) &&
        state.viewMode !== "issues" &&
        state.viewMode !== "drafts" &&
        state.viewMode !== "images" &&
        !isHomeSceneActive && (
          <CommandRail
            ref={commandRailRef}
            assistantMessage={assistantMessage}
            restoreHydrationProgressPct={restoreHydrationProgressPct}
            onRestoreHydrationSync={handleResyncHydration}
            // Spec 26 C2 — Inspector wins; cursor section fills in when no
            // inspector is open so the chip reads §X.X.X while the writer
            // browses the doc post-draft. Cursor section is sourced by a
            // debounced + run-gated subscriber that calls the single-sync
            // detectCursorSectionLight (ASTHRA_SEC_* bookmark lookup), so it
            // does NOT reintroduce the old useDocumentCursor freeze.
            // handleSendChat still does a fresh on-demand cursor read at
            // chat-send time as ground truth.
            // The RAW cursor section — the rail turns it into the chat scope, so
            // the post-run "first drafted section" fallback (the title page)
            // must not stand in for an unresolved cursor: selecting text in
            // §9.1 targeted "Title Page". The fallback stays on the TOC
            // highlight only (tocActiveSectionNumber).
            inspectorSectionNumber={commandRailInspectorSectionNumber}
            cursorSectionNumber={cursorSectionNumber}
            onScopeChipClick={docConfig ? handleScopeChipClick : undefined}
            // Spec 26 C6 — citation popover driven by useSelectionCitations.
            citations={selectionCitations.citations}
            citationsLoading={selectionCitations.isLoading}
            citationsError={selectionCitations.error}
            citationsFetchedText={selectionCitations.fetchedText}
            citationsStatus={selectionCitations.status}
            citationsMessage={selectionCitations.message}
            citationsTransactionIdsUsed={selectionCitations.transactionIdsUsed}
            citationsDerivations={selectionCitations.derivations}
            // 2026-05-06 — chips restoration. fetchNow drives both the
            // SuggestionChips "View citations for selected text" chip and
            // the CitationPopover error-state Retry button. Same
            // primitive — both are "fetch this selection's citations now"
            // intents, just surfaced from different UI affordances.
            // Hidden (not a dead button) when the document has no
            // (trial, session) identity — the lookup would silently no-op.
            onCite={selectionCitations.available ? selectionCitations.fetchNow : undefined}
            onCitationsDismiss={selectionCitations.clear}
            // Gates the SuggestionChips render in the rail. Pre-generation
            // (picker / setup screens) there is no drafted content to act
            // on. Once any section has been drafted, the chips apply.
            // Status set matches the existing post-draft predicate at L1927.
            hasDraftedContent={hasDraftedContent}
            // Context chips — selection actions read the live highlight;
            // section actions (redraft / edit) need a completed run.
            chipSelectionText={chipSelectionText}
            sectionActionsEnabled={state.state === "post-run" && Boolean(state.runId)}
            chipActionsBusy={chipActionsBusy}
            onRegenerateSection={handleRegenerateSectionChip}
            onInsertFromSource={handleInsertFromSourceChip}
            onReplaceFromSource={handleReplaceFromSourceChip}
            onResolveSectionFlags={handleResolveSectionFlagsChip}
            onSeeSectionFlags={handleSeeSectionFlagsChip}
            onShowChat={() => handleViewModeChange("timeline")}
            // Always wired: useChatBridge's sendChat
            // returns an honest error when docx_path isn't resolved yet
            // (idle / pre-run), so the writer sees what's needed rather
            // than nothing. Mid-run chat is still specced but blocked
            // by the backend read-only filter (Spec 28); useChatBridge's
            // internal guard returns the right copy there too.
            onSendChat={handleSendChat}
            chatStreamingStatus={composerStreamingStatus}
            // Composer Stop button — wired whenever something STOPPABLE is in
            // flight: a chat reply, a highlight edit, or an ad-hoc draft. Left
            // undefined for a regen (the retired chat-regeneration path), which
            // only ``cancelRun`` could reach and that would kill the whole run;
            // the button then reads busy-but-disabled rather than lying.
            onStopChat={stoppableActivity ? handleStopComposer : undefined}
            // Bridge lifecycle drives the rail's status pill + the soft
            // "queued" hint when the writer submits before the WS has
            // connected. ``queuedMessage`` is non-null only while a held
            // message is waiting for the next connect.
            bridgeStatus={bridgeStatus}
            chatQueuedMessage={chat.queuedMessage}
            // Spec 33 — v3 planner task list + progress. Empty/zero when
            // the v3 agent isn't engaged or hasn't emitted ``plan_ready``
            // yet, in which case CommandRail hides the strip entirely.
            chatTasks={chat.tasks}
            chatCompletedTasks={chat.completedTasks}
            chatTotalTasks={chat.totalTasks}
            chatCurrentTaskTitle={chat.currentTaskTitle}
            chatDraftingStatus={chat.draftingStatus}
            onInputFocus={() => {
              void emptyDocWatcherRef.current?.check();
              void captureSelection();
              void refreshCursorSectionNumber();
            }}
            selectionContext={capturedSelection}
            onDismissSelectionContext={dismissSelection}
            // State-aware: "stop" (mid-run / approval) → handleStopRun
            // cancels the backend run; "reset" (post-run) → handleReset
            // is just local cleanup.
            onStop={state.state === "mid-run" || state.state === "approval" ? handleStopRun : undefined}
            stopLabel={state.state === "mid-run" || state.state === "approval" ? "stop" : undefined}
            imageSelected={imageSelected}
            onReplaceImage={handleReplaceImage}
            // @-mention picker (attach source section/table). Same library
            // pick as useChatSystem: run selection wins, else the writer's
            // library choice.
            libraryId={activeSelection?.libraryId ?? (selectedLibraryId || null)}
          />
        )}
      {/* TOC / Section Navigator — opened by clicking the scope chip. */}
      {docConfig && (
        <TocPopup
          open={tocOpen}
          onClose={() => setTocOpen(false)}
          skeleton={docConfig.skeleton}
          activeSectionNumber={tocActiveSectionNumber}
          onHoverSection={setTocHoveredSectionNumber}
          onNavigate={(sectionNumber) => {
            setTocOpen(false);
            navigateToSection(sectionNumber).catch(() => {});
          }}
        />
      )}

      {/* Sole "fresh project" prompt — fires when the empty-doc watcher
          observes the writer cleared the document body. */}
      {newProjectConfirmOpen && (
        <NewProjectConfirmModal
          currentProjectLabel={activeSelection?.docName ?? activeSelection?.docCode ?? null}
          onConfirmRotate={handleConfirmRotateProject}
          onCancelRestore={handleCancelRestoreProject}
        />
      )}

      {/* Live duplicate-name guard — some files matched documents already
          in the library and were skipped from the last upload batch. */}
      {duplicateUploadNames && duplicateUploadNames.length > 0 && (
        <DuplicateUploadModal
          blockedNames={duplicateUploadNames}
          onDismiss={() => {
            duplicateUploadRef.current = null;
            setDuplicateUploadNames(null);
          }}
          onReplace={() => void replaceDuplicateUploads()}
        />
      )}

      {sourceReplaced && (
        <SourceReplacedNotice
          state={sourceReplaced}
          // Impact is per run, so the button shows only while a run is open.
          onCheckImpact={state.runId ? openSourceImpact : undefined}
          onDismiss={() => setSourceReplaced(null)}
        />
      )}

      {customDraftPickerActive && (
        <CustomDraftPicker
          sections={sections}
          onCancel={() => setCustomDraftPickerActive(false)}
          onStart={(sectionIds, runInstructions) => {
            setCustomDraftPickerActive(false);
            if (!activeSelection || sectionIds.length === 0) return;
            // Route through the shared run-start primitive with the current run
            // id as ``continueRunId`` — useDraftFlow sees submissionMode==="custom"
            // and dispatches customDraftSections (which drafts each CUSTOM-N
            // section from its saved instruction), reusing this run's SSE stream.
            void actuallyStartRun(activeSelection, null, sectionIds, state.runId, {
              runInstructions: runInstructions ?? null,
            });
          }}
        />
      )}

      {importExistingDocOpen && (
        <ImportExistingDocModal
          onConfirmImport={() => {
            setImportExistingDocOpen(false);
            handleStartCustomImport();
          }}
          onStartFresh={() => {
            setImportExistingDocOpen(false);
            handleContextCardStartFreshDraft();
          }}
          onDismiss={() => setImportExistingDocOpen(false)}
        />
      )}

      {/* Native checkout — the order to confirm (per-section or whole document,
          with a promo field), then the payment watch. Both only mount for a paid
          (custom-workspace) draft; a demo/exempt draft never opens them. Prices
          shown are doc_pricing.json's; the charge is server-side. */}
      <OrderDialog
        sectionOrder={pendingCheckout?.sectionOrder ?? null}
        wholeOrder={pendingCheckout?.wholeOrder ?? null}
        busy={checkout.phase.kind !== "idle"}
        onPay={payForOrder}
        onApplyPromo={applyOrderPromo}
        onCancel={() => {
          setPendingCheckout(null);
          checkout.reset();
        }}
      />
      {/* Priced by the server, but off the self-serve allowlist — there is no
          product to sell here, so this explains rather than charges. Without it
          the draft proceeded into a 402 no checkout could clear. */}
      <SalesAssistedDialog docType={salesAssisted} onDismiss={() => setSalesAssisted(null)} />
      {/* Custom "build on your own document" paywall — the flat unlock, with the
          same promo affordance. Run-less: paying dispatches the stashed build. */}
      <UnlockAsthraDialog
        open={customUnlock !== null}
        busy={checkout.phase.kind !== "idle"}
        onPay={() => {
          const cu = customUnlock;
          if (!cu) return;
          pendingCustomRef.current = { selection: cu.selection, retry: cu.retry };
          void checkout.begin([{ doc_id: cu.docId, doc_type: cu.docType, scope: "custom" }], null);
        }}
        onApplyPromo={async (code) => {
          const cu = customUnlock;
          if (!cu) return { ok: false, message: "Nothing to unlock." };
          const result = await postPromo({
            code,
            doc_id: cu.docId,
            doc_type: cu.docType,
            scope: "custom",
          });
          if (!result.valid) return { ok: false, message: "That code is not valid." };
          setCustomUnlock(null);
          if (cu.selection) startCustomFlowRef.current?.(cu.selection);
          else cu.retry?.();
          return { ok: true };
        }}
        onCancel={() => {
          setCustomUnlock(null);
          checkout.reset();
        }}
      />
      <CheckoutDialog
        phase={checkout.phase}
        onCheckNow={checkout.checkNow}
        onRetryDraft={() => void checkout.retryDraft()}
        onClose={() => {
          checkout.reset();
          setPendingCheckout(null);
          setCustomUnlock(null);
        }}
      />
      {billingOpen && <BillingDialog onDismiss={() => setBillingOpen(false)} />}
      {changePasswordOpen && <ChangePasswordDialog onDismiss={() => setChangePasswordOpen(false)} />}

      {/* Table-artefact builder — tag source tables, describe a transform,
          preview the derived table, and (only on approval) save it into the
          library as a CSV. The build call is preview-only; save reuses
          uploadContent(..., "text/csv"). */}
      {artefactBuilderOpen && (
        <ArtefactBuilderPanel
          libraryId={selectedLibraryId}
          building={artefactBuilding}
          buildError={artefactBuildError}
          stages={artefactStages}
          onCancel={handleCloseArtefact}
          onBuild={handleBuildArtefact}
        />
      )}
      {artefactPreview && (
        <ArtefactPreviewModal
          preview={artefactPreview}
          saving={artefactSaving}
          saveError={artefactSaveError}
          onCancel={handleCloseArtefact}
          onApprove={handleApproveArtefact}
          onDiscard={handleDiscardArtefact}
        />
      )}

      {/* Spec 23 P10.5 — single-section regen dialog. Opens from the
          Inspector's "Regen" button (post-run only). Confirm
          dispatches the regen EventLog entry and starts a fresh
          run scoped to that one section via actuallyStartRun
          (same primitive Draft more sections uses). */}
      {regenTarget && (
        <RegenOne
          target={{ ...regenTarget, libraryName: libraries.find((l) => l.id === selectedLibraryId)?.name ?? null }}
          onConfirm={handleRegenConfirm}
          onCancel={handleRegenCancel}
          onCopyInstead={
            state.runId
              ? () => {
                  setCopyTarget({
                    moduleId: regenTarget.sectionId,
                    number: regenTarget.sectionNumber,
                    title: regenTarget.sectionTitle,
                    placement: "replace_section",
                  });
                  handleRegenCancel();
                }
              : undefined
          }
        />
      )}
      {wordOnlyCopy ? (
        <ReviewPopup
          item={{
            id: `word-only-${wordOnlyCopy.sectionNumber}`,
            sectionNumber: wordOnlyCopy.sectionNumber,
            sectionTitle: "",
            kind: "text",
            before: wordOnlyCopy.before,
            after: wordOnlyCopy.after,
            fills: [],
            stillOpen: [],
            sourceNote: `${wordOnlyCopy.note}. This section wasn't drafted here, so only the Word document changes.`,
            revisionCount: 0,
          }}
          phase={wordOnlyCopy.phase}
          onApprove={() => {
            const { sectionNumber, after, note, sourceDocx } = wordOnlyCopy;
            setWordOnlyCopy((cur) => (cur ? { ...cur, phase: "applying" } : cur));
            void enqueueWordWrite(
              () =>
                insertSectionContent({
                  sectionNumber,
                  content: sanitizeDraftForWord(after),
                  ...(sourceDocx ? { fragmentBase64: sourceDocx } : {}),
                }),
              `Writing §${sectionNumber}`,
            )
              .catch(() => false)
              .then((ok) => {
                closeWordOnlyCopy();
                logActivity(
                  "asthra",
                  ok
                    ? `Replaced §${sectionNumber} in the document: ${note}. Only the document changed (the section wasn't drafted here).`
                    : `Couldn't find §${sectionNumber} in the open document, so nothing was changed.`,
                );
              });
          }}
          onReject={closeWordOnlyCopy}
          onClose={closeWordOnlyCopy}
        />
      ) : null}
      {copyTarget && state.runId && (
        <CopyFromSource
          runId={state.runId}
          moduleId={copyTarget.moduleId}
          sectionNumber={copyTarget.number}
          sectionTitle={copyTarget.title}
          documents={copySources}
          initialPlacement={copyTarget.placement}
          highlightText={copyTarget.highlight ?? null}
          onDone={(resp) => {
            const highlight = copyTarget.highlight;
            if (highlight && resp.preview) {
              // The highlight-edit review: Word first (the exact highlighted
              // range), then the record when the section is tracked.
              setCopyTarget(null);
              logActivity("you", `${resp.source_note}, to replace the highlighted text.`);
              handleViewModeChange("timeline");
              void highlightEdit.startWithReplacement(
                {
                  selectedText: highlight,
                  instruction: resp.source_note,
                  runId: state.runId,
                  sectionId: resp.preview.module_id,
                  sectionNumber: copyTarget.number || null,
                },
                resp.preview,
              );
              return;
            }
            if (resp.redraft && state.runId) {
              // The same pipeline as Regenerate: a redraft proposal in the
              // chat, reviewed in the pop-up; Approve saves it and replaces the
              // section in Word.
              const r = resp.redraft;
              setCopyTarget(null);
              logActivity("you", `${resp.source_note}, for review.`);
              handleViewModeChange("timeline");
              adhocFlow.offer({
                runId: state.runId,
                moduleId: r.module_id,
                sectionNumber: copyTarget.number || null,
                sectionLabel: copyTarget.number ? `§${copyTarget.number}` : copyTarget.title,
                instruction: resp.source_note,
                content: r.content,
                wordCount: r.word_count,
                baseSectionVersion: r.base_section_version,
                mode: "redraft",
                anchorText: null,
                originalText: r.original_text,
                sourceDocx: r.source_docx ?? null,
              });
              return;
            }
            if (resp.preview && copyTarget.placement === "replace_section") {
              const sectionNumber = copyTarget.number;
              const after = resp.preview.replacement_text;
              const sourceDocx = resp.preview.source_docx ?? null;
              setCopyTarget(null);
              void readSectionSubtree(sectionNumber)
                .catch(() => null)
                .then((before) =>
                  setWordOnlyCopy({
                    sectionNumber,
                    before: before ?? "",
                    after,
                    note: resp.source_note,
                    sourceDocx,
                    phase: "reviewing",
                  }),
                );
              return;
            }
            handleCopyDone(resp.source_note);
          }}
          onCancel={() => setCopyTarget(null)}
        />
      )}

      {/* Bulk "Fix issues" window — opened from the Issues tab or the "Fix
          flags" chip. On Start it runs the pass (useResolveIssuesFlow) and
          stays open to show each issue's status, then the changes to review. */}
      {resolveWizardTargets !== null && (
        <FixWindowHost
          flow={reviewFlow}
          targets={resolveWizardTargets}
          documents={inventoryDtoToView(libraryInventory).documents}
          busy={resolveIssuesFlow.busy}
          onCancel={closeFixWindow}
          onStart={(documentScope, targets) => {
            // The window stays open and shows the pass it started.
            setFixPassTargets(targets);
            void startResolvePass(targets, documentScope);
          }}
          progress={fixProgress}
          onPause={resolveIssuesFlow.pause}
          onResume={resolveIssuesFlow.resume}
          onStop={resolveIssuesFlow.stop}
          onClose={closeFixWindow}
          // The pass's changes are approved or rejected in this same window,
          // which closes once none is left.
          review={
            state.runId
              ? {
                  runId: state.runId,
                  libraryId: draftsLibraryId,
                  refreshSignal: proposalsRefreshSignal,
                  writeTitleFields: writeResolvedTitleFields,
                  // The Issues tab's own review queue (under this window) and
                  // its list both move with each decision.
                  onDecided: () => {
                    setIssuesRefreshSignal((n) => n + 1);
                    setProposalsRefreshSignal((n) => n + 1);
                  },
                }
              : null
          }
          onReviewed={() => setReviewStripHiddenFor(resolveIssuesFlow.summary)}
          onReview={() => {
            closeFixWindow();
            setReviewStripHiddenFor(resolveIssuesFlow.summary);
            handleViewModeChange("issues");
            setReviewWindowSignal((n) => n + 1);
          }}
        />
      )}

      {/* Spec 23 P9 — back-after digest pill. Floats above the body,
          anchored bottom-left of the shell's positioning context.
          Renders only when (a) the hook caught a qualifying return
          AND (b) there's actually something wanting the writer —
          otherwise we're announcing nothing, which would just be
          noise. Both paths (Review click + × dismiss) acknowledge
          so the pill clears without auto-re-firing on the next
          visibility flicker. */}
      {presence.isBack && presence.awayMs !== null && thingsWantYouCount > 0 && (
        <DigestReturnPill
          awayMs={presence.awayMs}
          count={thingsWantYouCount}
          onReview={handleDigestReview}
          onDismiss={presence.acknowledge}
        />
      )}

      {/* Spec 34 — chat-first Library Hub slide-over. Opened by the
          ContextChipsBar's "Manage all sources →" / "Create new
          library" actions. Coexists with the legacy modal above
          during M7's additive integration; M5/M6 of the cleanup
          MR will retire the legacy modal once all entry points
          route through the slide-over. */}
      <LibraryHubSlideOver
        open={hubSlideOverOpen}
        onClose={() => {
          // Looking inside a library (›) loads it without choosing it. Closing
          // without "Use" puts back the library that was in use before.
          const before = preInspectLibraryIdRef.current;
          preInspectLibraryIdRef.current = null;
          if (before !== null && before !== selectedLibraryId) setSelectedLibraryId(before);
          setHubSlideOverOpen(false);
        }}
        library={activeLibraryForChips}
        libraries={hubLibraries}
        onShowLibraryList={refreshLibraries}
        // "N files · M pages" on the selected library's card (film screen 2).
        // Only the inspected library's inventory is loaded, so only it gets a
        // page total; totalPageCount returns null when any file lacks one.
        libraryPageCounts={
          selectedLibraryId
            ? { [selectedLibraryId]: totalPageCount(inventoryDtoToView(libraryInventory).documents) }
            : undefined
        }
        mode={hubSlideOverMode}
        rows={hubRows}
        workspaceName={activeWorkspace?.name ?? "workspace"}
        // Workspace switcher atop the library-list view. Changing it
        // persists the pick and cascades getLibraries(newWorkspaceId)
        // (useWorkspaceLibrary), so the ``libraries`` prop above updates
        // in place while the slide-over stays open.
        workspaces={workspaces}
        selectedWorkspaceId={selectedWorkspaceId}
        onSelectWorkspace={handleSelectedWorkspaceIdChange}
        // Live SSE progress for the in-Hub HubList. ``loading`` covers
        // the gap between fetch start and the first SSE event;
        // ``libraryResolveEvent`` carries the per-doc ticks after that.
        // ``finally`` block in refreshLibraryInventory clears both, so
        // the spinner disappears the moment ``done`` lands or the
        // stream errors.
        loading={libraryInventoryLoading || !!libraryResolveEvent}
        loadingMessage={resolveProgressLabel(libraryResolveEvent)}
        resolveEvent={libraryResolveEvent}
        // Run-gated Source↔Audit toggle: the active run's audit library, or null
        // when no run / feature off (the toggle + panel self-hide).
        runId={state.runId ?? null}
        auditLibrary={hubAuditLibrary}
        onRefreshAuditLibrary={loadHubAuditLibrary}
        docTypeOptions={deriveDocTypeOptions(getLibraryDoctype(selectedLibraryId), [])}
        onSelectLibrary={(id) => {
          const lib = libraries.find((l) => l.id === id);
          if (!lib) return;
          // Check the draft orchestration FIRST — it may consume the pick.
          preInspectLibraryIdRef.current = null;
          const consumed = advanceDraftWithLibrary(lib);
          if (consumed) {
            emitLibrarySelected(lib, false);
            setHubSlideOverOpen(false);
            setLibraryStepOpen(false);
            return;
          }
          emitLibrarySelected(lib, false);
          setSelectedLibraryId(id);
          setHubSlideOverOpen(false);
        }}
        // Build-on-existing-document: the source library is optional, so offer
        // "Continue without a library" while the custom-import orchestration is
        // active. Absent (undefined) for a normal fresh draft — a library is
        // required there.
        onSkipLibrary={customImportActiveRef.current ? handleSkipLibraryForCustom : undefined}
        onInspectLibrary={(id) => {
          const lib = libraries.find((l) => l.id === id);
          if (!lib) return;
          if (preInspectLibraryIdRef.current === null) preInspectLibraryIdRef.current = selectedLibraryId;
          setSelectedLibraryId(id);
        }}
        onCreateLibrary={async (input) => {
          // Create the library in the selected workspace, then make
          // it the active one so the slide-over's optimistic flip to
          // ``uploading`` (and handleNewHubAddFiles, which targets
          // ``selectedLibraryId``) drops files into THIS new library.
          const workspaceId = selectedWorkspaceId;
          if (!workspaceId) {
            console.error("[Library Hub] Cannot create library: no workspace selected.");
            return;
          }
          // Custom libraries belong in a custom workspace — the demo workspace's
          // sample libraries are shared and free. Block here and open the
          // workspace picker's "+ New workspace" flow. Throwing keeps the writer
          // on the create form with the message.
          if (activeWorkspaceExempt) {
            setWorkspacePickerOpen(true);
            throw new Error(
              "Create a custom workspace first — the demo workspace's sample libraries are shared and free.",
            );
          }
          const name = input.name.trim();
          if (!name) return;
          try {
            const result = await createLibrary(workspaceId, name);
            // A library the writer just created stays in use on close.
            preInspectLibraryIdRef.current = null;
            setSelectedLibraryId(result.library.id);
            // Best-effort: persist the writer's scope pick as the
            // library's target doctype. Only CSR/CMC/IB overlap the
            // TargetDoctype enum; Protocol/General are skipped by the
            // ``isTargetDoctype`` guard. Client-side only — advisory UX.
            if (isTargetDoctype(input.scope)) {
              setLibraryDoctype(result.library.id, input.scope);
            }
            // Re-fetch the workspace's library list so the brand-new
            // library shows up in library-list immediately (previously
            // it only appeared after a re-login / workspace switch).
            // No refreshLibraryInventory() here: this closure still holds the
            // PREVIOUS selectedLibraryId, so calling it aborted the new
            // library's stream (started by the selection effect) and left the
            // hub on "Loading library…" forever. The effect loads it.
            await refreshLibraries();
          } catch (err) {
            console.error("[Library Hub] Library creation failed:", err);
            // Rethrow so the slide-over keeps the writer on the create
            // form (with an inline error) instead of navigating to a
            // library that was never created.
            throw err;
          }
        }}
        onAddFiles={handleNewHubAddFiles}
        libraryTabs={hubLibraryTabs}
        onCreateArtefact={handleCreateArtefact}
        onPickDoc={(_docId) => {
          // The slide-over flips to detail mode internally on row
          // pick; this callback is here for any cross-surface
          // analytics hook. No-op for now.
        }}
        onRefreshLibrary={() => {
          invalidateResolveGuard();
          return refreshLibraryInventory();
        }}
        onClassifyLibrary={() => {
          void classifyLibraryInventory();
        }}
        onVerifyDoc={(input) => {
          console.warn(
            "[Spec 34 M7] onVerifyDoc received %o — wiring to the " +
              "approve/reclassify API lands in the next commit.",
            input,
          );
        }}
        onDeleteDoc={deleteHubDoc}
        replacingDocIds={replacingDocIds}
        onReplaceDoc={(row, file) => {
          if (selectedLibraryId && row.doc_id) void replaceHubDoc(selectedLibraryId, row.doc_id, row.filename, file);
        }}
        onDownloadDoc={async (row) => {
          if (!row.doc_id) return;
          try {
            const { blob, filename } = await fetchDocumentDownload(row.doc_id);
            downloadBlob(blob, filename);
          } catch (error) {
            console.error("Failed to download document:", error);
          }
        }}
      />
      {/* Phase 7.2 — DoctypePicker: the canonical doctype-selection
          surface. Opened by:
            * Standalone chip-click on the doctype ContextStrip chip
              (via openDoctypePickerStandalone) — pendingDraftStart
              stays false; the pick lands in selectedDocCode and
              that's it.
            * The /draft orchestrator (via runDraftCommand →
              tryAdvanceDraft) when doctype is the missing piece.
              pendingDraftStart=true; on pick, the orchestration
              continues toward starting the run.
          On cancel: closes the picker. Standalone picks preserve
          whatever was already in selectedDocCode. Mid-/draft cancel
          clears pendingDraftStart so the writer isn't trapped. */}
      <DoctypePicker
        open={doctypePickerOpen}
        selectedDocCode={activeSelection?.docCode ?? selectedDocCode}
        appConfig={appConfig}
        // Tier-scoped: only the doctypes GET /doc-types returned for this
        // user (already filtered by doc_pricing.json for pay_per_use). Empty
        // array while loading / on error = show nothing, never fall open.
        allowedDocCodes={tierAllowedDocCodes ?? []}
        // Workspace selector + that workspace's private templates. Picking a
        // workspace here is the shell's workspace selection, so a template is
        // always drafted in the workspace it was listed for.
        workspaces={workspaces}
        selectedWorkspaceId={selectedWorkspaceId || null}
        onWorkspaceChange={handleSelectedWorkspaceIdChange}
        privateTemplates={privateTemplates}
        regions={submissionRegions}
        regionStepFor={regionStepFor}
        loadDoctypeAbout={getDoctypeAbout}
        // When building on an existing doc, the picker identifies what the
        // document IS rather than what to draft — swap the header copy.
        customImport={customImportActiveRef.current}
        onSelect={handleDoctypePick}
        onCancel={() => {
          setDoctypePickerOpen(false);
          regionRelaunchRef.current = null;
          setRegionStepFor(null);
          if (pendingDraftStart) {
            setPendingDraftStart(false);
          }
        }}
      />

      <LibraryStep
        open={libraryStepOpen && pendingDraftStart}
        libraries={libraries}
        selectedLibraryId={selectedLibraryId || null}
        workspaceName={activeWorkspace?.name ?? null}
        docName={lookupDoctypeChoice(selectedDocCode)?.docName ?? null}
        // A workspace template draws on its submission type's sources.
        docType={
          privateTemplates?.find((t) => t.code === selectedDocCode)?.base_doc_type ||
          lookupDoctypeChoice(selectedDocCode)?.backendDocType ||
          selectedDocCode
        }
        loadExpectedSources={customImportActiveRef.current ? undefined : getExpectedSources}
        stepTotal={customImportActiveRef.current ? 3 : 5}
        onUse={(lib) => {
          setLibraryStepOpen(false);
          if (advanceDraftWithLibrary(lib)) emitLibrarySelected(lib, false);
        }}
        onInspect={(lib) => {
          if (preInspectLibraryIdRef.current === null) preInspectLibraryIdRef.current = selectedLibraryId;
          setSelectedLibraryId(lib.id);
          openSlideOver("list");
        }}
        onNewLibrary={() => openSlideOver("creating")}
        onSkip={
          customImportActiveRef.current
            ? () => {
                setLibraryStepOpen(false);
                handleSkipLibraryForCustom();
              }
            : undefined
        }
        onBack={() => {
          setLibraryStepOpen(false);
          setRegionStepFor(lookupDoctypeChoice(selectedDocCode));
          setDoctypePickerOpen(true);
        }}
        onCancel={() => {
          setLibraryStepOpen(false);
          setPendingDraftStart(false);
          customImportActiveRef.current = false;
        }}
      />

      {/* Draft name for a build-on-your-document run, which has no section
          picker to name it on. Opened by ``launchDraft`` when such a selection
          arrives unnamed; Continue relaunches it named. */}
      <HomePromptDialog
        request={namePromptRequest}
        onSubmit={async (name, folder) => {
          const held = namePromptFor;
          setNamePromptFor(null);
          if (!held) return;
          let folderId = held.folderId ?? null;
          if (folder && "newLabel" in folder) {
            try {
              folderId = (await createNamePromptFolder(folder.newLabel)).id;
            } catch (err) {
              setStartError(`Couldn't create the folder: ${err instanceof Error ? err.message : String(err)}`);
              return;
            }
          } else if (folder) {
            folderId = folder.value || null;
          }
          pendingFolderIdRef.current = folderId;
          launchDraftRef.current?.({ ...held, name, folderId });
        }}
        onCancel={() => {
          setNamePromptFor(null);
          customImportActiveRef.current = false;
        }}
      />

      <WorkspacePicker
        open={workspacePickerOpen}
        workspaces={workspaces}
        selectedWorkspaceId={selectedWorkspaceId}
        onSelect={(id) => {
          handleSelectedWorkspaceIdChange(id);
          setWorkspacePickerOpen(false);
        }}
        onCreate={async (name) => {
          await createAndSelectWorkspace(name);
          setWorkspacePickerOpen(false);
        }}
        onCancel={() => setWorkspacePickerOpen(false)}
      />
      <SourceImpactHost runId={state.runId} onRedraft={sourceImpactRedraft} spine={sections} />
      {/* Kill confirmation dialog — extracted to separate component. */}
      <KillRunConfirmation
        open={showKillConfirmation}
        onConfirm={handleConfirmKill}
        onCancel={() => setShowKillConfirmation(false)}
        confirming={killConfirming}
      />
      <SaveAndStartFreshModal
        open={!!freshDraftPending}
        onConfirm={handleConfirmFreshDraft}
        onCancel={() => setFreshDraftPending(null)}
        confirming={freshDraftSaving}
      />
      {/* Dev-only overlay — visible when
          localStorage["asthra:debug:word-ops"] === "1". Renders nothing
          otherwise so the writer never sees it. */}
      <WordOpsPanel />
    </div>
  );
};

export default VariantCShell;
